import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import AdminVisitorsPanel from "@/pages/AdminVisitorsPanel";
import { AttendanceSummary } from "@/components/AttendanceSummary";
import { useAdminLtrLock } from "@/hooks/useAdminLtrLock";
import { API, LOGOS, EVENT } from "@/lib/constants";
import {
  PARTICIPATION_DAYS,
  PARTICIPATION_DAY_FULL_LABELS,
  PARTICIPATION_DAY_FULL_LABEL_LIST,
  parseParticipationDayLabel,
} from "../lib/participationDay";
import {
  LogOut, CheckCircle2, XCircle, Clock, Users, Mail,
  Car, Instagram, Phone, User, Hash, Palette, Calendar as CalendarIcon,
  Wrench, X, Loader2, Download, Flag, Send, AlertTriangle, ShieldCheck,
  ChevronDown, ChevronUp, QrCode, MapPin, Globe, KeyRound,
} from "lucide-react";

const TABS = [
  { id: "all", label: "All", icon: Users },
  { id: "pending", label: "Pending", icon: Clock },
  { id: "approved", label: "Approved", icon: CheckCircle2 },
  { id: "rejected", label: "Rejected", icon: XCircle },
];

// Admin-only Participation Day options — imported from
// `@/lib/participationDay`. See that module for the source of truth
// (option list, canonical "label — date" strings, and reverse-mapping
// parse helper). Kept as an import so the isolated Jest fixture in
// `src/__tests__/participationDayDropdown.test.js` can exercise the
// SAME contract without loading the entire dashboard module tree.
// R2 candidate (2026-08-30): read-only edition classification. UI derives
// edition from `created_at` alone. No `edition` field is read from or written
// to any application record.
const EDITION_CUTOFF_ISO_UTC = "2026-08-19T21:00:00+00:00";
const EDITIONS = [
  { id: "winter",       label: "Winter 2026" },
  { id: "may",          label: "May 2026" },
  { id: "all",          label: "All Editions" },
  { id: "unclassified", label: "Unclassified" },
];
function isWinterByCreatedAt(createdAt) {
  return typeof createdAt === "string" && createdAt >= EDITION_CUTOFF_ISO_UTC;
}
const PARTICIPATION_DAY_MAP = Object.fromEntries(PARTICIPATION_DAYS.map(d => [d.id, d]));
const DAY_FILTERS = [
  { id: "all", label: "Any Day" },
  { id: "unassigned", label: "Unassigned" },
  ...PARTICIPATION_DAYS.map(d => ({ id: d.id, label: d.label })),
];

// Display rule (admin UI only): Independent + Rimal Center + blank → all show as "CrewZNatioN".
// Applicants' original choice is preserved in the database but this single crew label is
// used everywhere the admin sees it (row cards, detail modal, edit Crew dropdown, CSVs).
const displayGroup = (raw) => {
  const v = (raw || "").trim().toLowerCase();
  if (!v || v === "independent" || v === "rimal center") return "CrewZNatioN";
  return (raw || "").trim();
};

// Appends a per-click cache-buster (&_t=<ms>) to any admin export URL so
// Cloudflare treats each click as a unique cache key and never serves a
// stale error response. Backend middleware also sets Cache-Control:no-store
// on /api/admin/* — this is belt-and-suspenders for the CDN edge.
const bust = (url) => {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_t=${Date.now()}`;
};

// SINGLE SOURCE OF TRUTH for "essential registration fields" — Sep 12, 2026
// owner correction. The "Incomplete pending applications" list must be
// based ONLY on fields the Winter registration form has ALWAYS required.
// Newly-added origin_country/origin_city are NOT counted here; the
// legacy location gap is a separate label and MUST NOT be conflated with
// this count. Admin-assigned participation_day is NOT counted. Server
// metadata (timestamps, integrity flags) is NOT counted. Storage-fetch
// outcomes for photos are NOT counted — only the STORED reference field
// is checked. Optional fields (photo_secondary / left / right, instagram,
// modifications, retired group) are NOT counted.
const _emptyStr = (v) => !((v == null ? "" : String(v)).trim());
function essentialGaps(app) {
  const gaps = [];
  if (_emptyStr(app.full_name)) gaps.push("full_name");
  const age = Number(app.age);
  if (!age || !Number.isFinite(age) || age <= 0) gaps.push("age");
  if (_emptyStr(app.email)) gaps.push("email");
  if (_emptyStr(app.mobile)) gaps.push("mobile");
  if (_emptyStr(app.vehicle_make)) gaps.push("vehicle_make");
  if (_emptyStr(app.vehicle_model)) gaps.push("vehicle_model");
  if (_emptyStr(app.vehicle_year)) gaps.push("vehicle_year");
  if (_emptyStr(app.vehicle_color)) gaps.push("vehicle_color");
  if (_emptyStr(app.plate_number)) gaps.push("plate_number");
  if (_emptyStr(app.photo_primary)) gaps.push("photo_primary");
  return gaps;
}
function isIncompletePending(app) {
  return app && app.status === "pending" && essentialGaps(app).length > 0;
}

// Origin classifier moved to /lib/originClassifier.js (see that file
// for full Sep 12, 2026 hardening notes and the /src/__tests__/
// classifyOrigin.test.js regression suite covering the exact live bug).
import { classifyOrigin } from "../lib/originClassifier";


// ---------------------------------------------------------------------------
// Photo component — loading state, retryable error, eager decoding in the
// detail modal, click-to-enlarge with original download. Sep 12, 2026:
// (a) row thumbnails were fetching full-resolution originals eagerly and
// saturating the storage upstream, and (b) the file handler mapped every
// upstream error to 404, so intermittent gallery gaps looked like deleted
// files. This component distinguishes truly-missing (404 body detail) from
// retryable (5xx / network) failures and renders a Retry button in the
// latter case. Native `loading="lazy"` is opt-in via prop so admin detail
// gallery photos always load immediately.
// ---------------------------------------------------------------------------
// Sep 13, 2026 owner review v3: Photo pipeline correctness pass.
// The v2 rewrite left five ways for stale callbacks / timers / probes
// to overwrite a fresh render (imgRef.src='' race, probe-after-onload,
// setState updater side-effects, uncleared retry setTimeouts, competing
// probes). v3 introduces a monotonically-increasing "attempt generation"
// counter. Every side-effecting callback (probe fetch, retry timeout,
// watchdog timeout, IO observer) captures the generation at scheduling
// time and refuses to touch state / schedule further work if its
// generation was superseded. The `<img>` uses a `key` bound to (path,
// nonce) so React tears down the old element on any change — no stale
// DOM events can fire. Only actual decoded pixels (naturalWidth > 0)
// may transition state to "ok".
const LOAD_BUDGET_MS = 15000;
const PROBE_BUDGET_MS = 10000;
const AUTO_RETRY_DELAY_MS = 800;
const IO_ROOT_MARGIN = "300px";
function Photo({
  path, label, aspect = "aspect-[4/3]", lazy = false,
  showOpenOriginal = true, dataTestId, w = null,
}) {
  const [state, setState] = useState("loading");
  const [nonce, setNonce] = useState(0);
  const [detail, setDetail] = useState(null);
  const [visible, setVisible] = useState(!lazy);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const timersRef = useRef(new Set());
  const autoRetriedRef = useRef(false);
  const prevPathRef = useRef(null);
  // Generation counter: bumped on every path/nonce change or explicit
  // disposal. Every scheduled callback captures the current value and
  // no-ops if the generation has moved past it.
  const genRef = useRef(0);
  // Sticky "we succeeded" flag per generation — set only by an actual
  // decoded onLoad(nw > 0). Once set, probe callbacks cannot roll the
  // state back to retryable.
  const okRef = useRef(false);
  // stateRef mirrors `state` so timers can read the current value
  // WITHOUT calling setState with a side-effecting updater.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const _addTimer = (t) => { timersRef.current.add(t); return t; };
  const _clearTimer = (t) => {
    if (!t) return;
    clearTimeout(t);
    timersRef.current.delete(t);
  };
  const _clearAllTimers = () => {
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current.clear();
  };
  const _abortInFlight = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
      abortRef.current = null;
    }
  };

  // Effect A: react to path/nonce changes. Increments generation,
  // aborts in-flight probes, clears every scheduled timer. Does NOT
  // touch `<img>` src directly — React `key` handles element identity.
  useEffect(() => {
    genRef.current += 1;
    okRef.current = false;
    setState("loading");
    setDetail(null);
    if (prevPathRef.current !== path) {
      autoRetriedRef.current = false;
      prevPathRef.current = path;
      if (lazy) setVisible(false);
    }
    _clearAllTimers();
    _abortInFlight();
    return () => {
      genRef.current += 1;   // supersede on unmount / dep change
      _clearAllTimers();
      _abortInFlight();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce]);

  // Effect B: IntersectionObserver for lazy tiles.
  useEffect(() => {
    if (!lazy || visible || !path) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true); return;
    }
    const myGen = genRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (genRef.current !== myGen) { try { io.disconnect(); } catch (_) {} return; }
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            try { io.disconnect(); } catch (_) { /* noop */ }
            break;
          }
        }
      },
      { rootMargin: IO_ROOT_MARGIN },
    );
    io.observe(el);
    return () => { try { io.disconnect(); } catch (_) {} };
  }, [lazy, visible, path, nonce]);

  const _q = [];
  if (w) _q.push(`w=${w}`);
  if (nonce > 0) _q.push(`_r=${nonce}`);
  const qs = _q.length ? (path && path.includes("?") ? "&" : "?") + _q.join("&") : "";
  const src = path ? `${API}/files/${path}${qs}` : null;
  const armedSrc = visible ? src : undefined;
  const rawOriginal = path
    ? `${API}/files/${path}${path.includes("?") ? "&" : "?"}original=1`
    : null;

  const _scheduleAutoRetry = (myGen) => {
    if (autoRetriedRef.current) return false;
    autoRetriedRef.current = true;
    const t = _addTimer(setTimeout(() => {
      timersRef.current.delete(t);
      if (genRef.current !== myGen) return;  // superseded
      setNonce((n) => n + 1);
    }, AUTO_RETRY_DELAY_MS));
    return true;
  };

  const probe = useCallback(async (myGen) => {
    if (!path) { setState("missing"); return; }
    _abortInFlight();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let deadlineHit = false;
    const probeTimer = _addTimer(setTimeout(() => {
      deadlineHit = true;
      try { ctrl.abort(); } catch (_) {}
    }, PROBE_BUDGET_MS));
    let response = null;
    try {
      response = await fetch(src, { method: "GET", cache: "no-store", signal: ctrl.signal });
      _clearTimer(probeTimer);
      if (genRef.current !== myGen || okRef.current) return;
      if (response.ok) {
        if (_scheduleAutoRetry(myGen)) return;
        setState("retryable");
        setDetail(`Response OK but image did not decode (${response.headers.get("content-type") || "unknown type"}). Retryable.`);
        return;
      }
      if (response.status === 404) {
        setState("missing");
        setDetail("File not found on storage — the original object is missing.");
        return;
      }
      if (_scheduleAutoRetry(myGen)) return;
      setState("retryable");
      setDetail(`Storage returned ${response.status}. Retryable.`);
    } catch (e) {
      _clearTimer(probeTimer);
      if (genRef.current !== myGen || okRef.current) return;
      if (e && (e.name === "AbortError" || ctrl.signal.aborted)) {
        if (!deadlineHit) return;
        if (_scheduleAutoRetry(myGen)) return;
        setState("retryable");
        setDetail(`Load timed out after ${Math.round(PROBE_BUDGET_MS/1000)}s. Retryable.`);
        return;
      }
      if (_scheduleAutoRetry(myGen)) return;
      setState("retryable");
      setDetail(`Network error: ${e.message || e}`);
    } finally {
      // Owner review v4: probe reads status/headers only — never consume
      // the body. Release the underlying stream so the browser does not
      // keep downloading a whole photo it will never use.
      _clearTimer(probeTimer);
      if (response && response.body && !response.bodyUsed) {
        try { await response.body.cancel(); } catch (_) { /* noop */ }
      }
      // Identity-guarded clearance so we do not clobber a newer probe.
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  }, [path, src]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect C: arm watchdog at the moment we assign src (independent of
  // any DOM event). Reads state via stateRef inside the timer body so
  // we do NOT put side effects in a setState updater.
  useEffect(() => {
    _clearAllTimers();
    if (!armedSrc || state !== "loading") return;
    const myGen = genRef.current;
    const t = _addTimer(setTimeout(() => {
      timersRef.current.delete(t);
      if (genRef.current !== myGen || okRef.current) return;
      if (stateRef.current === "loading") probe(myGen);
    }, LOAD_BUDGET_MS));
    return () => _clearTimer(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armedSrc, state, nonce]);

  if (!path) {
    return (
      <div
        className={`w-full ${aspect} bg-white/[0.02] flex items-center justify-center border border-white/10`}
        data-testid={dataTestId ? `${dataTestId}-empty` : undefined}
      >
        <span className="text-white/20 text-xs tracking-wider uppercase">
          {label ? `No ${label} photo` : "No photo"}
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative w-full ${aspect} bg-black`} data-testid={dataTestId}>
      {state === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/[0.02] border border-white/10">
          <Loader2 className="w-5 h-5 animate-spin text-white/40" />
        </div>
      )}
      {state === "missing" && (
        <div
          data-testid={dataTestId ? `${dataTestId}-missing` : undefined}
          className="absolute inset-0 flex flex-col items-center justify-center bg-red-500/[0.06] border border-red-400/40 text-red-200 gap-1 px-3 text-center"
        >
          <AlertTriangle className="w-5 h-5" />
          <span className="text-[11px] tracking-wider uppercase font-heading font-bold">
            {label || "Photo"} unavailable
          </span>
          <span className="text-[10px] opacity-80">{detail || "Missing on storage"}</span>
        </div>
      )}
      {state === "retryable" && (
        <div
          data-testid={dataTestId ? `${dataTestId}-retryable` : undefined}
          className="absolute inset-0 flex flex-col items-center justify-center bg-amber-500/[0.06] border border-amber-400/50 text-amber-200 gap-2 px-3 text-center"
        >
          <AlertTriangle className="w-5 h-5" />
          <span className="text-[11px] tracking-wider uppercase font-heading font-bold">
            Storage hiccup
          </span>
          <span className="text-[10px] opacity-80 max-w-full truncate">
            {detail || "Please retry"}
          </span>
          <button
            type="button"
            data-testid={dataTestId ? `${dataTestId}-retry-btn` : undefined}
            onClick={() => { autoRetriedRef.current = false; setNonce((n) => n + 1); }}
            className="mt-1 px-2 py-0.5 text-[11px] tracking-wider uppercase font-heading font-bold border border-amber-300 hover:bg-amber-300 hover:text-black transition"
          >
            Retry
          </button>
        </div>
      )}
      {/* React key bound to (path, nonce) so the old <img> is torn down
          on any change. Stale onLoad/onError events from a previous
          attempt cannot fire on a different DOM element. */}
      <img
        key={`${path}#${nonce}`}
        src={armedSrc}
        alt={label || ""}
        decoding="async"
        onLoad={(e) => {
          const myGen = genRef.current;
          const nw = e.currentTarget.naturalWidth;
          if (!nw || nw === 0) {
            // Decoded to nothing — probe once for a bounded error.
            probe(myGen);
            return;
          }
          okRef.current = true;
          // Owner review v4: cancel any in-flight probe + its deadline
          // timer so a real decoded pixel does not leave an unbounded
          // fetch open behind it.
          _abortInFlight();
          _clearAllTimers();
          setState("ok");
        }}
        onError={() => {
          if (okRef.current) return;   // never overwrite a successful decode
          probe(genRef.current);
        }}
        className={`w-full ${aspect} object-cover ${state === "ok" ? "opacity-100" : "opacity-0"} transition-opacity`}
        data-testid={dataTestId ? `${dataTestId}-img` : undefined}
      />
      {state === "ok" && showOpenOriginal && (
        <a
          href={rawOriginal}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={dataTestId ? `${dataTestId}-open-original` : undefined}
          className="absolute bottom-1 right-1 px-2 py-0.5 bg-black/70 hover:bg-black text-white/90 text-[10px] tracking-wider uppercase font-heading font-bold border border-white/20"
          title="Open the full original photo in a new tab (unmodified, uncropped)"
        >
          Open original
        </a>
      )}
    </div>
  );
}

// Silently retries a transient network error (Cloudflare 5xx, no-network, timeout).
// Most useful right after a deploy when the backend container is briefly warming up.
// - Retries: 3 attempts (initial + 2 retries) with delays 2s, 4s
// - Only retries: ERR_NETWORK, ECONNABORTED, ERR_BAD_RESPONSE, and HTTP 5xx
// - Does NOT retry: 4xx (auth/permission/validation — those are real errors)
// - Total wait worst case: ~6s before surfacing an error toast
// Exported for isolated retry-behavior tests (Sep 14 2026 · 520 defect).
// Actual implementation lives in ../utils/withRetry so a Jest test can
// exercise the retry semantics without loading React/react-router-dom.
import { _isTransient, _sleep, withRetry } from "../utils/withRetry";
export { _isTransient, _sleep, withRetry };

// Silently records a reviewer's click on a read-only button so the admin can audit
// what the reviewer attempted to use. Fire-and-forget; admins are ignored server-side.
const logReviewerBlockedClick = (label, meta = {}) => {
  try {
    if (typeof window === "undefined") return;
    const role = localStorage.getItem("admin_role");
    if (role !== "reviewer") return;
    const tok = localStorage.getItem("admin_token");
    if (!tok) return;
    axios.post(
      `${API}/admin/reviewer-click-log`,
      { label, meta },
      { headers: { Authorization: `Bearer ${tok}` } }
    ).catch(() => {}); // never surface the error to the reviewer
  } catch {
    // never throw
  }
};

// Build an inert click handler for read-only links/buttons. Calls preventDefault and
// silently posts a click-log to the backend. Returns undefined for admin (full access).
const blockClickIfReviewer = (isReviewer, label, meta) => (
  isReviewer
    ? (e) => { try { e.preventDefault(); } catch { /* button onClick has no preventDefault */ } logReviewerBlockedClick(label, meta); }
    : undefined
);

// Fetch a protected admin resource with the Bearer token in the
// Authorization HEADER (never in the URL), read the response as a Blob, and
// trigger the browser download. Never places the JWT in the URL, referrer,
// browser history, or clipboard. Filename is derived from
// Content-Disposition when the server sets one, otherwise from `fallback`.
async function downloadWithAuth(url, fallbackName = "download", externalSignal) {
  const tok = (typeof window !== "undefined") && localStorage.getItem("admin_token");
  if (!tok) throw new Error("Not authenticated");
  // Sep 12, 2026 owner report: previous Missing-Info CSV / master export
  // clicks produced no visible outcome — either the file downloaded and the
  // browser hid it, or the request failed silently. This wrapper now emits
  // an explicit "Preparing…" loading toast, a "Downloaded … · N rows ·
  // scope=winter" success toast (with row count from X-Rows-Exported), and
  // a specific error toast on any failure. Removes the "silent-fail"
  // ambiguity end-to-end.
  const toastId = (() => {
    try { return toast.loading(`Preparing ${fallbackName}…`); }
    catch { return null; }
  })();
  // Sep 13, 2026 (corrected post-review): two independent finite deadlines,
  // both cleanly cancellable via a single AbortController. Neither is a
  // total-request cap — the goal is to bound only STALLS.
  //
  //   * HEADERS_TIMEOUT_MS — fires if the server never returns response
  //     headers (i.e. `fetch()` never resolves). Cleared the moment
  //     headers arrive.
  //   * IDLE_TIMEOUT_MS — after headers arrive, we read the body via
  //     `resp.body.getReader()` and reset this deadline every time a
  //     REAL chunk lands. A healthy streamed export (e.g. the
  //     `/admin/entry-passes-v2.zip` route which shims to
  //     `/admin/entry-passes.zip` and takes 3+ minutes) keeps emitting
  //     chunks and NEVER trips this deadline; a stalled body (headers
  //     received but then no further bytes) trips it and aborts. The
  //     same reader path bounds the non-OK error-body read.
  const HEADERS_TIMEOUT_MS = 60000;
  const IDLE_TIMEOUT_MS = 90000;
  const _ac = (typeof AbortController !== "undefined") ? new AbortController() : null;
  // v9 Codex defect 4: link an externally-supplied disposal signal to
  // this fetch's AbortController so a component unmount (or the
  // prepare helper's overall deadline) can cancel the final download's
  // in-flight fetch and its body-read loop. Listener is removed in
  // `finally` to prevent leaks across successful completions.
  const _onExternalAbort = () => { try { if (_ac) _ac.abort(); } catch { /* ignore */ } };
  const _externalHasAdd = externalSignal && typeof externalSignal.addEventListener === "function";
  if (_externalHasAdd) {
    if (externalSignal.aborted) { try { if (_ac) _ac.abort(); } catch { /* ignore */ } }
    else externalSignal.addEventListener("abort", _onExternalAbort);
  }
  let _phase = "headers";               // "headers" | "body" | "done"
  let _timer = (_ac && typeof setTimeout !== "undefined")
    ? setTimeout(() => { try { _ac.abort(); } catch { /* ignore */ } }, HEADERS_TIMEOUT_MS)
    : null;
  const _armIdle = () => {
    if (!_ac || typeof setTimeout === "undefined") return;
    if (_timer) { try { clearTimeout(_timer); } catch { /* ignore */ } }
    _timer = setTimeout(() => { try { _ac.abort(); } catch { /* ignore */ } }, IDLE_TIMEOUT_MS);
  };
  const _clearTimer = () => {
    if (_timer) { try { clearTimeout(_timer); } catch { /* ignore */ } _timer = null; }
  };
  // Bounded reader helper — reads the whole body but resets the idle
  // deadline on every real chunk. Aborts if the AbortController fires.
  const _readAllBounded = async (resp) => {
    const body = resp.body;
    if (!body || typeof body.getReader !== "function") {
      // Fallback for environments without streaming body (very old
      // browsers). We do NOT fall through to `resp.blob()` unbounded —
      // instead we bound with the CURRENT idle deadline; if the
      // browser cannot stream, we still cannot leak an unbounded
      // wait.
      _armIdle();
      return await resp.blob();
    }
    const reader = body.getReader();
    const chunks = [];
    let received = 0;
    try {
      _armIdle();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        _clearTimer();
        if (value && value.byteLength) {
          chunks.push(value);
          received += value.byteLength;
        }
        _armIdle();
      }
      _clearTimer();
    } finally {
      try { reader.releaseLock(); } catch { /* ignore */ }
    }
    return new Blob(chunks);
  };
  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${tok}` },
      credentials: "omit",
      signal: _ac ? _ac.signal : undefined,
    });
    // Headers received. Enter body phase and switch to idle deadline.
    _phase = "body";
    if (!resp.ok) {
      // Bounded error-body read: same reader/idle deadline as the OK
      // path. A non-OK response whose body stalls will trip
      // IDLE_TIMEOUT_MS and abort — no unbounded wait here either.
      const blob = await _readAllBounded(resp);
      let body = "";
      try { body = await blob.text(); } catch { /* ignore */ }
      throw new Error(`HTTP ${resp.status} ${(body || "").slice(0, 200)}`);
    }
    const blob = await _readAllBounded(resp);
    if (blob.size === 0) {
      throw new Error(`Empty response (0 bytes) — server returned no data. Try again in a few seconds.`);
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    const cd = resp.headers.get("content-disposition") || "";
    const m = /filename\*?="?([^";]+)"?/i.exec(cd);
    a.download = m ? decodeURIComponent(m[1]) : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    const rowsHdr = resp.headers.get("x-rows-exported");
    const scopeHdr = resp.headers.get("x-scope-edition");
    const missHdr = resp.headers.get("x-missing-only");
    const parts = [`Downloaded ${a.download}`];
    if (rowsHdr) parts.push(`${rowsHdr} row${rowsHdr === "1" ? "" : "s"}`);
    if (scopeHdr) parts.push(`scope=${scopeHdr}${missHdr === "1" ? " · missing-info-only" : ""}`);
    try {
      if (toastId) toast.success(parts.join(" · "), { id: toastId, duration: 10000 });
      else toast.success(parts.join(" · "), { duration: 10000 });
    } catch { /* ignore */ }
  } catch (err) {
    try {
      const isAbort = err && (err.name === "AbortError" || (typeof DOMException !== "undefined" && err instanceof DOMException && err.name === "AbortError"));
      let msg;
      if (isAbort) {
        msg = _phase === "headers"
          ? `Download failed: server did not send response headers within 60 s. Try again in a few seconds.`
          : `Download failed: server stopped sending data (no progress for 90 s). Try again in a few seconds.`;
      } else {
        msg = `Download failed: ${err.message || err}`;
      }
      if (toastId) toast.error(msg, { id: toastId, duration: 14000 });
      else toast.error(msg, { duration: 14000 });
    } catch { /* ignore */ }
    throw err;
  } finally {
    _phase = "done";
    _clearTimer();
    if (_externalHasAdd && typeof externalSignal.removeEventListener === "function") {
      try { externalSignal.removeEventListener("abort", _onExternalAbort); } catch { /* ignore */ }
    }
  }
}


async function prepareAndDownloadEntryPasses(prepareUrl, fallbackName = "entry-passes.zip", externalSignal) {
  // Lifecycle controller — a SINGLE gate that (a) marks the helper
  // terminated, (b) aborts the current fetch controller, (c) makes
  // the poll sleep abortable, (d) clears the overall-deadline timer,
  // (e) prevents every later action (fetch/download/toast).
  const tok = (typeof window !== "undefined") && localStorage.getItem("admin_token");
  if (!tok) throw new Error("Not authenticated");
  const PREP_HEADERS_MS = 60000;
  const BODY_MS = 30000;
  const PREP_OVERALL_MS = 20 * 60 * 1000;
  const POLL_INTERVAL_MS = 2000;
  const toastId = (() => { try { return toast.loading(`Preparing ${fallbackName}…`); } catch { return null; } })();
  const _updateToast = (m) => { if (!terminated) { try { if (toastId) toast.loading(m, { id: toastId }); } catch {} } };
  const _dismissToast = () => { try { if (toastId) toast.dismiss(toastId); } catch {} };

  let terminated = false;
  let terminationReason = null;
  let currentAc = null;
  let sleepResolver = null;      // resolves the poll sleep on termination
  let overallTimer = null;
  const _abortErr = (msg) => { const e = new Error(msg); e.name = "AbortError"; return e; };
  const _terminate = (err) => {
    if (terminated) return;
    terminated = true;
    terminationReason = err || _abortErr("Aborted");
    if (currentAc) { try { currentAc.abort(); } catch {} }
    if (sleepResolver) { const r = sleepResolver; sleepResolver = null; try { r(); } catch {} }
    if (overallTimer) { try { clearTimeout(overallTimer); } catch {} overallTimer = null; }
  };
  const _onExternalAbort = () => _terminate(_abortErr("Aborted (component unmounted)"));
  if (externalSignal && typeof externalSignal.addEventListener === "function") {
    if (externalSignal.aborted) _terminate(_abortErr("Aborted (component unmounted)"));
    else externalSignal.addEventListener("abort", _onExternalAbort);
  }
  overallTimer = setTimeout(
    () => _terminate(_abortErr(`Prepare did not complete within ${Math.round(PREP_OVERALL_MS / 60000)} minutes.`)),
    PREP_OVERALL_MS,
  );
  const _checkTerminated = () => { if (terminated) throw terminationReason || _abortErr("Aborted"); };
  const _abortableSleep = (ms) => new Promise((resolve) => {
    const t = setTimeout(() => { sleepResolver = null; resolve(); }, ms);
    sleepResolver = () => { try { clearTimeout(t); } catch {} resolve(); };
  });
  const _withDeadline = (promise, ms, ac, phaseLabel) => new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return; settled = true;
      try { ac.abort(); } catch {}
      reject(_abortErr(`Timed out after ${ms} ms during ${phaseLabel}`));
    }, ms);
    promise.then(
      (v) => { if (settled) return; settled = true; clearTimeout(timer); resolve(v); },
      (e) => { if (settled) return; settled = true; clearTimeout(timer); reject(e); },
    );
  });
  const _requestJson = async (url, init, phaseLabel) => {
    _checkTerminated();
    currentAc = new AbortController();
    try {
      const headersP = fetch(url, { ...init, signal: currentAc.signal, credentials: "omit" });
      const resp = await _withDeadline(headersP, PREP_HEADERS_MS, currentAc, `${phaseLabel}-headers`);
      _checkTerminated();
      const bodyP = resp.ok ? resp.json() : resp.text();
      const body = await _withDeadline(bodyP, BODY_MS, currentAc, `${phaseLabel}-body`);
      _checkTerminated();
      return { resp, body };
    } finally {
      currentAc = null;
    }
  };

  const _run = async () => {
    const { resp: prepResp, body: prepBody } = await _requestJson(prepareUrl,
      { method: "POST", headers: { Authorization: `Bearer ${tok}` } }, "prepare");
    if (prepResp.status === 409) throw new Error("Another All-Passes prepare is already in progress. Try again in a minute.");
    if (!prepResp.ok) throw new Error(`Prepare failed (HTTP ${prepResp.status}) ${String(prepBody).slice(0, 200)}`);
    const taskId = prepBody.task_id;
    if (!taskId) throw new Error("Prepare returned no task_id");
    const base = prepareUrl.replace(/\/admin\/entry-passes-prepare(\?.*)?$/, "/admin/entry-passes");
    const statusUrl = `${base}-status?task_id=${encodeURIComponent(taskId)}`;
    const downloadUrl = `${base}-download?task_id=${encodeURIComponent(taskId)}`;
    const t0 = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      _checkTerminated();
      await _abortableSleep(POLL_INTERVAL_MS);
      _checkTerminated();
      const { resp: sResp, body: sBody } = await _requestJson(statusUrl,
        { method: "GET", headers: { Authorization: `Bearer ${tok}` } }, "status");
      if (!sResp.ok) throw new Error(`Status check failed (HTTP ${sResp.status}) ${String(sBody).slice(0, 200)}`);
      if (sBody.state === "ready") {
        _checkTerminated();
        _dismissToast();
        // v9 Codex defect 4 — explicit bounded handoff. The prepare
        // phase is over; the final download has its OWN independent
        // bounded deadlines (HEADERS_TIMEOUT_MS / IDLE_TIMEOUT_MS
        // inside `downloadWithAuth`). Preserve a HEALTHY streaming
        // response by clearing this helper's overall-prepare deadline
        // AND detaching our external-abort listener from the prepare's
        // `_terminate` handler (so disposal no longer routes through
        // `_terminate` and cannot mutate state after the handoff).
        // Disposal must STILL cancel the final download — we pass the
        // externalSignal directly to `downloadWithAuth`, which links
        // its own AbortController to the signal.
        if (overallTimer) { try { clearTimeout(overallTimer); } catch {} overallTimer = null; }
        if (externalSignal && typeof externalSignal.removeEventListener === "function") {
          try { externalSignal.removeEventListener("abort", _onExternalAbort); } catch {}
        }
        await downloadWithAuth(downloadUrl, sBody.filename || fallbackName, externalSignal);
        return;
      }
      if (sBody.state === "failed")    throw new Error(`Prepare failed: ${sBody.error || "unknown error"}`);
      if (sBody.state === "cancelled") throw new Error(`Prepare cancelled: ${sBody.error || "cancelled"}`);
      const secs = Math.round((Date.now() - t0) / 1000);
      _updateToast(`Preparing ${fallbackName}… ${sBody.rendered || 0} passes rendered · ${secs}s`);
    }
  };
  try {
    await _run();
  } catch (err) {
    const settled = err || terminationReason;
    try {
      const isAbort = settled && settled.name === "AbortError";
      const msg = isAbort ? `All Passes ZIP cancelled: ${settled.message || settled}` : `All Passes ZIP failed: ${settled.message || settled}`;
      if (!terminated || settled === terminationReason) {
        if (toastId) toast.error(msg, { id: toastId, duration: 14000 });
        else toast.error(msg, { duration: 14000 });
      }
    } catch {}
    throw settled;
  } finally {
    // ALWAYS clear ALL timers/listeners on every exit, including success.
    if (overallTimer) { try { clearTimeout(overallTimer); } catch {} overallTimer = null; }
    if (externalSignal && typeof externalSignal.removeEventListener === "function") {
      externalSignal.removeEventListener("abort", _onExternalAbort);
    }
    if (currentAc) { try { currentAc.abort(); } catch {} }
    if (sleepResolver) { const r = sleepResolver; sleepResolver = null; try { r(); } catch {} }
  }
}


export default function AdminDashboard() {
  useAdminLtrLock();
  const navigate = useNavigate();
  // Delegated auth-download interceptor: any element with the
  // `data-authed-download` attribute intercepts anchor navigation and
  // downloads via fetch()+Bearer header, so the admin JWT NEVER appears in
  // the URL / referrer / browser history.
  useEffect(() => {
    // Sep 13, 2026 post-post-review: give the delegated auth-download
    // interceptor an AbortController tied to the effect's lifetime so
    // the bounded background prepare/poll helper below aborts every
    // in-flight fetch on unmount.
    const disposeController = (typeof AbortController !== "undefined") ? new AbortController() : null;
    const disposeSignal = disposeController ? disposeController.signal : undefined;
    const handler = (e) => {
      const prep = e.target && e.target.closest && e.target.closest("[data-authed-prepare-download]");
      if (prep) {
        const url = prep.getAttribute("href") || prep.getAttribute("data-download-url");
        const fname = prep.getAttribute("data-download-filename") || "export.zip";
        if (!url) return;
        e.preventDefault();
        prepareAndDownloadEntryPasses(url, fname, disposeSignal).catch(() => { /* toast already emitted */ });
        return;
      }
      const el = e.target && e.target.closest && e.target.closest("[data-authed-download]");
      if (!el) return;
      const url = el.getAttribute("href") || el.getAttribute("data-download-url");
      const fname = el.getAttribute("data-download-filename") || "export";
      if (!url) return;
      e.preventDefault();
      downloadWithAuth(url, fname).catch((err) => {
        try {
          toast.error(`Download failed: ${err.message || err}`);
        } catch { /* ignore */ }
      });
    };
    document.addEventListener("click", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
      try { if (disposeController) disposeController.abort(); } catch { /* ignore */ }
    };
  }, []);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [attendDay, setAttendDay] = useState(null);
  const [attendSummary, setAttendSummary] = useState(null);
  const [attendLoading, setAttendLoading] = useState(false);
  const [attendError, setAttendError] = useState(null);
  const attendReqRef = useRef(0);
  // R2 candidate (2026-08-30): default edition = Winter 2026.
  const [editionFilter, setEditionFilter] = useState("winter");
  // Sep 12, 2026 (post-republish owner directive): allow the admin to
  // narrow the list to rows that are structurally incomplete OR missing
  // recoverable contact / location details, so partial applicants can be
  // found and corrected without hunting through the full list. This is a
  // client-side view filter over the already-loaded list — it does NOT
  // change the API query and never drops a row.
  const [missingOnly, setMissingOnly] = useState(false);
  // Persistent reconciliation summary. The toast (14 s) can be missed if
  // the admin glances away or is on mobile — this state renders a
  // dismissable card next to the button so the last result stays visible
  // until the next click or a full reload.
  const [reconResult, setReconResult] = useState(null); // {ok, db_total, returned, unaccounted, flagged, truncated, checked_at, error}
  const [reconLoading, setReconLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  // May 2026 read-only archive: derived from the existing edition selector.
  // Winter (default) preserves all live-event behavior; May (and Unclassified)
  // switch the dashboard into a read-only mode where mutation controls are
  // hidden and any in-flight selection/modal is cleared. Backend guards are
  // the authoritative refusal layer; this is the UI honesty layer.
  const isMayArchive = editionFilter === "may";

  // May 2026 read-only archive: clear any open selection/modal on edition
  // change so a May-selected row cannot be acted on under the Winter scope
  // (or vice versa) after switching. Backend guards would refuse the
  // mutation anyway; this closes the stale-scope UI gap.
  useEffect(() => {
    setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionFilter]);

  const token = typeof window !== "undefined" ? localStorage.getItem("admin_token") : null;
  const adminEmail = typeof window !== "undefined" ? localStorage.getItem("admin_email") : null;
  const adminRole = typeof window !== "undefined" ? localStorage.getItem("admin_role") || "admin" : "admin";
  const adminName = typeof window !== "undefined" ? localStorage.getItem("admin_name") || "Admin" : "Admin";
  const isReviewer = adminRole === "reviewer";
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Reviewer-submitted recommendations indexed by application_id (loaded alongside
  // applications). The admin uses this to see badges + accept/dismiss actions; the
  // reviewer uses this to see what they've already submitted.
  const [pendingRecs, setPendingRecs] = useState({}); // { application_id: rec }
  const [allPendingRecs, setAllPendingRecs] = useState([]); // flat list for admin summary
  const [reviewerAccess, setReviewerAccess] = useState([]); // [{email, name, access_state, seconds_remaining, access_expires_at}]
  const [reviewerActivity, setReviewerActivity] = useState(null); // {total, by_rec_status, items: [...]}
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityFilter, setActivityFilter] = useState("all"); // all | pending | accepted | dismissed
  const [duplicates, setDuplicates] = useState(null); // {total_approved, unique_people, duplicates_to_archive, archived_existing}
  const [duplicatesBusy, setDuplicatesBusy] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null); // {scope_edition, configured, from_number, total_approved, sms_ok, sms_failed, never_attempted}
  const [smsAudit, setSmsAudit] = useState(null);   // {total_approved, sendable_total, ksa_ok, ksa_fixable, non_ksa_ok, invalid, missing}
  const [smsBusy, setSmsBusy] = useState(false);

  const fetchRecs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/recommendations?status=pending`, authHeader);
      const list = r.data || [];
      const byApp = {};
      list.forEach((rec) => { byApp[rec.application_id] = rec; });
      setPendingRecs(byApp);
      setAllPendingRecs(list);
    } catch (err) {
      // non-fatal — recommendations are additive
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReviewerAccess = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/reviewer-access`, authHeader);
      setReviewerAccess(r.data || []);
    } catch (err) {
      // non-fatal (e.g., reviewer logging in — they don't have access to this endpoint)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReviewerActivity = useCallback(async () => {
    try {
      const r = await withRetry(() => axios.get(`${API}/admin/reviewer-activity`, authHeader));
      setReviewerActivity(r.data || null);
    } catch (err) {
      // non-fatal — admin-only endpoint
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extendReviewer = async (email, hours = 24) => {
    try {
      await axios.post(`${API}/admin/reviewer-access/${encodeURIComponent(email)}/extend?hours=${hours}`, {}, authHeader);
      toast.success(`Access extended by ${hours}h`);
      fetchReviewerAccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extend access");
    }
  };

  const revokeReviewer = async (email) => {
    if (!window.confirm("Revoke access for this reviewer? They will be unable to log in until you extend again.")) return;
    try {
      await axios.post(`${API}/admin/reviewer-access/${encodeURIComponent(email)}/revoke`, {}, authHeader);
      toast.success("Access revoked");
      fetchReviewerAccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke access");
    }
  };

  const fetchDuplicates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/duplicates/preview`, authHeader);
      setDuplicates(r.data);
    } catch (err) {
      // non-fatal — admin only endpoint
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const archiveDuplicates = async () => {
    const phrase = window.prompt(
      "This will SOFT-DELETE all duplicate approved records (the older copies). " +
      "The list will drop to one record per person. This is REVERSIBLE — you can " +
      "click Restore at any time.\n\nType 'ARCHIVE DUPLICATES' (exactly, all caps) to proceed:"
    );
    if (phrase !== "ARCHIVE DUPLICATES") {
      if (phrase !== null) toast.error("Confirmation phrase mismatch — nothing was changed");
      return;
    }
    setDuplicatesBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/duplicates/archive`,
        { confirm: "ARCHIVE DUPLICATES" },
        authHeader,
      );
      toast.success(`Archived ${r.data.archived} duplicate${r.data.archived === 1 ? "" : "s"}. Approved is now ${r.data.total_after}.`);
      await Promise.all([fetchDuplicates(), fetchData()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive duplicates");
    } finally {
      setDuplicatesBusy(false);
    }
  };

  const restoreDuplicates = async () => {
    if (!window.confirm("Restore every archived duplicate back to Approved? This brings them back into the dashboard, gate scanner, and email blasts.")) return;
    setDuplicatesBusy(true);
    try {
      const r = await axios.post(`${API}/admin/duplicates/restore`, {}, authHeader);
      toast.success(`Restored ${r.data.restored}. Approved is now ${r.data.total_approved}.`);
      await Promise.all([fetchDuplicates(), fetchData()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore duplicates");
    } finally {
      setDuplicatesBusy(false);
    }
  };

  // Feb 2026 cross-edition audit — out-of-order response supersession
  // for the delivery-status and SMS-status count endpoints. Same pattern
  // as `fetchScopeRef` on the applications list: every fetch stamps a
  // monotonically-increasing id + the exact scope tuple it was launched
  // under. A response whose id/scope no longer matches the current UI
  // filter is DROPPED even if it succeeded — otherwise a slow Winter
  // response could overwrite the May counts that populated first after
  // a fast Winter→May switch, and the operator would then launch a
  // bulk action based on stale-edition totals.
  //
  // Rolling-deploy compatibility guard: a response is also dropped if
  // it lacks `scope_edition` in the payload (new frontend, old backend
  // that silently ignored the query param). This fails SAFE — the
  // panel shows "loading" and every bulk button remains disabled until
  // the backend is confirmed to be edition-aware. Do NOT infer backend
  // release from the JS build filename.
  const smsScopeRef = useRef({ id: 0, scope: "" });
  const deliveryScopeRef = useRef({ id: 0, scope: "" });

  const fetchSmsStatus = useCallback(async () => {
    const scope = editionFilter;
    const myId = smsScopeRef.current.id + 1;
    smsScopeRef.current = { id: myId, scope };
    // Null out immediately so `smsStatus?.total_approved || 0` cannot
    // silently substitute a previous edition's totals into the SMS
    // Blast confirmation while the new-scope fetch is in flight.
    setSmsStatus(null);
    try {
      const [statusR, auditR] = await Promise.all([
        axios.get(`${API}/admin/sms/status?edition=${encodeURIComponent(scope)}`, authHeader),
        axios.get(`${API}/admin/sms/audit?edition=${encodeURIComponent(scope)}`, authHeader),
      ]);
      // Discard superseded / mismatched responses.
      if (smsScopeRef.current.id !== myId || smsScopeRef.current.scope !== scope) return;
      const data = statusR.data;
      // Rolling-deploy guard: old backend silently ignores edition and
      // returns a response WITHOUT `scope_edition`. Reject that state
      // so bulk actions stay disabled.
      if (!data || data.scope_edition !== scope) return;
      setSmsStatus(data);
      setSmsAudit(auditR.data);
    } catch (err) {
      // non-fatal; smsStatus stays null so the ready-gate keeps blast
      // buttons disabled.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionFilter]);

  const sendTestSms = async () => {
    const to = window.prompt(
      "Send a TEST SMS to which phone number?\n\n" +
      "Use international format (e.g. +966501234567 or just 0501234567 for KSA). " +
      "This is a single-message dry run — no other recipients are affected."
    );
    if (!to || !to.trim()) return;
    setSmsBusy(true);
    try {
      const r = await axios.post(`${API}/admin/sms/test`, { to: to.trim() }, authHeader);
      if (r.data.ok) {
        toast.success(`Test SMS queued. Twilio SID: ${r.data.sid?.slice(0, 12)}…`);
      } else {
        toast.error(`Test SMS failed: ${r.data.error || r.data.status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test SMS");
    } finally {
      setSmsBusy(false);
    }
  };

  const blastSms = async (onlyFailed = false) => {
    // Feb 2026 cross-edition READY gate. The confirmation prompt would
    // otherwise be built from `smsStatus?.total_approved || 0` — a
    // silent 0 (fetch pending / superseded / rolling-deploy old
    // backend) would let the operator dispatch a blast with no
    // visible count. Refuse until the SMS panel counts have been
    // fetched from the backend AND echoed back with the current
    // scope.
    if (!smsStatus || smsStatus.scope_edition !== editionFilter) {
      toast.error(
        `SMS panel counts for '${editionFilter}' haven't loaded yet. ` +
        `Refresh the SMS panel and retry.`
      );
      return;
    }
    const total = smsStatus.total_approved;
    const failedOnly = smsStatus.sms_failed;
    const target = onlyFailed ? failedOnly : total;
    const scopeLabel = editionFilter === "winter"
      ? "Winter 2026"
      : editionFilter === "may"
      ? "Legacy May 2026"
      : editionFilter === "unclassified"
      ? "Unclassified"
      : "ALL Editions (Winter + May)";
    const phrase = window.prompt(
      `SCOPE: ${scopeLabel} (edition=${editionFilter}).\n` +
      `This will send the bilingual Entry Pass SMS to ${target} approved applicant${target === 1 ? "" : "s"}` +
      `${onlyFailed ? " (RETRY of previously failed sends only)" : ""} in that scope. ` +
      `Each SMS includes a unique signed link to their pass page. ` +
      `Anyone who already received a successful SMS in the last 6 hours is automatically skipped.\n\n` +
      `Type 'SEND SMS BLAST' to proceed:`
    );
    if (phrase !== "SEND SMS BLAST") {
      if (phrase !== null) toast.error("Confirmation phrase mismatch — nothing was sent");
      return;
    }
    setSmsBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/sms/blast-all-v2?edition=${encodeURIComponent(editionFilter)}`,
        { confirm: "SEND SMS BLAST", only_failed: onlyFailed },
        { ...authHeader, timeout: 600000 },
      );
      const d = r.data;
      toast.success(
        `[${d.scope_edition || editionFilter}] Sent ${d.sent} · Skipped ${d.skipped_recent_success} · Failed ${d.failed} · Invalid ${d.invalid_numbers}`
      );
      await fetchSmsStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "SMS blast failed");
    } finally {
      setSmsBusy(false);
    }
  };

  const [loadError, setLoadError] = useState(null);
  // Separate error tracking per endpoint so we can tell the admin WHICH
  // request failed. Promise.all previously masked which of the two calls
  // was returning HTTP 520 — reproduction of the Sept 12, 2026 incident
  // showed both `fetchData` and `fetchDeliveryStatus` logging failures in
  // the same session; we now surface each independently.
  const [listError, setListError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  // Sep 12, 2026 (owner-observed intermittent 520 producing STALE totals
  // that masquerade as the newly-selected filter): every fetchData call is
  // stamped with a monotonic id so a slow response from a superseded
  // filter can be discarded on arrival, and the stats card is emptied to
  // "unavailable" immediately on filter change until the FRESH response
  // lands. Response scope is also fingerprinted with the exact filter tuple
  // sent — a response whose scope no longer matches the current UI filter
  // is dropped even if it succeeded.
  const fetchScopeRef = useRef({ id: 0, scope: "", controller: null });
  // Stored as a ref (not state) so referencing it inside `fetchData` does
  // NOT introduce a stale closure that a `useCallback` dep would fix at
  // the cost of a re-created callback firing an extra `useEffect` fetch.
  // The value is only ever read/written inside `fetchData`; it does not
  // participate in rendering.
  const applicationsScopeRef = useRef("");
  const fetchData = useCallback(async () => {
    const scope = `${activeTab}|${dayFilter}|${editionFilter}|${attendDay || ""}`;
    const myId = fetchScopeRef.current.id + 1;
    // Transport-level cancellation: abort any in-flight request for a
    // previous scope, then install a fresh controller for this fetch.
    // Aborting propagates through axios (→ ERR_CANCELED / CanceledError)
    // AND through withRetry's sleep (→ RetryAbortedError), so a stalled
    // 520-retry does NOT continue to burn its 4s delay after the admin
    // switched filters or navigated away.
    try { fetchScopeRef.current.controller?.abort("superseded"); } catch { /* noop */ }
    const controller = new AbortController();
    fetchScopeRef.current = { id: myId, scope, controller };
    // Cross-scope retention guard: applications loaded under a PREVIOUS
    // filter must never be presented as current data of the new filter.
    // Clear on scope change; preserve on same-scope refresh.
    if (applicationsScopeRef.current && applicationsScopeRef.current !== scope) {
      setApplications([]);
    }
    setLoading(true);
    setLoadError(null);
    setListError(null);
    setStatsError(null);
    // Sep 13, 2026 owner-flagged UI defect: previously we set
    // `{__stale: true, total: null, ...}` at request START, which the
    // tab renderer / StatCard rendered as "UNAVAILABLE" + tooltip
    // "stats endpoint failed" for the entire in-flight window — a
    // loading transition was being displayed as a confirmed error.
    // Fix: at start, mark stats loading (not stale). Only mark stats
    // stale/failed on an ACTUAL request failure below.
    //
    // Sep 14, 2026 owner-directed 520 correction: the list and stats
    // GETs previously fired ONCE with no retry — a single transient
    // edge/upstream 5xx (e.g. Cloudflare 520 recorded for the
    // production admin panel) surfaced as "list + stats server error
    // 520" and cleared the visible applicant list. The status-update
    // WRITE path already uses `withRetry`, which the backend's own
    // idempotency guard (server.py:6229-6246) makes safe; the READ
    // path is idempotent by definition, so applying the SAME bounded
    // retry to reads is both symmetrical and side-effect-free. We
    // also preserve the previously-loaded list on final SAME-SCOPE
    // failure so an admin does not lose their in-progress view on a
    // single upstream blip. Cross-scope requests abort at the
    // transport (AbortController) AND the retry sleep (signal).
    //
    // Per-attempt bound: each axios GET has `timeout: 30000` so a
    // single hung upstream cannot stretch the retry sequence beyond
    // ~3 × 30 s + 2 s + 4 s ≈ 96 s. The 2 s / 4 s delays are the
    // BACKOFF between attempts, not a total-time bound; the ceiling
    // is (tries × per-attempt-timeout) + Σ backoff.
    // Sep 14 2026 owner-corrected: at fetch start, if the previous stats
    // were loaded under a DIFFERENT scope, NULL OUT the numeric fields
    // AND update __scope in one step — otherwise the tab counter / stat
    // card would keep displaying the previous scope's numbers labeled
    // as current (`stats.__scope === newScope` would silence the
    // mismatch warning while `stats.total` still held the old value).
    // Same-scope refresh preserves the numbers so a transient blip
    // does not blank the counters mid-review.
    setStats((prev) => {
      const changedScope = prev.__scope && prev.__scope !== scope;
      return changedScope
        ? { __loading: true, __stale: false, __scope: scope, total: null, pending: null, approved: null, rejected: null }
        : { ...prev, __loading: true, __stale: false, __scope: scope };
    });
    let statsFailure = null;
    let listFailure = null;
    const AXIOS_PER_ATTEMPT_TIMEOUT_MS = 30000;
    const listP = withRetry(() => axios.get(
      attendDay
        ? `${API}/admin/applications?status=approved&participation_day=all&attend_day=${attendDay}&edition=${editionFilter}`
        : `${API}/admin/applications?status=${activeTab}&participation_day=${dayFilter}&edition=${editionFilter}`,
      { ...authHeader, signal: controller.signal, timeout: AXIOS_PER_ATTEMPT_TIMEOUT_MS },
    ), { signal: controller.signal }).catch((e) => { listFailure = e; return null; });
    const statsP = withRetry(() => axios.get(
      `${API}/admin/stats?edition=${editionFilter}`,
      { ...authHeader, signal: controller.signal, timeout: AXIOS_PER_ATTEMPT_TIMEOUT_MS },
    ), { signal: controller.signal }).catch((e) => { statsFailure = e; return null; });
    const [appsRes, statsRes] = await Promise.all([listP, statsP]);
    // TERMINAL ABORT GUARD — catches both scope change AND unmount BEFORE
    // any state-setter runs. On unmount the useEffect cleanup aborts the
    // controller but does NOT bump `fetchScopeRef.current.id`, so the
    // scope/id response guard below alone would not stop `setLoading /
    // setStats / setApplications` from firing on an unmounted component.
    // Checking `controller.signal.aborted` here unifies the two paths and
    // is the single terminal exit.
    if (controller.signal.aborted) {
      return;
    }
    // Response guard (backstop): even if transport cancellation raced a
    // response, discard superseded responses so a slow retry that
    // eventually resolves cannot overwrite the counts for a filter the
    // admin has since switched away from.
    if (fetchScopeRef.current.id !== myId || fetchScopeRef.current.scope !== scope) {
      return;
    }
    // Do NOT surface an abort as an error — that's a filter change, not a failure.
    const _isAbort = (e) => e && (e.name === "CanceledError" || e.name === "RetryAbortedError" || e.code === "ERR_CANCELED" || e.aborted === true);
    if (_isAbort(listFailure)) listFailure = null;
    if (_isAbort(statsFailure)) statsFailure = null;
    if (appsRes) {
      setApplications(appsRes.data);
      applicationsScopeRef.current = scope;
      // Owner directive (Sept 12, 2026): the admin list must NEVER present a
      // partial result as all applicants. When the backend hits its row/time
      // boundary it emits X-Truncated with the reason; we surface this as
      // an explicit incomplete state so the current filter's rows are not
      // trusted as complete.
      const truncReason = appsRes.headers?.["x-truncated"] || appsRes.headers?.["X-Truncated"];
      const truncCount = appsRes.headers?.["x-truncated-count"] || appsRes.headers?.["X-Truncated-Count"];
      if (truncReason) {
        setListError({
          status: 200,
          truncated: true,
          message: `Only the first ${truncCount || appsRes.data.length} applicants for this filter were returned (${truncReason}). This view is INCOMPLETE — narrow the filter or contact support.`,
        });
      }
    } else if (listFailure) {
      // Owner directive 2026-09-14: preserve the previously-loaded list
      // on a final SAME-SCOPE read failure so an admin does not lose
      // their filtered view mid-review. The error toast + listError
      // banner below still surface the failure loudly. NOTE: retention
      // only applies when the scope matches — cross-scope requests
      // cleared applications at the top of this function.
      setListError({
        status: listFailure?.response?.status || listFailure?.code || "unknown",
        message: listFailure?.response?.data?.detail || listFailure?.message || String(listFailure),
      });
    }
    if (statsRes) {
      setStats({ ...statsRes.data, __stale: false, __loading: false, __scope: scope });
    } else {
      // Only NOW (after the request confirmed a failure) surface UNAVAILABLE.
      setStats({ __stale: true, __loading: false, total: null, pending: null, approved: null, rejected: null, __scope: scope });
      setStatsError({
        status: statsFailure?.response?.status || statsFailure?.code || "unknown",
        message: statsFailure?.response?.data?.detail || statsFailure?.message || String(statsFailure),
      });
    }
    if (listFailure || statsFailure) {
      const primary = listFailure || statsFailure;
      // Session-expired handling stays the same.
      if (primary.response?.status === 401 || (primary.response?.status === 403 && /expired|revoked/i.test(primary.response?.data?.detail || ""))) {
        const msg = primary.response?.data?.detail || "Session ended";
        toast.error(msg, { duration: 6000 });
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_email");
        localStorage.removeItem("admin_role");
        localStorage.removeItem("admin_name");
        navigate("/admin");
      } else {
        const which = listFailure && statsFailure ? "list + stats"
                    : listFailure ? "list" : "stats";
        const err = primary;
        let msg;
        if (err.code === "ECONNABORTED") {
          msg = `${which} request timed out. Please refresh.`;
        } else if (err.code === "ERR_NETWORK" || /network/i.test(err.message || "")) {
          msg = `Network lost while loading ${which}. Please check your internet and retry.`;
        } else if (err.response?.status >= 500) {
          msg = `${which} server error ${err.response.status}. Please retry in 30 seconds.`;
        } else if (err.response?.data?.detail) {
          msg = `${which} failed: ${err.response.data.detail}`;
        } else {
          msg = `${which} failed (${err.message || "unknown"}).`;
        }
        toast.error(msg, { duration: 8000 });
        setLoadError({ which, message: msg, listStatus: listFailure?.response?.status, statsStatus: statsFailure?.response?.status });
        // eslint-disable-next-line no-console
        console.error("[fetchData] failed:", { listFailure, statsFailure });
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dayFilter, editionFilter, attendDay]);

  const fetchAttendance = useCallback(async () => {
    const myId = ++attendReqRef.current;
    setAttendLoading(true);
    try {
      const r = await withRetry(() => axios.get(
        `${API}/admin/attendance-summary?edition=${encodeURIComponent(editionFilter)}`,
        { ...authHeader, timeout: 30000 },
      ));
      if (myId !== attendReqRef.current) return;
      setAttendSummary(r.data);
      setAttendError(null);
    } catch (e) {
      if (myId !== attendReqRef.current) return;
      setAttendSummary(null);
      setAttendError(e?.response?.data?.detail || e?.message || "request failed");
    } finally {
      if (myId === attendReqRef.current) setAttendLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionFilter]);

  useEffect(() => {
    if (token && !isReviewer) fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAttendance, applications]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
    fetchRecs();
    if (!isReviewer) {
      fetchReviewerAccess();
      fetchReviewerActivity();
      fetchDuplicates();
      fetchSmsStatus();
    }
    // On unmount, abort any in-flight list+stats retry sequence so we
    // do not leak a 4s sleep or a state update after the component
    // has been removed from the tree.
    return () => {
      try { fetchScopeRef.current.controller?.abort("unmount"); } catch { /* noop */ }
    };
  }, [token, fetchData, fetchRecs, fetchReviewerAccess, fetchReviewerActivity, fetchDuplicates, fetchSmsStatus, navigate, isReviewer]);

  const updateStatus = async (id, status, emailMode = null) => {
    setUpdating(true);
    try {
      const body = emailMode ? { status, email_mode: emailMode } : { status };
      // Backend is now IDEMPOTENT for status updates — duplicate calls (network blips,
      // retries) will silently skip resending the email and return the current state.
      // So it's safe to wrap this mutation in withRetry.
      const res = await withRetry(() =>
        axios.patch(`${API}/admin/applications/${id}/status`, body, authHeader)
      );
      const emailInfo = res.data?.email || {};
      const wasIdempotent = !!res.data?.idempotent_skip;
      const label =
        emailMode === "apology" ? "rejected with apology"
        : emailMode === "silent" ? "rejected silently (no email sent)"
        : status;
      if (wasIdempotent) {
        toast.success(`Application already ${status} — email previously sent`);
      } else if (emailMode === "silent") {
        toast.success(`Application ${label}`);
      } else if (status === "approved" || status === "rejected") {
        if (emailInfo.sent) {
          toast.success(`Application ${label} — email sent to applicant`);
        } else {
          toast.warning(
            `Application ${label}, but email could NOT be sent: ${emailInfo.error || "unknown"}. Use 'Email Applicant' to send manually.`,
            { duration: 8000 }
          );
        }
      } else {
        toast.success(`Application ${status}`);
      }
      fetchData();
      setSelected(null);
    } catch (err) {
      let msg;
      if (err.code === "ECONNABORTED") {
        msg = "Approve/Reject request timed out (email may still be sending). Click Re-verify in 30s, then decide whether to retry.";
      } else if (err.code === "ERR_NETWORK") {
        msg = "Network connection lost during Approve/Reject. Please check your internet and retry.";
      } else if (err.response?.status === 401) {
        msg = "Your session expired. Please log out and log back in.";
      } else if (err.response?.status === 403) {
        msg = "Permission denied. Only the admin can approve or reject applications.";
      } else if (err.response?.status === 404) {
        msg = "This applicant no longer exists — someone may have deleted them.";
      } else if (err.response?.status >= 500) {
        msg = `Server error ${err.response.status} during update. Please retry in 30 seconds.`;
      } else if (err.response?.data?.detail) {
        msg = `Update failed: ${err.response.data.detail}`;
      } else {
        msg = `Update failed (${err.message || "unknown"}). Please retry.`;
      }
      toast.error(msg, { duration: 10000 });
      // eslint-disable-next-line no-console
      console.error("[updateStatus] failed:", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_name");
    navigate("/admin");
  };

  // Reviewer submits a recommendation (no status change, no email to applicant).
  // For the reviewer, we present it as if it were a real approval/rejection.
  const submitRecommendation = async (id, decision, note = "") => {
    setUpdating(true);
    try {
      await axios.post(
        `${API}/admin/applications/${id}/recommend`,
        { decision, note },
        authHeader,
      );
      toast.success(`Application ${decision}`);
      fetchRecs();
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update application");
    } finally {
      setUpdating(false);
    }
  };

  // Admin accepts a recommendation → real status change + email to applicant.
  const acceptRecommendation = async (recId) => {
    setUpdating(true);
    try {
      const res = await axios.post(`${API}/admin/recommendations/${recId}/accept`, {}, authHeader);
      const sent = res.data?.email?.sent;
      toast.success(sent ? "Recommendation accepted — applicant emailed" : "Recommendation accepted");
      fetchRecs();
      fetchData();
      fetchReviewerActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept recommendation");
    } finally {
      setUpdating(false);
    }
  };

  // Admin dismisses a recommendation → no status change, no email.
  const dismissRecommendation = async (recId) => {
    setUpdating(true);
    try {
      await axios.post(`${API}/admin/recommendations/${recId}/dismiss`, {}, authHeader);
      toast.success("Recommendation dismissed");
      fetchRecs();
      fetchReviewerActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to dismiss recommendation");
    } finally {
      setUpdating(false);
    }
  };

  // --- Corrected-pass delivery status (verified against Resend) ---
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [autoCompleteRunning, setAutoCompleteRunning] = useState(false);
  const [autoCompleteProgress, setAutoCompleteProgress] = useState(null); // job object
  const autoCompleteCancelled = useRef(false);

  const fetchDeliveryStatus = useCallback(async () => {
    const scope = editionFilter;
    const myId = deliveryScopeRef.current.id + 1;
    deliveryScopeRef.current = { id: myId, scope };
    setDeliveryLoading(true);
    // Null out immediately so `deliveryStatus?.total_approved || 0`
    // cannot silently substitute a previous edition's totals into the
    // Force-Send / Complete-Delivery confirmations while the new-scope
    // fetch is in flight.
    setDeliveryStatus(null);
    try {
      // Feb 2026 cross-edition scope fix: pass the current edition so
      // `deliveryStatus.total_approved`, `.missing_count`, and
      // `.failed_count` — which drive the "Force-Send All" and
      // "Complete Delivery (N)" confirmations — match the scope those
      // buttons will act on. Backend requires `edition` (returns 422
      // otherwise).
      const res = await withRetry(() => axios.get(
        `${API}/admin/corrected-pass-status?edition=${encodeURIComponent(scope)}`,
        { ...authHeader, timeout: 2 * 60 * 1000 },
      ));
      // Discard superseded / mismatched responses (Winter response
      // arriving after user switched to May must not overwrite May
      // counts).
      if (deliveryScopeRef.current.id !== myId || deliveryScopeRef.current.scope !== scope) return;
      const data = res.data;
      // Rolling-deploy guard: old backend silently ignores edition and
      // returns a response WITHOUT `scope_edition`. Reject that state
      // so Force-Send / Complete-Delivery stay disabled.
      if (!data || data.scope_edition !== scope) return;
      setDeliveryStatus(data);
    } catch (err) {
      // 401 = silently expired token (login redirect handled elsewhere)
      // Surface the actual reason so the admin knows whether to retry, refresh, or escalate.
      if (err.response?.status === 401) return;
      let msg;
      if (err.code === "ECONNABORTED") {
        msg = "Delivery check timed out (>2 min) — Resend may be slow. Click Re-verify to retry.";
      } else if (err.code === "ERR_NETWORK" || err.message?.toLowerCase().includes("network")) {
        msg = "Network connection lost while checking delivery. Please check your internet and click Re-verify.";
      } else if (err.response?.status >= 500) {
        msg = `Server error ${err.response.status} while checking delivery. Try again in 30 seconds.`;
      } else if (err.response?.data?.detail) {
        msg = `Delivery check failed: ${err.response.data.detail}`;
      } else {
        msg = `Delivery check failed (${err.message || "unknown"}). Click Re-verify to retry.`;
      }
      toast.error(msg, { duration: 8000 });
      // eslint-disable-next-line no-console
      console.error("[fetchDeliveryStatus] failed:", err);
    } finally {
      setDeliveryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionFilter]);

  useEffect(() => {
    if (!token) return;
    fetchDeliveryStatus();
  }, [token, fetchDeliveryStatus]);

  const pollJob = (jobId) => new Promise((resolve) => {
    const tick = async () => {
      if (autoCompleteCancelled.current) { resolve(null); return; }
      try {
        const p = await axios.get(`${API}/admin/bulk-jobs/${jobId}`, authHeader);
        setAutoCompleteProgress(p.data);
        if (p.data.state === "done") { resolve(p.data); return; }
      } catch {
        resolve(null); return;
      }
      setTimeout(tick, 2500);
    };
    tick();
  });

  const completeDelivery = async () => {
    // Feb 2026 cross-edition READY gate — the `missing_count` and
    // `failed_count` used to size the confirmation prompt below MUST
    // be scoped to the CURRENT edition filter. A stale-edition
    // response (Winter → May fast switch) or an old-backend response
    // (silently ignores `edition`) would let the operator launch a
    // corrected-pass batch based on the wrong recipient set. Refuse
    // early.
    if (!deliveryStatus || deliveryStatus.scope_edition !== editionFilter) {
      toast.error(
        `Delivery counts for '${editionFilter}' haven't loaded yet. ` +
        `Click Re-Verify on the Delivery Status card and retry.`
      );
      return;
    }
    // Sep 12, 2026 owner-directed honesty guard: refuse to declare "nothing
    // to do" when the provider snapshot is not yet trustworthy for
    // negatives. A `checking` / `stale` / `unavailable` state could hide
    // real missing rows behind a `missing_count=0` figure that only
    // reflects positive DB signals. The badge on the delivery card
    // already tells the admin the counts are unverified; refuse to
    // start a resend batch until we can trust the missing list.
    const negativesTrustworthy = deliveryStatus.provider_negatives_trustworthy !== false;
    const missing = deliveryStatus.missing_count + deliveryStatus.failed_count;
    if (!negativesTrustworthy) {
      toast.warning(
        `Delivery log is ${deliveryStatus.provider_status || "checking"} — refuse to start resend until Resend confirms who still needs a pass. Click Re-Verify in a few seconds.`,
        { duration: 8000 }
      );
      return;
    }
    if (missing === 0) {
      toast.success("Every applicant already has their corrected pass — nothing to do.");
      return;
    }
    const scopeLabel = editionFilter === "winter"
      ? "Winter 2026"
      : editionFilter === "may"
      ? "Legacy May 2026"
      : editionFilter === "unclassified"
      ? "Unclassified"
      : "ALL Editions (Winter + May)";
    const ok = window.confirm(
      `SCOPE: ${scopeLabel} (edition=${editionFilter}).\n\n` +
      `Send the corrected Entry Pass to ${missing} applicant${missing === 1 ? "" : "s"} in that scope who haven't received it yet?\n\n` +
      `The system will:\n` +
      `  1. Send to each missing applicant (3 automatic retries per email)\n` +
      `  2. Re-verify against Resend's delivery log\n` +
      `  3. Repeat if anyone is still missing (up to 2 passes)\n\n` +
      `Runs in the background — you can close this dialog.`
    );
    if (!ok) return;

    autoCompleteCancelled.current = false;
    setAutoCompleteRunning(true);
    setAutoCompleteProgress(null);
    const toastId = toast.loading("Starting delivery completion…");

    try {
      for (let pass = 1; pass <= 2; pass++) {
        if (autoCompleteCancelled.current) break;

        toast.loading(`Pass ${pass}/2 — verifying Resend delivery log…`, { id: toastId });
        const res = await axios.post(
          `${API}/admin/applications/fix-missing-corrected-passes-v2?edition=${encodeURIComponent(editionFilter)}`,
          {},
          { ...authHeader, timeout: 2 * 60 * 1000 }
        );
        const { missing_count, job_id, total_approved, resend_delivered_count } = res.data;

        if (missing_count === 0) {
          toast.success(
            `All ${total_approved} approved applicants verified — corrected pass delivered to everyone. Resend log confirms ${resend_delivered_count} unique recipients.`,
            { id: toastId, duration: 15000 }
          );
          await fetchDeliveryStatus();
          break;
        }

        if (!job_id) {
          toast.warning(`${missing_count} still missing but no job started`, { id: toastId });
          break;
        }

        toast.loading(
          `Pass ${pass}/2 — sending to ${missing_count} missing (with retries)…`,
          { id: toastId }
        );
        const done = await pollJob(job_id);
        if (!done) break;

        toast.loading(
          `Pass ${pass}/2 — sent ${done.sent}/${done.total}${done.failed ? ` · ${done.failed} failed` : ""}. Re-verifying…`,
          { id: toastId }
        );
        // Small grace period so Resend log catches up
        await new Promise((r) => setTimeout(r, 4000));
      }

      // Final verification
      await fetchDeliveryStatus();
      const final = await axios.get(
        `${API}/admin/corrected-pass-status?edition=${encodeURIComponent(editionFilter)}`,
        authHeader,
      );
      const f = final.data;
      if (f.missing_count + f.failed_count === 0) {
        toast.success(
          `✓ Delivery complete — ${f.delivered_count}/${f.total_approved} verified via Resend.`,
          { id: toastId, duration: 15000 }
        );
      } else {
        toast.warning(
          `${f.delivered_count}/${f.total_approved} delivered · ${f.missing_count + f.failed_count} still missing. Check the list below and retry if needed.`,
          { id: toastId, duration: 15000 }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delivery completion failed", { id: toastId });
    } finally {
      setAutoCompleteRunning(false);
      setAutoCompleteProgress(null);
    }
  };

  const forceResendEveryone = async () => {
    // Feb 2026 cross-edition READY gate — refuse until the delivery
    // status counts have been fetched and echoed back with the
    // current scope. Otherwise `deliveryStatus?.total_approved || 0`
    // could be a stale-edition number (Winter → May switch, response
    // out of order) OR a silent 0 (fetch pending) OR the response
    // from an old backend that ignored the edition param.
    if (!deliveryStatus || deliveryStatus.scope_edition !== editionFilter) {
      toast.error(
        `Delivery counts for '${editionFilter}' haven't loaded yet. ` +
        `Click Re-Verify on the Delivery Status card and retry.`
      );
      return;
    }
    const total = deliveryStatus.total_approved;
    if (total === 0) {
      toast.error("No approved applicants to send to");
      return;
    }
    const scopeLabel = editionFilter === "winter"
      ? "Winter 2026"
      : editionFilter === "may"
      ? "Legacy May 2026"
      : editionFilter === "unclassified"
      ? "Unclassified"
      : "ALL Editions (Winter + May)";
    const ok = window.confirm(
      `SCOPE: ${scopeLabel} (edition=${editionFilter}).\n\n` +
      `Force-send a FRESH Entry Pass to every approved applicant in that scope?\n\n` +
      `Every email will have a UNIQUE subject line including their pass code and name — ` +
      `Gmail can't thread or dedupe these with any earlier email, so each applicant will ` +
      `see a brand-new message in their inbox.\n\n` +
      `Use this when you want to guarantee everyone receives a visible, un-threaded copy. ` +
      `Runs in the background at 1 email/second.`
    );
    if (!ok) return;

    autoCompleteCancelled.current = false;
    setAutoCompleteRunning(true);
    setAutoCompleteProgress(null);
    const toastId = toast.loading(`Starting force-resend (${scopeLabel})…`);

    try {
      const res = await axios.post(
        `${API}/admin/applications/force-resend-all-corrected-v2?edition=${encodeURIComponent(editionFilter)}`,
        {},
        { ...authHeader, timeout: 60 * 1000 }
      );
      const { total: t, job_id } = res.data;
      if (!job_id) {
        toast.error("Failed to start force-resend", { id: toastId });
        return;
      }
      toast.loading(`Force-resending to all ${t} applicants…`, { id: toastId });
      const done = await pollJob(job_id);
      if (!done) return;

      await new Promise((r) => setTimeout(r, 4000)); // grace for Resend to log
      await fetchDeliveryStatus();
      const final = await axios.get(`${API}/admin/corrected-pass-status`, authHeader);
      const f = final.data;
      toast.success(
        `✓ Force-resend complete — ${done.sent}/${done.total} sent${done.failed ? ` · ${done.failed} failed` : ""}. Resend-verified: ${f.delivered_count}/${f.total_approved}.`,
        { id: toastId, duration: 18000 }
      );
    } catch (err) {
      let msg;
      if (err.code === "ECONNABORTED") {
        msg = "Force-resend request timed out before the job started. Please retry.";
      } else if (err.code === "ERR_NETWORK") {
        msg = "Network connection lost. Please check your internet and retry.";
      } else if (err.response?.status >= 500) {
        msg = `Server error ${err.response.status} while starting force-resend. Try again in 30 seconds.`;
      } else {
        msg = err.response?.data?.detail || `Force-resend failed (${err.message || "unknown"})`;
      }
      toast.error(msg, { id: toastId, duration: 10000 });
      // eslint-disable-next-line no-console
      console.error("[forceResendAll] failed:", err);
    } finally {
      setAutoCompleteRunning(false);
      setAutoCompleteProgress(null);
    }
  };

  const composeApprovalEmail = (app) => {
    const subject = encodeURIComponent("You're Approved — CrewZNatioN × Rimal Center Private Gathering");
    const body = encodeURIComponent(
      `Hello ${app.full_name},\n\n` +
        `Congratulations — your application for the CrewZNatioN × Rimal Center Private Automotive Gathering has been APPROVED.\n\n` +
        `EVENT DETAILS\n` +
        `Date: ${EVENT.date}\n` +
        `Time: ${EVENT.time}\n` +
        `Location: ${EVENT.location}\n` +
        `Map: ${EVENT.mapUrl}\n\n` +
        `APPROVED VEHICLE\n` +
        `${app.vehicle_year} ${app.vehicle_make} ${app.vehicle_model} (${app.vehicle_color})\n` +
        `Plate: ${app.plate_number}\n\n` +
        `Please reply to confirm your attendance. Drive safely.\n\n` +
        `— CrewZNatioN & Rimal Center Management`
    );
    return `mailto:${app.email || ""}?subject=${subject}&body=${body}`;
  };

  const composeRejectionEmail = (app) => {
    const subject = encodeURIComponent("CrewZNatioN × Rimal Center Application Update");
    const body = encodeURIComponent(
      `Hello ${app.full_name},\n\n` +
        `Thank you for applying to the CrewZNatioN × Rimal Center Private Automotive Gathering.\n\n` +
        `After careful review, we regret to inform you that your application was not approved for this edition. ` +
        `Due to limited entry, management decisions are final.\n\n` +
        `We hope to see you at a future gathering.\n\n` +
        `— CrewZNatioN & Rimal Center Management`
    );
    return `mailto:${app.email || ""}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-black text-white" dir="ltr" lang="en" data-testid="admin-dashboard-root">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="flex items-center justify-between px-6 sm:px-10 py-4">
          <div className="flex items-center gap-4">
            <img src={LOGOS.crewznation} alt="" className="h-8 w-auto object-contain opacity-80" />
            <div className="h-5 w-px bg-white/20" />
            <div>
              <div className="label-kicker flex items-center gap-2">
                Admin Portal
                {isReviewer && (
                  <span
                    data-testid="reviewer-role-badge"
                    className="text-[9px] px-1.5 py-0.5 bg-[#F5C518]/15 border border-[#F5C518]/40 text-[#F5C518] tracking-[0.2em] uppercase font-heading font-bold"
                  >
                    Reviewer
                  </span>
                )}
              </div>
              <div className="font-body text-xs text-white/40">{adminName} · {adminEmail}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={async () => {
                if (!adminEmail) { toast.error("No admin email on this session."); return; }
                if (!window.confirm(`Send a password-reset token to ${adminEmail}? The email arrives in ~30 seconds. The token is valid for 30 minutes and can only be used once.`)) return;
                try {
                  const jwt = localStorage.getItem("admin_token");
                  const r = await axios.post(`${API}/admin/password-reset/dispatch`,
                    { email: adminEmail },
                    { headers: { Authorization: `Bearer ${jwt}` } });
                  toast.success(`Reset token sent to ${r.data?.to || adminEmail}. Check your inbox, then sign out and use “Forgot password?” to paste it in.`);
                } catch (err) {
                  const detail = err?.response?.data?.detail || "Reset dispatch failed.";
                  toast.error(detail);
                }
              }}
              data-testid="self-service-reset-btn"
              className="hidden sm:inline-flex items-center gap-2 text-xs text-white/50 hover:text-[#F5C518] transition-colors tracking-[0.15em] uppercase"
              title="Sends a single-use, 30-min token to your admin email so you can rotate your password on your phone or laptop without any terminal commands."
            >
              <KeyRound className="w-3.5 h-3.5" /> Reset my password
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-xs text-white/50 hover:text-[#F5C518] transition-colors tracking-[0.15em] uppercase"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-10 py-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-extrabold text-white text-3xl sm:text-4xl mb-2">
              Applications <span className="text-[#F5C518]">Dashboard</span>
            </h1>
            <p className="font-body text-white/40 text-sm">
              Review and approve vehicle applications for the private gathering.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={bust(`${API}/admin/guest-book.html?status=approved`)}
              data-authed-download="1"
              data-download-filename="guest-book.html"
              target="_blank"
              rel="noopener noreferrer"
              onClick={blockClickIfReviewer(isReviewer, "Guest Book")}
              data-testid="guest-book-btn"
              className="btn-ghost inline-flex items-center gap-2"
              title="Visual guest book with car photos — printable"
            >
              <Car className="w-4 h-4" /> Guest Book
            </a>
            <a
              href={bust(`${API}/admin/applications-guest-list.csv`)}
              data-authed-download="1"
              data-download-filename="applications-guest-list.csv"
              onClick={blockClickIfReviewer(isReviewer, "Guest List CSV")}
              data-testid="export-guest-list-btn"
              className="btn-ghost inline-flex items-center gap-2"
              title="All applicants · Name · Car · Primary Photo"
            >
              <Users className="w-4 h-4" /> Guest List
            </a>
            <a
              href={bust(`${API}/admin/approved-deduplicated.csv`)}
              data-authed-download="1"
              data-download-filename="approved-deduplicated.csv"
              onClick={blockClickIfReviewer(isReviewer, "Approved (Deduplicated)")}
              data-testid="export-approved-deduped-btn"
              className="btn-ghost inline-flex items-center gap-2"
              title="Approved applicants only · duplicates removed · Name + Car"
            >
              <Users className="w-4 h-4" /> Approved (Deduped)
            </a>
            <a
              href={bust(`${API}/admin/entry-passes-prepare?edition=${editionFilter}`)}
              data-authed-prepare-download="1"
              data-download-filename={`entry-passes-${editionFilter}.zip`}
              onClick={blockClickIfReviewer(isReviewer, "All Passes (PDF ZIP)")}
              data-testid="download-all-pdfs-btn"
              className="btn-ghost inline-flex items-center gap-2"
              title={`ZIP of every approved guest's Entry Pass PDF + a manifest CSV (Name · Car · Mobile · Email · Crew · Pass Code) for the current edition scope (${editionFilter}).`}
            >
              <Download className="w-4 h-4" /> All Passes (PDF ZIP)
              <span className="text-[10px] tracking-wider uppercase opacity-70">
                · {editionFilter}
              </span>
            </a>
            <a
              href={bust(`${API}/admin/applications.csv?edition=${editionFilter}&status=${activeTab}`)}
              data-authed-download="1"
              data-download-filename={`applications-${editionFilter}-${activeTab}.csv`}
              onClick={blockClickIfReviewer(isReviewer, "Export CSV")}
              data-testid="export-csv-btn"
              className="btn-ghost inline-flex items-center gap-2"
              title={`Export the current view (${editionFilter === "winter" ? "Winter 2026" : editionFilter === "may" ? "Legacy May 2026" : editionFilter === "all" ? "All Editions (incl. May)" : "Unclassified"} · ${activeTab === "all" ? "every status" : activeTab}) as a CSV with a per-row Missing Fields column.`}
            >
              <Download className="w-4 h-4" /> Export CSV
              <span className="text-[10px] tracking-wider uppercase opacity-70">
                · {editionFilter}
              </span>
            </a>
            <a
              href={bust(`${API}/admin/applications.csv?edition=${editionFilter}&status=${activeTab}&missing_only=true`)}
              data-authed-download="1"
              data-download-filename={`applications-${editionFilter}-missing-info.csv`}
              onClick={blockClickIfReviewer(isReviewer, "Missing-Info CSV")}
              data-testid="export-missing-info-csv-btn"
              className="btn-ghost inline-flex items-center gap-2 hover:!border-amber-400 hover:!text-amber-300"
              title="Download ONLY the applicants (in the current edition + status view) who are missing at least one recoverable field. Each row's Missing Fields column names the exact gaps (email / mobile / origin / photo / plate / day / timestamp)."
            >
              <AlertTriangle className="w-4 h-4" /> Missing-Info CSV
              <span className="text-[10px] tracking-wider uppercase opacity-70">
                · {editionFilter}
              </span>
            </a>
          </div>
        </div>

        {/* Per-Crew Guest Lists — visible to BOTH roles, click-blocked for reviewer */}
        <CrewGuestLists token={token} isReviewer={isReviewer} />

        {/* Master Exports — ADMIN ONLY · completely hidden from reviewers */}
        {!isReviewer && (
          <div
            data-testid="master-exports-panel"
            className="mb-6 border-2 p-4 sm:p-5"
            style={{ borderColor: "#EF4444" + "55", background: "#EF4444" + "08" }}
          >
            <div className="label-kicker mb-2" style={{ color: "#EF4444" }}>
              🔒 Admin-Only Master Exports · Confidential
            </div>
            <div className="text-white/70 text-xs mb-3 leading-relaxed">
              Full per-applicant data dump — every field submitted plus backend status timestamps.
              These two lists are <strong className="text-white">hidden from reviewers</strong> at
              both the UI level and the backend API level (reviewer tokens receive a 401).
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={bust(`${API}/admin/applications-full-export.csv?status=approved`)}
              data-authed-download="1"
              data-download-filename="applications-full-export.csv"
                data-testid="master-export-approved-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#22C55E] text-black font-heading font-bold tracking-wider uppercase hover:bg-[#16A34A] transition"
                title="Confidential · all approved applicants with every submitted field"
              >
                <Download className="w-4 h-4" /> Approved · Master Full Export
              </a>
              <a
                href={bust(`${API}/admin/applications-full-export.csv?status=rejected`)}
              data-authed-download="1"
              data-download-filename="applications-full-export.csv"
                data-testid="master-export-rejected-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#EF4444] text-white font-heading font-bold tracking-wider uppercase hover:bg-[#DC2626] transition"
                title="Confidential · all rejected applicants with every submitted field"
              >
                <Download className="w-4 h-4" /> Rejected · Master Full Export
              </a>
              <a
                href={bust(`${API}/admin/blur-kit-master-full.csv`)}
              data-authed-download="1"
              data-download-filename="blur-kit-master-full.csv"
                data-testid="master-export-blur-kit-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5C518] text-black font-heading font-bold tracking-wider uppercase hover:bg-[#E0B30E] transition"
                title="Confidential · all Blur Kit applicants (approved + rejected) with every submitted field"
              >
                <Download className="w-4 h-4" /> Blur Kit Guest List Approvals &amp; Rejections
              </a>
              <a
                href={bust(`${API}/admin/violations-export.xlsx`)}
              data-authed-download="1"
              data-download-filename="violations-export.xlsx"
                data-testid="master-export-violations-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5C518] text-black font-heading font-bold tracking-wider uppercase hover:bg-[#E0B30E] transition"
                title="Excel file · 4 columns + 2 embedded high-resolution vehicle photos per approved applicant · may take a few minutes to download"
              >
                <Download className="w-4 h-4" /> The Gathering Violations (.xlsx)
              </a>
              <a
                href={bust(`${API}/admin/violations-export.csv?status=rejected`)}
              data-authed-download="1"
              data-download-filename="violations-export.csv"
                data-testid="master-export-violations-rejections-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#EF4444] text-white font-heading font-bold tracking-wider uppercase hover:bg-[#DC2626] transition"
                title="CSV file · 461 rejected applicants with photo URLs · downloads instantly (tap URL in Excel to view the car photo)"
              >
                <Download className="w-4 h-4" /> The Gathering Violations - Rejections (.csv)
              </a>
              <a
                href={bust(`${API}/admin/corvettes-export.xlsx`)}
              data-authed-download="1"
              data-download-filename="corvettes-export.xlsx"
                data-testid="master-export-corvettes-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5C518] text-black font-heading font-bold tracking-wider uppercase hover:bg-[#E0B30E] transition"
                title="Excel file · all Corvettes (approved + rejected) with embedded vehicle photos"
              >
                <Download className="w-4 h-4" /> Corvettes (.xlsx)
              </a>
              <PhotoArchiveDownloadButton token={token} />
            </div>
            <PhotoArchiveByteBoundedPanel token={token} />
          </div>
        )}

        {/* Duplicate Cleanup — ADMIN ONLY. Hidden in the May 2026 read-only
            archive since its Archive/Restore actions would mutate approved
            records (backend also refuses the mutation for archived rows). */}
        {!isReviewer && !isMayArchive && duplicates && (
          <DuplicateCleanupPanel
            data={duplicates}
            busy={duplicatesBusy}
            token={token}
            onArchive={archiveDuplicates}
            onRestore={restoreDuplicates}
            onRefresh={fetchDuplicates}
          />
        )}

        {/* SMS Blast (Twilio) — ADMIN ONLY. Hidden in the May 2026 read-only
            archive; SMS/blast operations are Winter-only per archive policy. */}
        {!isReviewer && !isMayArchive && smsStatus && (
          <SmsBlastPanel
            data={smsStatus}
            audit={smsAudit}
            token={token}
            busy={smsBusy}
            ready={smsStatus.scope_edition === editionFilter}
            onTest={sendTestSms}
            onBlast={() => blastSms(false)}
            onRetryFailed={() => blastSms(true)}
            onRefresh={fetchSmsStatus}
          />
        )}

        {/* Delivery Status — verified against Resend — visible to BOTH roles, read-only for reviewer */}
        <DeliveryStatusCard
          status={deliveryStatus}
          loading={deliveryLoading}
          ready={!!deliveryStatus && deliveryStatus.scope_edition === editionFilter}
          onRefresh={fetchDeliveryStatus}
          onComplete={completeDelivery}
          onForceAll={forceResendEveryone}
          running={autoCompleteRunning}
          progress={autoCompleteProgress}
          readOnly={isReviewer}
          archiveReadOnly={isMayArchive}
        />

        {/* Rev 7.1 (Sep 25 2026) — Visitor Registrations panel.
            Separate from vehicle applications and vehicle admin
            emails, always visible to admins in read/action mode. */}
        {!isReviewer && !isMayArchive && (
          <AdminVisitorsPanel token={token} />
        )}

        {/* Reviewer Access — ADMIN ONLY */}
        {!isReviewer && reviewerAccess.length > 0 && (
          <ReviewerAccessPanel
            reviewers={reviewerAccess}
            onExtend={extendReviewer}
            onRevoke={revokeReviewer}
            onRefresh={fetchReviewerAccess}
          />
        )}

        {/* Reviewer Activity (full audit trail) — ADMIN ONLY */}
        {!isReviewer && reviewerActivity && (reviewerActivity.total > 0 || (reviewerActivity.click_total || 0) > 0) && (
          <ReviewerActivityPanel
            activity={reviewerActivity}
            open={activityOpen}
            onToggle={() => setActivityOpen(!activityOpen)}
            filter={activityFilter}
            onFilterChange={setActivityFilter}
            onJump={(appId) => {
              const found = applications.find((a) => a.id === appId);
              if (found) setSelected(found);
              else toast.info("Switch to All tab to load this applicant");
            }}
            onRefresh={fetchReviewerActivity}
          />
        )}

        {/* Pending reviewer recommendations — ADMIN ONLY banner above stats */}
        {!isReviewer && allPendingRecs.length > 0 && (
          <div
            data-testid="pending-recs-banner"
            className="border-2 border-[#F5C518] bg-[#F5C518]/10 p-4 sm:p-5 mb-6"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="label-kicker mb-1" style={{ color: "#F5C518" }}>
                  Pending Reviewer Recommendations
                </div>
                <div className="font-heading text-white text-base sm:text-lg">
                  {allPendingRecs.length} application{allPendingRecs.length === 1 ? "" : "s"} awaiting your decision
                </div>
                <div className="text-xs text-white/60 mt-1">
                  Click into any applicant below to see the recommendation + accept or dismiss it.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {allPendingRecs.slice(0, 3).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      const found = applications.find((a) => a.id === r.application_id);
                      if (found) setSelected(found);
                      else toast.info("Switch to All / Pending tab to load this applicant");
                    }}
                    className="text-xs px-3 py-1.5 border border-[#F5C518] text-[#F5C518] font-heading tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition"
                    title={`${r.reviewer_name} recommends ${r.decision}`}
                  >
                    {r.applicant_name} · {r.decision === "approved" ? "✓" : "✗"}
                  </button>
                ))}
                {allPendingRecs.length > 3 && (
                  <span className="text-xs text-white/50 self-center">+{allPendingRecs.length - 3} more</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Explicit edition-scope heading (Sept 12, 2026 owner directive):
            "All Editions" totals include May 2026 legacy records and MUST
            NOT be mistaken for the current Winter 2026 registration count.
            The heading always names the active scope so no counter row can
            be read out of context. */}
        {(() => {
          const scopeLabel = editionFilter === "winter"
            ? "Winter 2026 (Dec 10–12)"
            : editionFilter === "may"
            ? "Legacy · May 2026 only"
            : editionFilter === "unclassified"
            ? "Unclassified (missing/unparseable timestamp)"
            : "All Editions · Winter 2026 + Legacy May 2026 combined";
          const isCombined = editionFilter === "all";
          const isWinter = editionFilter === "winter";
          return (
            <div
              data-testid="edition-scope-heading"
              className={
                "flex flex-wrap items-center gap-2 mb-2 text-[11px] uppercase tracking-wider font-heading font-bold " +
                (isWinter ? "text-[#F5C518]" : isCombined ? "text-amber-300" : "text-white/70")
              }
            >
              <span>Counts scope:</span>
              <span
                className={
                  "px-2 py-0.5 border " +
                  (isWinter
                    ? "border-[#F5C518] bg-[#F5C518] text-black"
                    : isCombined
                    ? "border-amber-300 bg-amber-300/10 text-amber-200"
                    : "border-white/40 bg-white/[0.04] text-white/80")
                }
              >
                {scopeLabel}
              </span>
              {isCombined && (
                <span className="normal-case tracking-normal text-amber-200/80 text-xs font-body">
                  ⚠ Includes legacy May 2026 rows — NOT the current-event registration count.
                </span>
              )}
              {stats.__scope && stats.__scope !== `${activeTab}|${dayFilter}|${editionFilter}|${attendDay || ""}` && (
                <span
                  data-testid="stats-scope-mismatch-warning"
                  className="normal-case tracking-normal text-red-300 text-xs font-body"
                >
                  ⚠ Counters below are still refreshing — do not read them yet.
                </span>
              )}
            </div>
          );
        })()}

        {/* May 2026 read-only archive banner — clearly identifies the
            archived-view state without editing any approved copy elsewhere.
            Shown only when `edition=may` is active. All actions on May rows
            are refused server-side; this banner is the UI honesty layer. */}
        {isMayArchive && (
          <div
            data-testid="may-archive-banner"
            className="mb-4 border border-amber-300 bg-amber-300/[0.06] p-4 flex flex-wrap items-start gap-3"
          >
            <span className="inline-flex items-center gap-2 px-2 py-0.5 border border-amber-300 bg-amber-300 text-black text-[10px] tracking-[0.2em] uppercase font-heading font-bold">
              Read-Only Archive
            </span>
            <div className="flex-1 min-w-[220px] text-amber-100 text-sm font-body leading-relaxed">
              <span className="block font-heading font-bold text-amber-200 tracking-wide uppercase text-[11px] mb-1">
                May 2026 · Preserved Records
              </span>
              You're viewing the May 2026 archive. Applications, photos, recorded
              statuses, search, and read-only exports remain available.
              Approvals, day assignments, edits, deletions, resends, SMS and
              campaign messages are disabled for these records. Switch the
              edition to Winter to resume the active event.
            </div>
          </div>
        )}

        {/* Stats grid — for reviewer, overlay his pending recommendations onto the counts */}
        {(() => {
          let displayStats = stats;
          if (isReviewer && allPendingRecs.length > 0) {
            const approvedDelta = allPendingRecs.filter((r) => r.decision === "approved").length;
            const rejectedDelta = allPendingRecs.filter((r) => r.decision === "rejected").length;
            displayStats = {
              total: stats.total,
              pending: Math.max(0, (stats.pending || 0) - approvedDelta - rejectedDelta),
              approved: (stats.approved || 0) + approvedDelta,
              rejected: (stats.rejected || 0) + rejectedDelta,
            };
          }
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <StatCard label="Total" value={displayStats.total} loading={stats.__loading === true} icon={Users} color="#FFFFFF" />
              <StatCard label="Pending" value={displayStats.pending} loading={stats.__loading === true} icon={Clock} color="#F5C518" />
              <StatCard label="Approved" value={displayStats.approved} loading={stats.__loading === true} icon={CheckCircle2} color="#22C55E" />
              <StatCard label="Rejected" value={displayStats.rejected} loading={stats.__loading === true} icon={XCircle} color="#EF4444" />
            </div>
          );
        })()}

        {!isReviewer && (
          <AttendanceSummary
            summary={attendSummary}
            loading={attendLoading}
            error={attendError}
            attendDay={attendDay}
            onSelectDay={setAttendDay}
          />
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-white/10">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            // For the reviewer: overlay his recommendations onto the tab counts
            // so the tabs reflect what he sees in the list below.
            // Sept 12, 2026 owner-directed correction: when the stats endpoint
            // failed, `stats[tab.id]` is null/undefined — coercing to `0`
            // would falsely show empty counts as truth. Render `?` instead
            // so the tab count is honestly unknown until a successful retry.
            const raw = tab.id === "all" ? stats.total : stats[tab.id];
            const isUnknown = raw === null || raw === undefined;
            // Sep 14 2026 owner-corrected: while stats are refreshing (loading
            // AND no value yet for this scope), render "…" so the admin does
            // NOT read a stale-labeled number. On confirmed failure (not
            // loading, still unknown), render "?" (existing honesty gate).
            const isRefreshing = isUnknown && stats.__loading === true;
            let baseCount = isUnknown ? 0 : raw;
            if (isReviewer && allPendingRecs.length > 0 && tab.id !== "all") {
              const approvedDelta = allPendingRecs.filter((r) => r.decision === "approved").length;
              const rejectedDelta = allPendingRecs.filter((r) => r.decision === "rejected").length;
              if (tab.id === "approved") baseCount = baseCount + approvedDelta;
              else if (tab.id === "rejected") baseCount = baseCount + rejectedDelta;
              else if (tab.id === "pending") baseCount = Math.max(0, baseCount - approvedDelta - rejectedDelta);
            }
            const countDisplay = isRefreshing ? "…" : (isUnknown ? "?" : baseCount);
            return (
              <button
                key={tab.id}
                onClick={() => { setAttendDay(null); setActiveTab(tab.id); }}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 font-body text-sm tracking-wider uppercase transition-all ${
                  activeTab === tab.id
                    ? "text-[#F5C518] border-b-2 border-[#F5C518] -mb-px"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
                <span className="text-xs opacity-60">({countDisplay})</span>
              </button>
            );
          })}
        </div>

        {/* Edition filter — admin-only, read-only classification by created_at */}
        {!isReviewer && (
          <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="edition-filter-row">
            <span className="text-[10px] text-white/40 tracking-[0.2em] uppercase font-body">
              Edition
            </span>
            <div className="flex flex-wrap gap-1">
              {EDITIONS.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setEditionFilter(e.id)}
                  data-testid={`edition-filter-${e.id}`}
                  className={`px-3 py-1.5 text-xs font-body tracking-wider uppercase border transition ${
                    editionFilter === e.id
                      ? "border-[#F5C518] text-[#F5C518] bg-[#F5C518]/10"
                      : "border-white/15 text-white/50 hover:border-white/40 hover:text-white/80"
                  }`}
                >
                  {e.label}
                </button>
              ))}
            </div>
            {/* Owner-visibility shortcut (Feb 2026): one click clears every
                filter so the admin sees pending + approved + rejected across
                Winter + May + Unclassified with any day assignment. */}
            <button
              type="button"
              data-testid="show-all-applicants-btn"
              onClick={() => {
                setActiveTab("all");
                setEditionFilter("all");
                setDayFilter("all");
                setMissingOnly(false);
              }}
              className="ms-auto inline-flex items-center gap-2 px-3 py-1.5 text-xs font-heading font-bold tracking-wider uppercase border border-[#F5C518] bg-[#F5C518] text-black hover:bg-[#E0B30E] transition"
              title="Clear every filter and show every applicant (pending + approved + rejected + unassigned + flagged)"
            >
              Show ALL applicants
            </button>
            <button
              type="button"
              data-testid="incomplete-pending-btn"
              onClick={() => {
                setActiveTab("pending");
                setEditionFilter("winter");
                setDayFilter("all");
                setMissingOnly(true);
                try {
                  toast("Filter: Winter · pending · essentials missing (excludes country/city gaps and approved rows).", {
                    duration: 8000,
                    icon: "🎯",
                  });
                } catch (_) {}
              }}
              aria-pressed={activeTab === "pending" && editionFilter === "winter" && missingOnly}
              className={
                "inline-flex items-center gap-2 px-3 py-1.5 text-xs font-heading font-bold tracking-wider uppercase border transition " +
                (activeTab === "pending" && editionFilter === "winter" && missingOnly
                  ? "border-orange-400 bg-orange-400 text-black hover:bg-orange-300"
                  : "border-orange-400/60 text-orange-300 hover:bg-orange-400/10")
              }
              title="One-click: Winter 2026 · pending status · applicants missing at least one historically-required registration field (name / age / email / mobile / vehicle make+model+year+color / plate / primary photo). EXCLUDES: approved and rejected rows, newly-added country/city (never counted retroactively), admin-assigned participation day, timestamps, and failed photo GETs."
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Incomplete pending applications
            </button>
            <button
              type="button"
              data-testid="verify-applicants-btn"
              disabled={reconLoading}
              onClick={async () => {
                setReconLoading(true);
                try {
                  const r = await axios.get(`${API}/admin/applications-reconciliation`, authHeader);
                  const d = r.data || {};
                  const flagged = (applications || []).filter((a) => a.flag_needs_review).length;
                  const truncated = d.truncated === true;
                  const verified = d.verified === true;
                  const returnedCount = typeof d.api_surfaced_ids === "number"
                    ? d.api_surfaced_ids
                    : (Array.isArray(d.api_surfaced_ids) ? d.api_surfaced_ids.length : 0);
                  const unaccountedCount = (d.unaccounted_ids || []).length;
                  const result = {
                    ok: verified,
                    db_total: d.db_total ?? 0,
                    returned: returnedCount,
                    unaccounted: unaccountedCount,
                    flagged,
                    truncated,
                    checked_at: new Date().toISOString(),
                    error: null,
                    db_name: d.db_name || null,
                  };
                  setReconResult(result);
                  const emoji = verified ? "✅" : (truncated ? "⚠️ TRUNCATED" : "⚠️");
                  toast[verified ? "success" : "error"](
                    `${emoji} db=${result.db_total} · returned=${returnedCount} · unaccounted=${unaccountedCount} · flagged=${flagged}${truncated ? " · response was truncated — NOT verified" : ""}`,
                    { duration: 14000 },
                  );
                } catch (e) {
                  const msg = e.response?.status || e.message || String(e);
                  setReconResult({
                    ok: false,
                    error: `Verify failed: ${msg}`,
                    checked_at: new Date().toISOString(),
                  });
                  toast.error(`Verify failed: ${msg}`);
                } finally {
                  setReconLoading(false);
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-heading font-bold tracking-wider uppercase border border-white/40 bg-black text-white hover:bg-white/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reconcile stored applicant IDs against the API-returned list — proves every applicant is accounted for"
            >
              {reconLoading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying…</>
              ) : (
                <>Verify applicants</>
              )}
            </button>
          </div>
        )}
        {reconResult && (
          <div
            data-testid="verify-applicants-result"
            className={
              "mt-3 mb-4 px-4 py-3 border font-body text-sm flex flex-wrap items-start gap-3 " +
              (reconResult.error
                ? "border-red-500/60 bg-red-500/10 text-red-100"
                : reconResult.ok
                ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-100"
                : "border-amber-400/60 bg-amber-400/10 text-amber-100")
            }
          >
            <div className="min-w-0 flex-1">
              <div className="uppercase tracking-wider text-[10px] font-heading font-bold mb-1 opacity-80">
                Last verification · {new Date(reconResult.checked_at).toLocaleString()}
              </div>
              {reconResult.error ? (
                <div>{reconResult.error}</div>
              ) : (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span data-testid="recon-db-total">
                    <span className="opacity-70 text-[11px] uppercase tracking-wider mr-1">Stored:</span>
                    <span className="font-heading font-bold">{reconResult.db_total}</span>
                  </span>
                  <span data-testid="recon-returned">
                    <span className="opacity-70 text-[11px] uppercase tracking-wider mr-1">Returned:</span>
                    <span className="font-heading font-bold">{reconResult.returned}</span>
                  </span>
                  <span data-testid="recon-unaccounted">
                    <span className="opacity-70 text-[11px] uppercase tracking-wider mr-1">Unaccounted:</span>
                    <span className="font-heading font-bold">{reconResult.unaccounted}</span>
                  </span>
                  <span data-testid="recon-flagged">
                    <span className="opacity-70 text-[11px] uppercase tracking-wider mr-1">Flagged for review:</span>
                    <span className="font-heading font-bold">{reconResult.flagged}</span>
                  </span>
                  {reconResult.db_name && (
                    <span className="opacity-60 text-[11px] italic">
                      db: {reconResult.db_name}
                    </span>
                  )}
                  {reconResult.truncated && (
                    <span className="px-2 py-0.5 border border-amber-400 bg-amber-400 text-black uppercase font-heading text-[10px] tracking-wider font-bold">
                      Response truncated — NOT verified
                    </span>
                  )}
                  {reconResult.ok && !reconResult.truncated && (
                    <span className="px-2 py-0.5 border border-emerald-400 bg-emerald-400 text-black uppercase font-heading text-[10px] tracking-wider font-bold">
                      ✓ Every stored applicant accounted for
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="verify-applicants-result-dismiss"
              onClick={() => setReconResult(null)}
              className="opacity-60 hover:opacity-100 transition p-1 -m-1"
              title="Dismiss the last verification result"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {missingOnly && (() => {
          const scoped = (applications || []).filter((a) => {
            if (isReviewer && activeTab !== "all") {
              const effective = (pendingRecs[a.id]?.decision) || a.status;
              if (effective !== activeTab) return false;
            }
            return true;
          });
          const rows = [];
          for (const a of scoped) {
            if (!isIncompletePending(a)) continue;
            rows.push({ a, missing: essentialGaps(a) });
          }
          return (
            <div
              data-testid="missing-info-summary"
              className="mb-4 border border-orange-400/60 bg-orange-400/[0.06] p-4"
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                <span className="text-[11px] uppercase tracking-wider font-heading font-bold text-orange-300">
                  Incomplete pending applications · Winter 2026
                </span>
                <span
                  data-testid="missing-info-summary-count"
                  className="font-heading font-bold text-orange-200 text-lg"
                >
                  {rows.length} pending applicant{rows.length === 1 ? "" : "s"} missing essentials
                </span>
                {(editionFilter !== "winter" || activeTab !== "pending") && (
                  <span
                    data-testid="incomplete-scope-warning"
                    className="normal-case tracking-normal text-red-300 text-xs font-body"
                  >
                    ⚠ Filter isn't Winter + pending — click "Incomplete pending applications" above to reset.
                  </span>
                )}
              </div>
              <div className="text-orange-100/70 text-[11px] mb-2">
                Excludes: approved and rejected rows · newly-added country/city (never counted retroactively) · admin-assigned participation day · timestamps · failed photo GETs · optional/retired fields.
              </div>
              {rows.length === 0 ? (
                <div data-testid="missing-info-summary-empty" className="text-orange-100/70 text-sm">
                  No pending Winter applicant is missing an essential registration field in the current view. If you expect entries here, verify the edition and status filters above.
                </div>
              ) : (
                <ul
                  data-testid="missing-info-summary-list"
                  className="text-sm text-orange-100 divide-y divide-orange-400/20 max-h-64 overflow-auto"
                >
                  {rows.slice(0, 100).map(({ a, missing }) => (
                    <li
                      key={a.id}
                      data-testid={`missing-info-summary-row-${a.id}`}
                      className="py-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1"
                    >
                      <span className="font-heading font-bold text-orange-200 min-w-0 truncate">
                        {a.full_name || <em className="opacity-70">— no name —</em>}
                      </span>
                      <span className="text-[11px] opacity-60 tracking-wider uppercase">
                        ID {String(a.id).slice(0, 8)}
                      </span>
                      <span className="text-[11px] opacity-70">pending</span>
                      <span className="text-[11px] opacity-70">
                        Missing: <span className="text-orange-300 font-heading font-bold">{missing.join(" · ")}</span>
                      </span>
                    </li>
                  ))}
                  {rows.length > 100 && (
                    <li className="py-1.5 text-[11px] opacity-70 italic">
                      … {rows.length - 100} more not shown here. Use the Incomplete-Pending CSV button above to export them all.
                    </li>
                  )}
                </ul>
              )}
            </div>
          );
        })()}
        {loadError && (
          <div
            data-testid="load-error-banner"
            className="flex flex-wrap items-center gap-3 mb-4 px-4 py-3 border border-red-500/60 bg-red-500/10 text-red-200 font-body text-sm"
          >
            <span className="uppercase tracking-wider text-xs font-bold text-red-300">
              Load error
            </span>
            <span>{loadError.message}</span>
            <button
              type="button"
              onClick={fetchData}
              data-testid="load-error-retry-btn"
              className="ms-auto px-3 py-1 border border-red-300 text-red-100 font-heading font-bold tracking-wider uppercase text-xs hover:bg-red-500/30 transition"
            >
              Retry
            </button>
          </div>
        )}
        {stats.__stale && !loadError && (
          <div className="mb-4 px-3 py-2 border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 text-xs">
            Filter results unavailable — counts cleared.
          </div>
        )}

        {/* Participation Day filter — admin-only, combinable with status tab above */}
        {!isReviewer && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-[10px] text-white/40 tracking-[0.2em] uppercase font-body">
              Participation Day
            </span>
            <div className="flex flex-wrap gap-1">
              {DAY_FILTERS.map((d) => (
                <button
                  key={d.id}
                  onClick={() => { setAttendDay(null); setDayFilter(d.id); }}
                  data-testid={`day-filter-${d.id}`}
                  className={`px-3 py-1.5 text-xs font-body tracking-wider uppercase border transition ${
                    dayFilter === d.id
                      ? "border-[#F5C518] text-[#F5C518] bg-[#F5C518]/10"
                      : "border-white/15 text-white/50 hover:border-white/40 hover:text-white/80"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Applications list */}
        {/* Sep 12, 2026 owner-directed correction: `listError` / `statsError` state
            was captured but never rendered — a truncated or failed load could
            appear identical to a complete load. These banners make the
            incomplete state impossible to miss and refuse to let anyone
            treat a partial response as "all applicants". A truncated load is
            surfaced as INCOMPLETE, never described as "missing applicants". */}
        {listError?.truncated && (
          <div
            className="mb-4 border border-[#F5C518]/70 bg-[#F5C518]/10 px-4 py-3 text-[#F5C518] font-body text-sm tracking-wide"
            data-testid="admin-list-truncated-banner"
            role="alert"
          >
            <div className="uppercase font-semibold tracking-wider">⚠️ Incomplete load — NOT all applicants</div>
            <div className="mt-1 text-white/80">{listError.message}</div>
          </div>
        )}
        {listError && !listError.truncated && (
          <div
            className="mb-4 border border-red-500/60 bg-red-500/10 px-4 py-3 text-red-200 font-body text-sm"
            data-testid="admin-list-error-banner"
            role="alert"
          >
            <div className="uppercase font-semibold tracking-wider">List failed to load ({listError.status})</div>
            <div className="mt-1">{listError.message}</div>
          </div>
        )}
        {statsError && (
          <div
            className="mb-4 border border-orange-500/60 bg-orange-500/10 px-4 py-3 text-orange-200 font-body text-sm"
            data-testid="admin-stats-error-banner"
            role="alert"
          >
            <div className="uppercase font-semibold tracking-wider">Stats unavailable ({statsError.status})</div>
            <div className="mt-1">{statsError.message} — counts shown below may be stale.</div>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#F5C518] animate-spin" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-20 border border-white/10 bg-white/[0.02]">
            <Users className="w-10 h-10 text-white/20 mx-auto mb-4" />
            <p className="font-body text-white/40 text-sm tracking-wider uppercase">No applications yet</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="applications-list">
            {applications
              // Reviewer's client-side filter: when a tab filter is active (pending/approved/rejected),
              // treat the reviewer's pending recommendation as the applicant's apparent status.
              // This keeps his tab counts and list consistent with the "Approve" / "Reject" illusion.
              .filter((app) => {
                if (!isReviewer || activeTab === "all") return true;
                const effective = (pendingRecs[app.id]?.decision) || app.status;
                return effective === activeTab;
              })
              // Client-side "Incomplete pending applications" view filter
              // (Sep 12, 2026 owner-corrected predicate): row is shown ONLY
              // when it has a genuinely-missing historically-required
              // registration field. Legacy country/city gaps are NOT
              // counted. Approved/rejected rows are excluded because the
              // predicate demands `status === "pending"`. Row is NEVER
              // dropped from the API response — only hidden from this
              // view. Toggle the button again to see everyone.
              .filter((app) => {
                if (!missingOnly) return true;
                return isIncompletePending(app);
              })
              .map((app) => (
                <AppRow
                  key={app.id}
                  app={app}
                  onSelect={() => setSelected(app)}
                  pendingRec={pendingRecs[app.id] || null}
                  isReviewer={isReviewer}
                />
              ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <DetailModal
          app={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateStatus}
          updating={updating}
          composeApproval={composeApprovalEmail}
          composeRejection={composeRejectionEmail}
          onEdited={(updated) => {
            setSelected(updated);
            fetchData();
            fetchDeliveryStatus();
          }}
          authHeader={authHeader}
          role={adminRole}
          onRecommend={submitRecommendation}
          pendingRec={pendingRecs[selected.id] || null}
          onAcceptRec={acceptRecommendation}
          onDismissRec={dismissRecommendation}
          readOnlyArchive={isMayArchive}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, loading, icon: Icon, color }) {
  // When stats endpoint fails, `value` arrives as `null`/`undefined` — render
  // an explicit unavailable marker instead of `0`, which would look like a
  // real "no applicants" total to the admin. Owner directive (Sept 12, 2026).
  // Sep 14 2026 owner-corrected: distinguish LOADING (in-flight; render "…")
  // from FAILURE (settled with no value; render "UNAVAILABLE · —"). Loading
  // must NOT be styled as failure — that would misrepresent an ordinary
  // refresh as an endpoint outage.
  const isMissing = value === null || value === undefined;
  const isLoading = isMissing && loading === true;
  const isUnavailable = isMissing && !isLoading;
  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={"border p-5 " + (isUnavailable ? "border-yellow-500/40 bg-yellow-500/[0.03]" : "border-white/10 bg-white/[0.02]")}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="label-kicker" style={{ color }}>
          {label}{isUnavailable ? " · UNAVAILABLE" : ""}
        </div>
        <Icon className="w-4 h-4" style={{ color: isUnavailable ? "#EAB308" : color }} />
      </div>
      <div
        className={"font-heading font-extrabold text-3xl " + (isUnavailable ? "text-yellow-300" : (isLoading ? "text-white/60" : "text-white"))}
        data-testid={`stat-${label.toLowerCase()}`}
        title={
          isUnavailable ? "Counter unavailable — the stats endpoint failed. Click Retry to try again." :
          isLoading ? "Loading current counter…" : undefined
        }
      >
        {isUnavailable ? "—" : isLoading ? "…" : value}
      </div>
    </motion.div>
  );
}


// Admin-only: fetches the approved-photo ZIP with an Authorization: Bearer
// header (never exposes the JWT in the URL) and streams the response into a
// browser download. Supports optional part-size paging so large archives can
// be pulled in bounded chunks rather than a single multi-GB blob.
function PhotoArchiveDownloadButton({ token }) {
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("both");
  const [partSize, setPartSize] = useState(0); // 0 = single archive
  const [part, setPart] = useState(1);

  const download = async () => {
    if (!token) {
      toast.error("Not authenticated");
      return;
    }
    setBusy(true);
    const params = new URLSearchParams();
    if (scope && scope !== "both") params.set("edition", scope);
    const ps = Number(partSize) | 0;
    if (ps > 0) {
      params.set("part_size", String(ps));
      params.set("part", String(Math.max(1, Number(part) | 0)));
    }
    const url = `${API}/admin/photos-archive.zip${params.toString() ? "?" + params.toString() : ""}`;
    try {
      const resp = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status} ${body.slice(0, 120)}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const cd = resp.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/i.exec(cd);
      a.download = m ? m[1] : `the-gathering-approved-photos-${scope}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Photo archive downloaded");
    } catch (e) {
      toast.error(`Photo archive failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="photo-archive-download-panel"
      className="w-full mt-3 border border-white/10 bg-black/40 p-3 flex flex-wrap items-end gap-2"
    >
      <div className="flex flex-col text-xs text-white/70">
        <label className="label-kicker mb-1">Edition</label>
        <select
          data-testid="photo-archive-edition"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="bg-black border border-white/20 text-white text-xs px-2 py-1"
        >
          <option value="both">Both (May + Winter)</option>
          <option value="may">May 2026</option>
          <option value="winter">Winter 2026</option>
        </select>
      </div>
      <div className="flex flex-col text-xs text-white/70">
        <label className="label-kicker mb-1">Part size (0 = one file)</label>
        <input
          data-testid="photo-archive-part-size"
          type="number"
          min={0}
          max={2000}
          value={partSize}
          onChange={(e) => setPartSize(e.target.value)}
          className="bg-black border border-white/20 text-white text-xs px-2 py-1 w-24"
        />
      </div>
      <div className="flex flex-col text-xs text-white/70">
        <label className="label-kicker mb-1">Part #</label>
        <input
          data-testid="photo-archive-part-index"
          type="number"
          min={1}
          max={99}
          value={part}
          onChange={(e) => setPart(e.target.value)}
          disabled={Number(partSize) === 0}
          className="bg-black border border-white/20 text-white text-xs px-2 py-1 w-16 disabled:opacity-40"
        />
      </div>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        data-testid="photo-archive-download-btn"
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B5CF6] text-white font-heading font-bold tracking-wider uppercase hover:bg-[#7C3AED] transition disabled:opacity-60"
        title="Confidential · all APPROVED May + Winter vehicle photos (cars + motorcycles) with original bytes, anonymized folders"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {busy ? "Preparing…" : "Original Photos ZIP"}
      </button>
    </div>
  );
}


// Oct 8, 2026 — Byte-size-bounded multi-part exporter (preview-only).
// Build job → list parts → tap each download. Each ZIP ≤ max_part_bytes.
// A photo exceeding cap is NEVER silently resized; it surfaces in the
// oversize_originals list with an actionable note.
function PhotoArchiveByteBoundedPanel({ token }) {
  const [edition, setEdition] = useState("both");
  const [capMiB, setCapMiB] = useState(90);
  const [reserveKiB, setReserveKiB] = useState(64);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null); // {job_id, status, total_records, started_at, uploaded_parts_recoverable, preserve_parts, preserved_until_epoch}
  const [err, setErr] = useState("");
  // Owner directive 2026-02 — partial recovery UI state. Separate
  // from `recoverJobId` input field so pasting an id doesn't disrupt
  // an in-flight build.
  const [recoverJobId, setRecoverJobId] = useState("");

  // Feb 2026: /build now returns {job_id, status: "running"} immediately
  // to avoid Cloudflare's 100s edge timeout on large preparations. Poll
  // /status/{job_id} every 2s until status flips to "ready" or "failed".
  // Original bytes + strict cap are unchanged.
  const build = async () => {
    setBusy(true);
    setErr("");
    setPlan(null);
    setProgress(null);
    const buildStartedAt = Date.now();  // closed-over timestamp — do NOT reset per poll
    try {
      const body = {
        edition,
        max_part_bytes: Math.max(1024, Math.round(capMiB * 1024 * 1024)),
        min_free_overhead_bytes: Math.max(0, Math.round(reserveKiB * 1024)),
      };
      const r = await axios.post(
        `${API}/admin/photos-archive/byte-bounded/build`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const started = r.data;
      setProgress({
        job_id: started.job_id,
        status: started.status,
        phase: "queued",
        total_records: started.total_records,
        parts_uploaded: 0,
        parts_total: null,
        heartbeat_age_sec: 0,
        started_at: buildStartedAt,
      });

      // Bounded polling loop — 60 minute hard cap so a hung job can't
      // leave the UI pinned busy forever. Stall-detect surfaces as a
      // dedicated status so the user can abandon the job instead of
      // waiting for the full TTL.
      const deadline = Date.now() + 60 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        let s;
        try {
          s = await axios.get(
            `${API}/admin/photos-archive/byte-bounded/status/${started.job_id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (pollErr) {
          setErr(
            `Status poll failed: ${
              (pollErr && pollErr.response && pollErr.response.data &&
                pollErr.response.data.detail) || String(pollErr)
            }`,
          );
          return;
        }
        const state = s.data;
        setProgress({
          job_id: started.job_id,
          status: state.status,
          phase: state.phase || "running",
          total_records: state.total_records || started.total_records,
          parts_uploaded: state.parts_uploaded || 0,
          parts_total: state.parts_total || null,
          heartbeat_age_sec: state.heartbeat_age_sec || 0,
          started_at: buildStartedAt,  // never reset per poll
          stall_reason: state.stall_reason || null,
          // Owner directive 2026-02 — carry partial-recovery fields
          // through the poll loop so the UI can render the
          // uploaded-parts list and preservation banner even while
          // the build is still running.
          uploaded_parts_recoverable:
            state.uploaded_parts_recoverable || null,
          preserve_parts: !!state.preserve_parts,
          preserved_until_epoch: state.preserved_until_epoch || null,
          // Owner directive Feb 2026 — resumable surface. Status
          // exposes remaining_parts (what resume will upload) and
          // can_resume (true when there is at least one remaining
          // part AND no live lease conflict AND status permits).
          remaining_parts: state.remaining_parts || null,
          can_resume: !!state.can_resume,
          lease_owner: state.lease_owner || null,
          lease_until_epoch: state.lease_until_epoch || null,
          errors: state.errors || [],
        });
        if (state.status === "ready") {
          setPlan(state.plan);
          return;
        }
        if (state.status === "failed") {
          // Preserved-failed is NOT an error from the user's
          // perspective — the uploaded parts are intentionally
          // retained for download. Keep the progress visible so the
          // recoverable list stays rendered; surface a note instead
          // of a red error.
          if (state.preserve_parts) {
            setErr("");
            return;
          }
          setErr(state.error || "Preparation failed with no error message.");
          return;
        }
        if (state.status === "stalled") {
          setErr(
            state.stall_reason ||
              "Worker heartbeat lost. Use Abandon or Abandon & keep uploaded parts.",
          );
          return;
        }
      }
      setErr("Preparation did not complete within 60 minutes.");
    } catch (e) {
      setErr(
        (e && e.response && e.response.data && e.response.data.detail) ||
          String(e),
      );
    } finally {
      setBusy(false);
    }
  };

  // Owner directive 2026-02 — resume remaining parts. Idempotent;
  // uploads only the parts marked `uploaded=false` in the Mongo plan.
  // Safe to invoke multiple times — a second call while a lease is
  // held returns 409 with the current lease owner.
  const resumeRemaining = async () => {
    if (!progress || !progress.job_id) return;
    setErr("");
    try {
      const r = await axios.post(
        `${API}/admin/photos-archive/byte-bounded/resume/${progress.job_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Immediately refresh status so the UI shows "running" + the
      // live remaining list.
      try {
        const s = await axios.get(
          `${API}/admin/photos-archive/byte-bounded/status/${progress.job_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const st = s.data;
        setProgress((p) => ({
          ...(p || {}),
          job_id: progress.job_id,
          status: st.status,
          phase: st.phase || p?.phase,
          parts_uploaded: st.parts_uploaded || 0,
          parts_total: st.parts_total || null,
          heartbeat_age_sec: st.heartbeat_age_sec || 0,
          stall_reason: st.stall_reason || null,
          uploaded_parts_recoverable:
            st.uploaded_parts_recoverable || p?.uploaded_parts_recoverable || null,
          remaining_parts: st.remaining_parts || null,
          can_resume: !!st.can_resume,
          lease_owner: st.lease_owner,
          lease_until_epoch: st.lease_until_epoch,
          errors: st.errors || [],
          preserve_parts: !!st.preserve_parts,
          preserved_until_epoch: st.preserved_until_epoch || null,
        }));
      } catch (_e) { /* non-fatal */ }
    } catch (e) {
      setErr(
        (e && e.response && e.response.data && e.response.data.detail) ||
          String(e),
      );
    }
  };

  const abandon = async () => {
    if (!progress || !progress.job_id) return;
    try {
      await axios.post(
        `${API}/admin/photos-archive/byte-bounded/abandon/${progress.job_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProgress(null);
      setErr("");
    } catch (e) {
      setErr(
        (e && e.response && e.response.data && e.response.data.detail) ||
          String(e),
      );
    }
  };

  // Owner directive 2026-02 — preserve-parts abandon. Marks the job
  // `failed, preserve_parts=true` on the server, which:
  //   (1) leaves every already-uploaded GridFS part blob in place,
  //   (2) sets a bounded `preserved_until_epoch` (7 days from now),
  //   (3) keeps the parts downloadable via /part-N.zip with a
  //       `partial-` filename prefix, admin-auth gated.
  // The progress panel stays visible so the admin can click Download
  // on every row of `uploaded_parts_recoverable`.
  const abandonKeepParts = async () => {
    if (!progress || !progress.job_id) return;
    try {
      const r = await axios.post(
        `${API}/admin/photos-archive/byte-bounded/abandon/${progress.job_id}`,
        { preserve_parts: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Refresh the status snapshot so the UI immediately reflects
      // the preserved-failed state and the recoverable list.
      try {
        const s = await axios.get(
          `${API}/admin/photos-archive/byte-bounded/status/${progress.job_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const st = s.data;
        setProgress((p) => ({
          ...(p || {}),
          job_id: progress.job_id,
          status: st.status,
          phase: st.phase || "abandoned_by_admin_keep_parts",
          preserve_parts: !!st.preserve_parts,
          preserved_until_epoch:
            st.preserved_until_epoch ||
            (r.data && r.data.preserved_until_epoch) ||
            null,
          uploaded_parts_recoverable:
            st.uploaded_parts_recoverable || [],
        }));
      } catch (_e) { /* non-fatal — poll will catch up */ }
      setErr("");
    } catch (e) {
      setErr(
        (e && e.response && e.response.data && e.response.data.detail) ||
          String(e),
      );
    }
  };

  // Owner directive 2026-02 — recover a preserved job by id. Lets
  // the admin reopen the download panel for any job that has been
  // explicitly preserved (within its 7-day retention window) across
  // browser sessions.
  const recoverJob = async () => {
    const id = (recoverJobId || "").trim();
    if (!id) return;
    setErr("");
    try {
      const s = await axios.get(
        `${API}/admin/photos-archive/byte-bounded/status/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const st = s.data;
      setProgress({
        job_id: id,
        status: st.status,
        phase: st.phase || "unknown",
        total_records: st.total_records || null,
        parts_uploaded: st.parts_uploaded || 0,
        parts_total: st.parts_total || null,
        heartbeat_age_sec: st.heartbeat_age_sec || 0,
        started_at: Date.now(),
        stall_reason: st.stall_reason || null,
        uploaded_parts_recoverable:
          st.uploaded_parts_recoverable || null,
        preserve_parts: !!st.preserve_parts,
        preserved_until_epoch: st.preserved_until_epoch || null,
        remaining_parts: st.remaining_parts || null,
        can_resume: !!st.can_resume,
        lease_owner: st.lease_owner || null,
        lease_until_epoch: st.lease_until_epoch || null,
        errors: st.errors || [],
      });
      if (st.status === "ready" && st.plan) setPlan(st.plan);
    } catch (e) {
      setErr(
        (e && e.response && e.response.data && e.response.data.detail) ||
          String(e),
      );
    }
  };

  const downloadPart = async (part) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}${part.download_url}`;
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = part.filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        a.remove();
      }, 2000);
    } catch (e) {
      setErr(`Download failed for ${part.filename}: ${e}`);
    }
  };

  return (
    <div
      className="mt-6 border border-amber-400/40 bg-amber-500/[0.03] p-4"
      data-testid="photos-byte-bounded-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-amber-400" />
        <div className="text-sm uppercase tracking-widest text-amber-200">
          Byte-Size-Bounded Photo Archive · Multi-Part
        </div>
        <span className="text-[10px] px-2 py-0.5 bg-amber-500/70 text-black font-bold uppercase tracking-wider">
          Preview · Pending Review
        </span>
      </div>
      <div className="text-xs text-amber-200/80 mb-3 leading-relaxed">
        Builds multiple ZIPs; each ZIP's final on-disk size (incl. manifest
        and ZIP overhead) is ≤ the cap. Original bytes are preserved
        (ZIP_STORED, no transcode). May 2026 past-participants and Winter
        2026 approved vehicles live in separate top-level folders inside
        every part. 90 MiB is a configurable default; adjust for your
        upload target (ChatGPT doc hard cap is 512 MB; Emergent native
        chat cap is not publicly documented).
      </div>
      <div className="flex items-end gap-3 flex-wrap mb-3">
        <label className="text-xs text-white/80">
          <div className="uppercase tracking-widest text-[10px] text-white/50 mb-1">
            Edition
          </div>
          <select
            value={edition}
            onChange={(e) => setEdition(e.target.value)}
            data-testid="bb-edition"
            className="bg-black/40 border border-white/20 px-2 py-1 text-sm"
          >
            <option value="both">Both (May + Winter)</option>
            <option value="may">May 2026 (past)</option>
            <option value="winter">Winter 2026 (approved)</option>
          </select>
        </label>
        <label className="text-xs text-white/80">
          <div className="uppercase tracking-widest text-[10px] text-white/50 mb-1">
            Max MiB per part
          </div>
          <input
            type="number"
            step={1}
            min={1}
            value={capMiB}
            onChange={(e) => setCapMiB(Number(e.target.value) || 0)}
            data-testid="bb-cap-mib"
            className="bg-black/40 border border-white/20 px-2 py-1 w-24 text-sm"
          />
        </label>
        <label className="text-xs text-white/80">
          <div className="uppercase tracking-widest text-[10px] text-white/50 mb-1">
            Reserve KiB
          </div>
          <input
            type="number"
            step={8}
            min={0}
            value={reserveKiB}
            onChange={(e) => setReserveKiB(Number(e.target.value) || 0)}
            data-testid="bb-reserve-kib"
            className="bg-black/40 border border-white/20 px-2 py-1 w-24 text-sm"
          />
        </label>
        <button
          onClick={build}
          disabled={busy}
          data-testid="bb-build"
          className="text-sm px-3 py-2 border border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-black transition inline-flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {busy
            ? progress && progress.status
              ? `Preparing (${progress.status})…`
              : "Starting…"
            : "Build parts"}
        </button>
      </div>
      {/* Owner directive 2026-02 — recover a preserved job across
          browser sessions. Pastes any job id (e.g. one that was
          abandoned with preserve_parts=true in a previous session)
          and reopens its status + recoverable parts list. Does NOT
          interrupt an in-flight build. */}
      <div className="flex items-end gap-2 flex-wrap mb-3">
        <label className="text-xs text-white/80">
          <div className="uppercase tracking-widest text-[10px] text-white/50 mb-1">
            Recover preserved job by ID
          </div>
          <input
            type="text"
            value={recoverJobId}
            onChange={(e) => setRecoverJobId(e.target.value)}
            placeholder="paste job uuid"
            data-testid="bb-recover-id"
            className="bg-black/40 border border-white/20 px-2 py-1 text-xs font-mono w-96 max-w-full"
          />
        </label>
        <button
          onClick={recoverJob}
          data-testid="bb-recover-load"
          className="text-xs px-3 py-2 border border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-black transition"
          title="Load the status and uploaded-parts list for an existing job. Only preserved failed jobs survive beyond 30 min; ordinary jobs older than that are reaped."
        >
          Load
        </button>
      </div>
      {progress && !plan && (
        <div
          data-testid="bb-progress"
          className="text-xs text-amber-200/80 bg-amber-500/[0.04] border border-amber-500/30 px-2 py-1 mb-3 flex items-start justify-between gap-3"
        >
          <div>
            Job <span className="font-mono">{progress.job_id}</span> · status{" "}
            <span className="uppercase tracking-widest font-bold text-amber-200">
              {progress.status}
            </span>{" "}
            · phase <span className="font-semibold">{progress.phase || "-"}</span>
            {progress.parts_total ? (
              <>
                {" "}· parts {progress.parts_uploaded || 0}/{progress.parts_total}
              </>
            ) : null}{" "}
            · records {progress.total_records} · elapsed{" "}
            {Math.max(0, Math.round((Date.now() - progress.started_at) / 1000))}s
            {typeof progress.heartbeat_age_sec === "number" && progress.heartbeat_age_sec > 0 ? (
              <>
                {" "}· worker heartbeat {progress.heartbeat_age_sec}s ago
              </>
            ) : null}
          </div>
          {(progress.status === "stalled" || progress.status === "running") && (
            <div className="shrink-0 flex items-center gap-2 flex-wrap">
              {progress.can_resume && (
                <button
                  onClick={resumeRemaining}
                  data-testid="bb-resume"
                  className="text-xs px-2 py-1 border border-green-400 text-green-200 hover:bg-green-500 hover:text-black transition"
                  title="Upload only the parts missing from GridFS. Idempotent, uses a short lease; safe to click multiple times."
                >
                  Resume remaining parts
                  {progress.remaining_parts
                    ? ` (${progress.remaining_parts.length})`
                    : ""}
                </button>
              )}
              <button
                onClick={abandonKeepParts}
                data-testid="bb-abandon-keep-parts"
                className="text-xs px-2 py-1 border border-amber-400 text-amber-200 hover:bg-amber-400 hover:text-black transition"
                title="Mark the job failed but KEEP every already-uploaded part in GridFS. Preserved parts stay downloadable for 7 days."
              >
                Abandon &amp; keep uploaded parts
              </button>
              <button
                onClick={abandon}
                data-testid="bb-abandon"
                className="text-xs px-2 py-1 border border-red-400 text-red-300 hover:bg-red-500 hover:text-white transition"
                title="DESTRUCTIVE — mark the job failed and DELETE every uploaded part from GridFS. Use only if you do NOT want to recover the partial work."
              >
                Abandon (delete parts)
              </button>
            </div>
          )}
        </div>
      )}
      {progress && progress.preserve_parts && progress.preserved_until_epoch && (
        <div
          data-testid="bb-preserved-banner"
          className="text-[11px] text-amber-100 bg-amber-500/10 border border-amber-400/50 px-2 py-1 mb-3 flex items-start justify-between gap-3 flex-wrap"
        >
          <div>
            <span className="font-bold uppercase tracking-widest">
              Preserved · partial recovery
            </span>{" "}
            — Already-uploaded parts for job{" "}
            <span className="font-mono">{progress.job_id}</span> are retained
            and downloadable until{" "}
            <span className="font-mono">
              {new Date(progress.preserved_until_epoch * 1000).toISOString()}
            </span>
            .
          </div>
          {progress.can_resume && (
            <button
              onClick={resumeRemaining}
              data-testid="bb-resume-preserved"
              className="shrink-0 text-xs px-2 py-1 border border-green-400 text-green-200 hover:bg-green-500 hover:text-black transition"
              title="Finish uploading the parts that are still missing. Idempotent."
            >
              Resume remaining parts
              {progress.remaining_parts
                ? ` (${progress.remaining_parts.length})`
                : ""}
            </button>
          )}
        </div>
      )}
      {progress && Array.isArray(progress.errors) && progress.errors.length > 0 && (
        <div
          data-testid="bb-errors"
          className="text-[11px] text-red-200 bg-red-500/10 border border-red-400/40 px-2 py-1 mb-3"
        >
          <div className="uppercase tracking-widest text-[10px] text-red-300 mb-1">
            Recent recorded errors ({progress.errors.length})
          </div>
          <ul className="space-y-0.5">
            {progress.errors.map((e, i) => (
              <li key={i} className="font-mono break-all">
                [{e.phase}] {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {progress &&
        !plan &&
        Array.isArray(progress.uploaded_parts_recoverable) &&
        progress.uploaded_parts_recoverable.length > 0 && (
          <div data-testid="bb-partial-table" className="mb-3">
            <div className="text-[11px] uppercase tracking-widest text-amber-200/80 mb-1">
              Partial — {progress.uploaded_parts_recoverable.length}
              {progress.parts_total
                ? ` of ${progress.parts_total}`
                : ""}{" "}
              parts already in GridFS (filenames prefixed{" "}
              <span className="font-mono">partial-</span>)
            </div>
            <table className="w-full text-xs text-white/85 border-collapse">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-white/50">
                  <th className="text-left py-1 pr-2">#</th>
                  <th className="text-left py-1 pr-2">File</th>
                  <th className="text-right py-1 pr-2">Size (MiB)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {progress.uploaded_parts_recoverable.map((p) => (
                  <tr key={p.idx} className="border-t border-white/10">
                    <td className="py-1 pr-2 font-mono">{p.idx}</td>
                    <td className="py-1 pr-2 font-mono break-all">
                      partial-{p.filename}
                    </td>
                    <td className="py-1 pr-2 text-right">
                      {(p.size_bytes / 1048576).toFixed(2)}
                    </td>
                    <td className="py-1">
                      <button
                        onClick={() => downloadPart({
                          ...p,
                          filename: `partial-${p.filename}`,
                        })}
                        data-testid={`bb-partial-part-${p.idx}`}
                        className="px-2 py-0.5 border border-white/30 hover:border-amber-300 hover:text-amber-300"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {err && (
        <div
          data-testid="bb-error"
          className="text-xs text-red-300 bg-red-500/10 border border-red-500/40 px-2 py-1 mb-3"
        >
          {err}
        </div>
      )}
      {plan && (
        <div data-testid="bb-plan">
          <div className="text-xs text-white/60 mb-2">
            Job <span className="font-mono">{plan.job_id}</span> · cap{" "}
            {(plan.cap_bytes / 1048576).toFixed(1)} MiB · reserve{" "}
            {(plan.min_free_overhead_bytes / 1024).toFixed(0)} KiB · parts{" "}
            {plan.parts.length} · oversize originals{" "}
            {plan.oversize_originals.length} · split vehicles{" "}
            {plan.split_vehicles.length}
          </div>
          <table className="w-full text-xs text-white/85 border-collapse mb-3">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-white/50">
                <th className="text-left py-1 pr-2">#</th>
                <th className="text-left py-1 pr-2">File</th>
                <th className="text-right py-1 pr-2">Size (MiB)</th>
                <th className="text-right py-1 pr-2">Photos</th>
                <th className="text-left py-1 pr-2">Edns</th>
                <th className="text-left py-1 pr-2">OK</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plan.parts.map((p) => (
                <tr key={p.idx} className="border-t border-white/10">
                  <td className="py-1 pr-2 font-mono">{p.idx}</td>
                  <td className="py-1 pr-2 font-mono break-all">{p.filename}</td>
                  <td className="py-1 pr-2 text-right">
                    {(p.size_bytes / 1048576).toFixed(2)}
                  </td>
                  <td className="py-1 pr-2 text-right">{p.entries_in_zip}</td>
                  <td className="py-1 pr-2">{p.editions.join(", ")}</td>
                  <td className="py-1 pr-2">
                    {p.testzip_passed && p.within_cap ? (
                      <span className="text-emerald-300">✓</span>
                    ) : (
                      <span className="text-red-400">✗</span>
                    )}
                  </td>
                  <td className="py-1">
                    <button
                      onClick={() => downloadPart(p)}
                      data-testid={`bb-part-${p.idx}`}
                      className="px-2 py-0.5 border border-white/30 hover:border-amber-300 hover:text-amber-300"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {plan.oversize_originals.length > 0 && (
            <div
              data-testid="bb-oversize-list"
              className="text-[11px] bg-red-500/10 border border-red-500/40 p-2 mb-2"
            >
              <div className="font-bold text-red-200 mb-1">
                Oversize originals — not placed in any part
              </div>
              {plan.oversize_originals.map((o, i) => (
                <div key={i} className="font-mono text-red-100 leading-snug">
                  {o.anon_folder} · {o.edition} · angle {o.angle_index} ·{" "}
                  {o.size_bytes} B — {o.actionable_note}
                </div>
              ))}
            </div>
          )}
          {plan.split_vehicles.length > 0 && (
            <div className="text-[11px] text-white/70">
              Vehicles split across parts (anon_folder):{" "}
              {plan.split_vehicles.map((s) => (
                <span key={s.anon_folder} className="font-mono mr-2">
                  {s.anon_folder}→[{s.parts.join(",")}]
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}




function ReviewerAccessPanel({ reviewers, onExtend, onRevoke, onRefresh }) {
  // Humanize seconds_remaining into "23h 14m" / "Expired"
  const formatRemaining = (secs, state) => {
    if (state === "revoked") return "Revoked";
    if (state === "expired") return "Expired";
    if (state === "not_activated") return "Not yet logged in";
    if (secs == null) return "—";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m remaining` : `${m}m remaining`;
  };

  const stateColor = (state) => {
    if (state === "active") return "#22C55E";
    if (state === "expired") return "#EF4444";
    if (state === "revoked") return "#EF4444";
    if (state === "not_activated") return "#F5C518";
    return "#888";
  };

  const needsAttention = reviewers.some((r) => r.access_state === "expired");

  return (
    <div
      data-testid="reviewer-access-panel"
      className={`border-2 p-4 sm:p-5 mb-6 ${needsAttention ? "border-red-500 bg-red-500/5" : "border-white/10 bg-white/[0.02]"}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="label-kicker" style={{ color: needsAttention ? "#EF4444" : "#F5C518" }}>
            Reviewer Access {needsAttention && "· Action needed"}
          </div>
          <div className="font-heading text-white text-sm sm:text-base mt-1">
            {reviewers.length} reviewer{reviewers.length === 1 ? "" : "s"} · 24-hour access windows
          </div>
        </div>
        <button
          onClick={onRefresh}
          data-testid="refresh-reviewer-access-btn"
          className="text-xs px-3 py-1.5 border border-white/20 text-white/70 font-heading tracking-wider uppercase hover:border-[#F5C518] hover:text-[#F5C518] transition"
        >
          Refresh
        </button>
      </div>

      <div className="grid gap-2">
        {reviewers.map((r) => (
          <div
            key={r.email}
            className="flex flex-wrap items-center justify-between gap-3 p-3 border border-white/10 bg-black/30"
          >
            <div className="min-w-0 flex-1">
              <div className="font-heading font-semibold text-white text-sm">{r.name || r.email}</div>
              <div className="text-xs text-white/50 truncate">{r.email}</div>
            </div>
            <div
              className="text-[11px] px-2.5 py-1 border font-heading font-bold tracking-wider uppercase"
              style={{
                color: stateColor(r.access_state),
                borderColor: stateColor(r.access_state) + "66",
                background: stateColor(r.access_state) + "14",
              }}
            >
              {formatRemaining(r.seconds_remaining, r.access_state)}
            </div>
            <div className="flex gap-2 flex-wrap">
              {(r.access_state === "expired" || r.access_state === "revoked" || r.access_state === "active") && (
                <button
                  onClick={() => onExtend(r.email, 24)}
                  data-testid={`extend-reviewer-${r.email}`}
                  className="text-xs px-3 py-1.5 border border-[#F5C518] text-[#F5C518] font-heading tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition"
                  title={r.access_state === "active" ? "Add another 24 hours on top of the current expiry" : "Grant a fresh 24 hours of access"}
                >
                  {r.access_state === "active" ? "+24h" : "Extend 24h"}
                </button>
              )}
              {r.access_state === "active" && (
                <button
                  onClick={() => onRevoke(r.email)}
                  data-testid={`revoke-reviewer-${r.email}`}
                  className="text-xs px-3 py-1.5 border border-red-500/40 text-red-400 font-heading tracking-wider uppercase hover:bg-red-500 hover:text-white transition"
                >
                  Revoke
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ReviewerActivityPanel({ activity, open, onToggle, filter, onFilterChange, onJump, onRefresh }) {
  const { total = 0, by_rec_status = {}, items = [], clicks = [], click_total = 0, click_by_label = {} } = activity || {};
  const pendingCount = by_rec_status.pending || 0;
  const acceptedCount = by_rec_status.accepted || 0;
  const dismissedCount = by_rec_status.dismissed || 0;
  const supersededCount = by_rec_status.superseded || 0;
  const approveCount = items.filter((i) => i.decision === "approved").length;
  const rejectCount = items.filter((i) => i.decision === "rejected").length;

  const filtered = items.filter((i) => {
    if (filter === "all") return true;
    if (filter === "approved") return i.decision === "approved";
    if (filter === "rejected") return i.decision === "rejected";
    return i.rec_status === filter;
  });

  const recStatusBadge = (s) => {
    const map = {
      pending:    { label: "Pending",    color: "#F5C518" },
      accepted:   { label: "Accepted",   color: "#22C55E" },
      dismissed:  { label: "Dismissed",  color: "#94A3B8" },
      superseded: { label: "Superseded", color: "#64748B" },
    };
    const m = map[s] || { label: s, color: "#888" };
    return (
      <span
        className="text-[9px] px-1.5 py-0.5 border font-heading font-bold tracking-wider uppercase"
        style={{ color: m.color, borderColor: m.color + "66", background: m.color + "14" }}
      >
        {m.label}
      </span>
    );
  };

  return (
    <div
      data-testid="reviewer-activity-panel"
      className="border-2 border-white/10 bg-white/[0.02] mb-6"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-white/[0.03] transition"
        data-testid="reviewer-activity-toggle"
      >
        <div className="min-w-0 flex-1">
          <div className="label-kicker" style={{ color: "#F5C518" }}>
            Reviewer Activity · Private to you
          </div>
          <div className="font-heading text-white text-sm sm:text-base mt-1">
            {total} total decision{total === 1 ? "" : "s"} logged ·{" "}
            <span className="text-emerald-400">{approveCount} approved</span> ·{" "}
            <span className="text-red-400">{rejectCount} rejected</span>
          </div>
          <div className="text-xs text-white/50 mt-1">
            Pending: {pendingCount} · Accepted: {acceptedCount} · Dismissed: {dismissedCount}
            {supersededCount > 0 && ` · Superseded: ${supersededCount}`}
            {click_total > 0 && (
              <>
                {" · "}
                <span className="text-[#F5C518]">Restricted clicks: {click_total}</span>
              </>
            )}
          </div>
        </div>
        <span className="text-[10px] px-2 py-1 border border-white/20 text-white/60 tracking-wider uppercase">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div className="border-t border-white/10 p-4 sm:p-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { id: "all", label: "All", n: total },
              { id: "approved", label: "Approved", n: approveCount },
              { id: "rejected", label: "Rejected", n: rejectCount },
              { id: "pending", label: "Pending", n: pendingCount },
              { id: "accepted", label: "Accepted", n: acceptedCount },
              { id: "dismissed", label: "Dismissed", n: dismissedCount },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => onFilterChange(t.id)}
                data-testid={`activity-filter-${t.id}`}
                className={`text-[11px] px-2.5 py-1 border font-heading tracking-wider uppercase transition ${
                  filter === t.id
                    ? "bg-[#F5C518] text-black border-[#F5C518]"
                    : "text-white/70 border-white/20 hover:border-[#F5C518] hover:text-[#F5C518]"
                }`}
              >
                {t.label} ({t.n})
              </button>
            ))}
            <button
              onClick={onRefresh}
              className="ml-auto text-[11px] px-2.5 py-1 border border-white/20 text-white/60 font-heading tracking-wider uppercase hover:border-white/40 hover:text-white transition"
            >
              Refresh
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-8 text-white/40 font-body text-sm">No activity in this filter</div>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {filtered.map((i) => (
                <div
                  key={i.id}
                  className="border border-white/10 bg-black/30 p-3 flex flex-wrap items-start gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-heading font-semibold text-white text-sm truncate">
                        {i.applicant_name || "—"}
                      </span>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 border tracking-wider uppercase"
                        style={{
                          color: i.decision === "approved" ? "#22C55E" : "#EF4444",
                          borderColor: (i.decision === "approved" ? "#22C55E" : "#EF4444") + "66",
                          background: (i.decision === "approved" ? "#22C55E" : "#EF4444") + "14",
                        }}
                      >
                        {i.decision === "approved" ? "✓" : "✗"} {i.decision}
                      </span>
                      {recStatusBadge(i.rec_status)}
                      {i.real_status && (
                        <span className="text-[9px] text-white/50 tracking-wider uppercase">
                          real: {i.real_status}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/50 space-y-0.5">
                      {i.applicant_email && <div className="truncate">{i.applicant_email}</div>}
                      {i.vehicle && <div className="truncate">{i.vehicle}{i.plate ? ` · ${i.plate}` : ""}</div>}
                      {i.note && <div className="text-white/60 italic mt-1">"{i.note}"</div>}
                      <div className="text-[10px] text-white/40 mt-1">
                        By {i.reviewer_name} · {(i.created_at || "").replace("T", " ").slice(0, 16)}
                        {i.acted_at && ` · acted ${(i.acted_at || "").replace("T"," ").slice(0,16)}`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onJump(i.application_id)}
                    data-testid={`activity-jump-${i.id}`}
                    className="text-[11px] px-2.5 py-1 border border-[#F5C518]/40 text-[#F5C518] font-heading tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition self-start flex-shrink-0"
                  >
                    Open
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Silently logged read-only clicks. Reviewer never sees this section because
              the panel is admin-only — but every CSV/Re-verify/Force-Send button he
              tries is captured here with the timestamp. */}
          {click_total > 0 && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <div className="label-kicker" style={{ color: "#F5C518" }}>
                    Restricted clicks · silently logged
                  </div>
                  <div className="text-[11px] text-white/50 mt-1">
                    Buttons / links the reviewer tried to use. Most-recent {clicks.length} of {click_total}.
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(click_by_label).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([lbl, n]) => (
                    <span
                      key={lbl}
                      className="text-[10px] px-2 py-0.5 border border-[#F5C518]/30 bg-[#F5C518]/5 text-[#F5C518] font-heading tracking-wider uppercase"
                    >
                      {lbl} · {n}
                    </span>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="restricted-clicks-list">
                {clicks.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-3 px-3 py-2 border border-white/10 bg-black/30 text-[11px] font-body"
                  >
                    <span className="text-[#F5C518] font-heading font-bold tracking-wider uppercase text-[10px] truncate min-w-0 flex-1">
                      {c.label}
                    </span>
                    {c.meta && c.meta.email && (
                      <span className="text-white/50 truncate hidden sm:inline">{c.meta.email}</span>
                    )}
                    <span className="text-white/40 text-[10px] flex-shrink-0">
                      {(c.created_at || "").replace("T", " ").slice(0, 16)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function AppRow({ app, onSelect, pendingRec, isReviewer }) {
  const statusColors = {
    pending: "text-[#F5C518] border-[#F5C518]/30 bg-[#F5C518]/5",
    approved: "text-green-400 border-green-400/30 bg-green-400/5",
    rejected: "text-red-400 border-red-400/30 bg-red-400/5",
  };

  // For the reviewer only: overlay their pending recommendation as the apparent status.
  // From Abdulmohsen's perspective, applicants he's recommended "approved" look approved;
  // applicants he's recommended "rejected" look rejected. The main admin sees the real status.
  const displayStatus = (isReviewer && pendingRec)
    ? pendingRec.decision
    : app.status;

  const fileUrl = `${API}/files/${app.photo_primary}`;

  return (
    <motion.button
      layout
      whileHover={{ x: 2 }}
      onClick={onSelect}
      data-testid={`app-row-${app.id}`}
      className={
        "flex items-center gap-4 p-4 border bg-white/[0.02] hover:border-[#F5C518]/40 transition-all text-left " +
        (app.flag_needs_review
          ? "border-red-500/60 bg-red-500/[0.06]"
          : "border-white/10")
      }
    >
      <div className="w-20 h-20 flex-shrink-0" data-testid={`app-thumb-${app.id}`}>
        <Photo
          path={app.photo_primary}
          label="primary"
          aspect="aspect-square"
          lazy={true}
          w={200}
          showOpenOriginal={false}
          dataTestId={`app-thumb-inner-${app.id}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        {app.flag_needs_review && (
          <div
            data-testid={`review-flag-${app.id}`}
            className="mb-2 inline-flex items-center gap-2 px-2 py-1 border border-red-500/60 bg-red-500/10 text-red-300 text-[10px] font-bold tracking-wider uppercase"
            title={app.flag_reason || "Row could not be fully validated — please review"}
          >
            <AlertTriangle className="w-3 h-3" />
            REVIEW REQUIRED · ID {String(app.id).slice(0, 8)}
          </div>
        )}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-heading font-semibold text-white text-base sm:text-lg truncate">
            {app.full_name || (app.flag_needs_review ? "— review required —" : "")}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 border tracking-wider uppercase ${statusColors[displayStatus]}`}>
            {displayStatus}
          </span>
          {!isReviewer && app.participation_day && PARTICIPATION_DAY_MAP[app.participation_day] && (
            <span
              data-testid={`day-badge-${app.id}`}
              className="text-[10px] font-semibold px-2 py-0.5 border tracking-wider uppercase text-[#F5C518] border-[#F5C518]/40 bg-[#F5C518]/5"
              title={PARTICIPATION_DAY_FULL_LABELS[app.participation_day] || PARTICIPATION_DAY_MAP[app.participation_day].label}
            >
              {app.participation_day.toUpperCase()}
            </span>
          )}
          {/* Admin-only: highlight applicants with a pending reviewer recommendation
              so they're visually distinct in the list. Not shown to the reviewer since
              the row's own badge already reflects their "approved" / "rejected" illusion. */}
          {!isReviewer && pendingRec && (
            <span
              data-testid={`reviewer-rec-badge-${app.id}`}
              className={`text-[10px] font-semibold px-2 py-0.5 border tracking-wider uppercase inline-flex items-center gap-1 ${
                pendingRec.decision === "approved"
                  ? "text-emerald-400 border-emerald-400/40 bg-emerald-400/10"
                  : "text-red-400 border-red-400/40 bg-red-400/10"
              }`}
              title={`${pendingRec.reviewer_name} recommends ${pendingRec.decision.toUpperCase()} — click to Accept or Dismiss`}
            >
              {pendingRec.decision === "approved" ? "✓" : "✗"} Reviewer: {pendingRec.decision}
            </span>
          )}
        </div>
        <div className="font-body text-sm text-white/60 truncate">
          {app.vehicle_year} {app.vehicle_make} {app.vehicle_model} · {app.vehicle_color}
        </div>
        <div
          className="font-body text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
          data-testid={`app-origin-${app.id}`}
        >
          <span className="text-white/40">
            <span className="text-white/30 uppercase tracking-wider text-[10px] mr-1">City:</span>
            {(app.origin_city || "").toString().trim() || "Not provided"}
          </span>
          <span className="text-white/40">
            <span className="text-white/30 uppercase tracking-wider text-[10px] mr-1">Country:</span>
            {(app.origin_country || "").toString().trim() || "Not provided"}
          </span>
          {(() => {
            const { cls, reason } = classifyOrigin(app.origin_country, app.origin_city);
            if (cls === "outside_riyadh") {
              return (
                <span
                  data-testid={`origin-eligibility-${app.id}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-400/60 bg-emerald-400/10 text-emerald-300 font-heading font-bold uppercase text-[10px] tracking-wider"
                  title="Applicant is from outside Riyadh — eligible for Full Event Outdoor Display Pass (Dec 10–12, 2026). This is informational; the actual assignment is set by the admin."
                >
                  Outside Riyadh · Outdoor Pass eligible
                </span>
              );
            }
            if (cls === "review") {
              // Location needs review. Sep 12, 2026: never surfaces the
              // green outside-eligible badge for review states — the
              // admin decides after correcting the origin fields.
              const reasonLabel = ({
                country_field_holds_city: "Country field holds a city name",
                country_unrecognised: "Country not recognised",
                country_missing: "Country missing",
                saudi_no_city: "City missing",
                foreign_country_no_city: "City missing",
                city_country_conflict: "City doesn't match the country",
                city_placeholder: "City is a placeholder value",
                fallthrough: "Location partial",
              })[reason] || "Location partial";
              return (
                <span
                  data-testid={`origin-review-${app.id}`}
                  data-reason={reason}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-400/60 bg-amber-400/10 text-amber-300 font-heading font-bold uppercase text-[10px] tracking-wider"
                  title={`Location needs review — ${reasonLabel}. The applicant is NOT auto-eligible for the outdoor pass; open the record and confirm the origin.`}
                >
                  Location needs review · {reasonLabel}
                </span>
              );
            }
            if (cls === "unknown") {
              return (
                <span
                  data-testid={`origin-unknown-${app.id}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-white/30 bg-white/[0.04] text-white/60 font-heading font-bold uppercase text-[10px] tracking-wider"
                  title="Origin fields empty. Open the applicant to confirm."
                >
                  Location not provided
                </span>
              );
            }
            return null;
          })()}
        </div>
        <div className="font-body text-xs text-white/30 mt-1 flex items-center gap-3 flex-wrap">
          {app.instagram && <span>@{app.instagram.replace(/^@/, "")}</span>}
          {app.group && <><span>·</span><span className="text-[#F5C518]/70">{displayGroup(app.group)}</span></>}
          <span>·</span>
          <span>{(() => {
            const ts = app.created_at;
            if (!ts || typeof ts !== "string") return "date unknown";
            const d = new Date(ts);
            return isNaN(d.getTime()) ? "date unknown" : d.toLocaleDateString();
          })()}</span>
          {(() => {
            const missing = [];
            if (!(app.email || "").toString().trim()) missing.push("email");
            if (!(app.mobile || "").toString().trim()) missing.push("mobile");
            if (!(app.origin_country || "").toString().trim()) missing.push("country");
            if (!(app.origin_city || "").toString().trim()) missing.push("city");
            if (!(app.created_at || "").toString().trim()) missing.push("timestamp");
            if (!(app.participation_day || "").toString().trim() && app.status === "approved") missing.push("day");
            if (missing.length === 0) return null;
            return (
              <>
                <span>·</span>
                <span
                  data-testid={`missing-info-chip-${app.id}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-400/50 bg-amber-400/10 text-amber-300 font-heading font-bold uppercase text-[10px] tracking-wider"
                  title={`Missing: ${missing.join(", ")}. Row is still visible and editable — this is a completeness indicator, not a corruption error.`}
                >
                  Missing: {missing.join(" · ")}
                </span>
              </>
            );
          })()}
        </div>
      </div>
    </motion.button>
  );
}

function DetailModal({ app, onClose, onUpdate, updating, composeApproval, composeRejection, onEdited, authHeader, role = "admin", onRecommend, pendingRec, onAcceptRec, onDismissRec, readOnlyArchive = false }) {
  const isReviewerRole = role === "reviewer";
  // May 2026 read-only archive: reuse the `isReviewer` read-only semantics
  // to hide every ADMIN mutation control (approve/reject/resend/edits) via
  // the existing `{!isReviewer && ...}` guards. Reviewer-recommendation
  // buttons are additionally gated on `isReviewerRole && !readOnlyArchive`
  // so an archived row cannot accumulate new recommendations either.
  // Backend guards remain the authoritative refusal layer.
  const isReviewer = isReviewerRole || readOnlyArchive;
  const isReviewerOrReadOnly = isReviewer;
  const primary = `${API}/files/${app.photo_primary}`;
  const secondary = app.photo_secondary ? `${API}/files/${app.photo_secondary}` : null;
  const [resending, setResending] = useState(false);
  // Set of field keys currently in an open (unsaved) inline editor. Populated
  // by every `EditableField` via its `onEditingChange` callback. Non-empty →
  // the "Send Updated Approval" button is disabled and shows an "Unsaved
  // edits" hint, so we never send an email built from a stale in-flight edit.
  const [openEditors, setOpenEditors] = useState(() => new Set());
  const handleEditingChange = useCallback((fieldKey, editing) => {
    setOpenEditors((prev) => {
      const next = new Set(prev);
      if (editing) next.add(fieldKey);
      else next.delete(fieldKey);
      return next;
    });
  }, []);
  const hasUnsavedEdits = openEditors.size > 0;

  const CREW_OPTIONS = ["CrewZNatioN", "Blur Kit", "A9X Club", "The Fahrers", "Super Fuel", "Riyadh United Bikers", "RJC"];

  // Single PATCH helper used by every per-field inline editor.
  const saveField = async (fieldKey, newValue) => {
    const body = { [fieldKey]: newValue };
    const res = await axios.patch(`${API}/admin/applications/${app.id}`, body, authHeader);
    onEdited?.(res.data.applicant);
    return res.data.applicant;
  };

  const resendEntryPass = async () => {
    if (resending) return; // idempotent guard against double-clicks
    if (hasUnsavedEdits) {
      toast.error("Save or cancel your open edits before sending.");
      return;
    }
    // Read the day label from the LATEST saved app prop (never draft state).
    const dayKey = (app.participation_day || "").toLowerCase();
    const dayLabel = ({
      day1: "Day 1 · Thursday, Dec 10, 2026",
      day2: "Day 2 · Friday, Dec 11, 2026",
      day3: "Day 3 · Saturday, Dec 12, 2026",
      day1_day3: "Day 1 & Day 3 - Indian Motorcycles · Dec 10 & 12, 2026",
      indoor: "Three-Day Indoor Display · Dec 10–12, 2026",
      outdoor: "Full Event Outdoor Display Pass · Dec 10–12, 2026",
    })[dayKey] || "the current participation assignment";
    if (!window.confirm(
      `Send the updated Entry Pass to ${app.email}?\n\n` +
      `Assignment: ${dayLabel}\n\n` +
      `The email is built from the latest SAVED record. No status change; ` +
      `pass code #${(app.id || "").slice(0, 8).toUpperCase()} remains valid.`
    )) return;
    setResending(true);
    try {
      const res = await axios.post(`${API}/admin/applications/${app.id}/resend-entry-pass`, {}, authHeader);
      if (res.data && res.data.sent === true) {
        // Mail-provider ACCEPTED the send. Actual mailbox delivery is tracked
        // separately in the Entry Pass Delivery panel (Resend event log) —
        // do not conflate "accepted" with "delivered".
        toast.success(
          `Updated approval accepted by mail provider for ${res.data.email}. ` +
          `Delivery confirmation appears in the Entry Pass Delivery panel.`,
          { duration: 7000 }
        );
        onEdited?.(app);
      } else if (res.data && res.data.idempotent_skip === true) {
        // Version-scoped atomic idempotency (Sep 12, 2026): the exact same
        // pass version was already sent — no new outbound message. The
        // dashboard shows the earlier delivery so this is a HONEST no-op,
        // not a failure. Save a real change to bump the version and try again.
        toast.info(
          `Already sent for pass version ${res.data.corrected_pass_version ?? "?"} · ` +
          `${res.data.reason || "no new save since last send"}. ` +
          `Save a new change to send an updated pass.`,
          { duration: 8000 }
        );
      } else {
        const errMsg = (res.data && res.data.error) || "unknown error";
        toast.error(`Send failed (retry available): ${errMsg}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Send failed — please retry");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      // Sep 13, 2026 owner-demonstrated defect: with `flex items-center
      // justify-center` on a `overflow-y-auto` fixed wrapper, when the
      // modal content is taller than the viewport the flexbox vertically
      // centers the child — pushing the top of the content into NEGATIVE
      // offset relative to the scroll container. `scrollTop` cannot go
      // below 0, so the header / close button / first photo row are
      // unreachable in the scroll area. Fix by using block flow with
      // horizontal centering (`mx-auto`) and top-aligned padding, which
      // lets tall content scroll from top → bottom naturally while short
      // content still looks correctly framed inside the viewport.
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto px-4 sm:px-8"
      onClick={onClose}
      data-testid="detail-modal-wrapper"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/10 max-w-4xl w-full mx-auto my-4 sm:my-8"
        data-testid="detail-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="min-w-0">
            <div className="label-kicker mb-1">Application</div>
            <div className="font-heading font-semibold text-white text-xl truncate">{app.full_name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[#F5C518] transition-colors flex-shrink-0" data-testid="close-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Photos — all four intake positions (primary/secondary/left/right).
            Sep 12, 2026 owner directive: intermittent gallery gaps must
            surface as "loading" / "storage hiccup + Retry" / "unavailable",
            not silent broken-image icons. Each Photo eagerly loads in the
            detail modal (not lazy) and lets the reviewer open the full
            original in a new tab for approval decisions. */}
        <div className="grid grid-cols-2 gap-0" data-testid="detail-photo-gallery">
          <Photo path={app.photo_primary}   label="primary"   dataTestId="detail-photo-primary" />
          <Photo path={app.photo_secondary} label="secondary" dataTestId="detail-photo-secondary" />
          <Photo path={app.photo_left}      label="left"      dataTestId="detail-photo-left" />
          <Photo path={app.photo_right}     label="right"     dataTestId="detail-photo-right" />
        </div>

        {/* Details */}
        <div className="p-5 sm:p-7 space-y-6">
          {/* Pending reviewer recommendation banner — ADMIN ONLY */}
          {!isReviewer && pendingRec && (
            <div
              data-testid="pending-rec-banner"
              className="border-2 p-4 sm:p-5"
              style={{
                borderColor: pendingRec.decision === "approved" ? "#22C55E" : "#EF4444",
                background: pendingRec.decision === "approved" ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
              }}
            >
              <div className="flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0">
                  <div
                    className="label-kicker"
                    style={{ color: pendingRec.decision === "approved" ? "#22C55E" : "#EF4444" }}
                  >
                    Reviewer Recommendation · Pending
                  </div>
                  <div className="font-heading text-white text-sm sm:text-base mt-1">
                    <span className="font-bold">{pendingRec.reviewer_name}</span> recommends{" "}
                    <span className="font-bold uppercase">{pendingRec.decision}</span>
                  </div>
                  {pendingRec.note && (
                    <div className="text-xs text-white/70 italic mt-2 leading-relaxed">"{pendingRec.note}"</div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      if (window.confirm(
                        `ACCEPT this recommendation?\n\n${pendingRec.reviewer_name} recommends ${pendingRec.decision.toUpperCase()} for ${app.full_name}.\n\nThis will change the status AND send the ${pendingRec.decision} email to the applicant.`
                      )) onAcceptRec(pendingRec.id);
                    }}
                    disabled={updating}
                    data-testid="accept-rec-btn"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                    style={{ background: pendingRec.decision === "approved" ? "#22C55E" : "#EF4444", color: "#fff" }}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Accept
                  </button>
                  <button
                    onClick={() => onDismissRec(pendingRec.id)}
                    disabled={updating}
                    data-testid="dismiss-rec-btn"
                    className="btn-ghost inline-flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" /> Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          <Section title="Applicant">
            <EditableField
              icon={User}
              label="Full Name"
              value={app.full_name}
              fieldKey="full_name"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              testid="full-name"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={CalendarIcon}
              label="Age"
              value={app.age}
              fieldKey="age"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              type="number"
              validate={(v) => {
                const n = Number(v);
                if (Number.isNaN(n) || n < 0 || n > 120) return "Age must be 0–120";
                return null;
              }}
              parse={(v) => Number(v)}
              testid="age"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Mail}
              label="Email"
              value={app.email}
              fieldKey="email"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              type="email"
              full
              validate={(v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Invalid email"}
              extra={
                !isReviewer && app.status === "approved" && app.email ? (
                  <button
                    onClick={resendEntryPass}
                    disabled={resending}
                    data-testid="resend-pass-btn"
                    className="text-[10px] px-2 py-0.5 border border-white/20 text-white/70 tracking-wider uppercase hover:border-[#F5C518] hover:text-[#F5C518] transition disabled:opacity-50 inline-flex items-center gap-1"
                    title="Resend the Entry Pass to this address"
                  >
                    {resending ? <Loader2 className="w-3 h-3 animate-spin"
              onEditingChange={handleEditingChange}
            /> : <Send className="w-3 h-3" />}
                    Resend Pass
                  </button>
                ) : null
              }
              testid="email"
            />
            <EditableField
              icon={Phone}
              label="Mobile"
              value={app.mobile}
              fieldKey="mobile"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              type="tel"
              full
              testid="mobile"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Instagram}
              label="Instagram"
              value={app.instagram}
              displayValue={app.instagram ? `@${app.instagram.replace(/^@/, "")}` : "—"}
              fieldKey="instagram"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              placeholder="username (without @)"
              testid="instagram"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Flag}
              label="Club / Crew"
              value={app.group}
              displayValue={displayGroup(app.group)}
              fieldKey="group"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              type="select"
              options={CREW_OPTIONS}
              full
              testid="crew"
              hidden={isWinterByCreatedAt(app.created_at)}
              onEditingChange={handleEditingChange}
            />
          </Section>

          <Section title="Origin (Where they're coming from)">
            <EditableField
              icon={Globe}
              label="Country"
              value={app.origin_country}
              displayValue={(app.origin_country || "").toString().trim() || "Not provided"}
              fieldKey="origin_country"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              placeholder="Saudi Arabia"
              testid="origin-country"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={MapPin}
              label="City"
              value={app.origin_city}
              displayValue={(app.origin_city || "").toString().trim() || "Not provided"}
              fieldKey="origin_city"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              placeholder="Riyadh"
              testid="origin-city"
              onEditingChange={handleEditingChange}
            />
            {(() => {
              const { cls, reason } = classifyOrigin(app.origin_country, app.origin_city);
              if (cls === "outside_riyadh") {
                return (
                  <div
                    data-testid="detail-origin-eligibility"
                    className="sm:col-span-2 flex items-start gap-3 py-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-300/70 mb-0.5">
                        Outdoor Pass Eligibility
                      </div>
                      <div className="font-body text-sm text-emerald-300">
                        Applicant is from outside Riyadh → eligible for the{" "}
                        <span className="font-heading font-bold">Full Event Outdoor Display Pass</span>{" "}
                        (Dec 10–12, 2026). Informational only — set the actual assignment in
                        Participation Day below, then click{" "}
                        <span className="font-heading font-bold">Send Updated Approval</span>.
                      </div>
                    </div>
                  </div>
                );
              }
              if (cls === "review") {
                const humanReason = ({
                  country_field_holds_city:
                    "The country field currently holds a city name (e.g. Riyadh / الرياض). Correct the country field so eligibility can be classified.",
                  country_unrecognised:
                    "The country value is not on the recognised country list. Verify the country label before deciding the pass.",
                  country_missing:
                    "Only a city was provided — the country is missing. Fill it in before deciding the pass.",
                  saudi_no_city:
                    "Country is Saudi Arabia but no city was provided. Confirm the city so eligibility can be shown.",
                  foreign_country_no_city:
                    "A foreign country was provided but no city. Fill in the city before deciding the pass.",
                  city_country_conflict:
                    "The city and country don't match (e.g. Saudi Arabia + Dubai, or UAE + Riyadh). Correct one of them so eligibility can be classified.",
                  city_placeholder:
                    "The city value looks like a placeholder (Unknown / N/A / TBD / …). Ask the applicant for the real city before deciding the pass.",
                  fallthrough:
                    "Location is partial or ambiguous. Confirm the origin before deciding the pass.",
                })[reason] || "Location partial or ambiguous.";
                return (
                  <div
                    data-testid="detail-origin-review"
                    data-reason={reason}
                    className="sm:col-span-2 flex items-start gap-3 py-2"
                  >
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-0.5">
                        Location Needs Review — NOT auto-eligible
                      </div>
                      <div className="font-body text-sm text-amber-300/90">
                        {humanReason}
                      </div>
                      <div className="font-body text-xs text-white/50 mt-1">
                        The Full Event Outdoor Display Pass is NOT being suggested for
                        this record until the origin is confirmed. Nothing has been
                        auto-changed on the applicant.
                      </div>
                    </div>
                  </div>
                );
              }
              if (cls === "unknown") {
                return (
                  <div
                    data-testid="detail-origin-unknown"
                    className="sm:col-span-2 flex items-start gap-3 py-2"
                  >
                    <AlertTriangle className="w-4 h-4 text-white/50 mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-white/50 mb-0.5">
                        Location — Not provided
                      </div>
                      <div className="font-body text-sm text-white/70">
                        No origin was provided on this application. Ask the applicant
                        or edit the fields above before approving.
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </Section>

          <Section title="Vehicle">
            <EditableField
              icon={Car}
              label="Make"
              value={app.vehicle_make}
              fieldKey="vehicle_make"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              testid="vehicle-make"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Car}
              label="Model"
              value={app.vehicle_model}
              fieldKey="vehicle_model"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              testid="vehicle-model"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={CalendarIcon}
              label="Year"
              value={app.vehicle_year}
              fieldKey="vehicle_year"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              testid="vehicle-year"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Palette}
              label="Color"
              value={app.vehicle_color}
              fieldKey="vehicle_color"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              testid="vehicle-color"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Hash}
              label="Plate"
              value={app.plate_number}
              fieldKey="plate_number"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              testid="plate-number"
              onEditingChange={handleEditingChange}
            />
            <EditableField
              icon={Wrench}
              label="Modifications"
              value={app.modifications}
              displayValue={app.modifications || "—"}
              fieldKey="modifications"
              onSave={saveField}
              isReviewer={isReviewerOrReadOnly}
              multiline
              full
              testid="modifications"
              onEditingChange={handleEditingChange}
            />
          </Section>

          {/* Admin-only Participation Day assignment (hidden from applicants).
              Value is stored as key (day1|day2|day3|""), displayed as label + date. */}
          {!isReviewer && (
            <Section title="Participation Day (Admin-Only)">
              <EditableField
                icon={CalendarIcon}
                label="Assigned Day"
                value={PARTICIPATION_DAY_FULL_LABELS[app.participation_day] || ""}
                displayValue={
                  app.participation_day && PARTICIPATION_DAY_MAP[app.participation_day]
                    ? PARTICIPATION_DAY_FULL_LABELS[app.participation_day]
                    : "— Not assigned —"
                }
                fieldKey="participation_day"
                onSave={saveField}
                isReviewer={isReviewerOrReadOnly}
                type="select"
                options={PARTICIPATION_DAY_FULL_LABEL_LIST}
                parse={parseParticipationDayLabel}
                full
                testid="participation-day"
              onEditingChange={handleEditingChange}
            />
            </Section>
          )}

          <Section title="Submitted">
            <Field icon={Clock} label="Date" value={new Date(app.created_at).toLocaleString()} full />
          </Section>
        </div>

        {/* Actions */}
        <div className="p-5 sm:p-7 border-t border-white/10 bg-white/[0.02] flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="label-kicker mb-1">Current Status</div>
            <div className="font-heading font-semibold text-white capitalize text-lg">
              {(isReviewerRole && pendingRec) ? pendingRec.decision : app.status}
            </div>
          </div>

          {/* ADMIN actions (role=admin) */}
          {!isReviewer && app.status !== "approved" && (
            <button
              onClick={() => {
                if (!app.participation_day || !PARTICIPATION_DAY_MAP[app.participation_day]) {
                  toast.warning(
                    "Assign a participation day first (Day 1, Day 2, Day 3, Three-Day Indoor Display, or Full Event Outdoor Display Pass).",
                    { duration: 6000 }
                  );
                  return;
                }
                onUpdate(app.id, "approved");
              }}
              disabled={updating || !app.participation_day || !PARTICIPATION_DAY_MAP[app.participation_day]}
              title={
                !app.participation_day || !PARTICIPATION_DAY_MAP[app.participation_day]
                  ? "Assign a participation day before approving"
                  : "Approve and send bilingual approval email"
              }
              data-testid="approve-btn"
              className="btn-primary inline-flex items-center justify-center gap-2"
              style={{ background: "#22C55E", color: "#000" }}
            >
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
          )}
          {!isReviewer && app.status === "approved" && (
            <button
              onClick={() => {
                if (window.confirm(
                  `Reject ${app.full_name} and send the BILINGUAL APOLOGY email (EN/AR) explaining the auto-approval glitch?\n\n` +
                  `They will also lose their approved status. Use this ONLY for applicants who were previously auto-approved.`
                )) onUpdate(app.id, "rejected", "apology");
              }}
              disabled={updating}
              data-testid="reject-apology-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 hover:!border-orange-500 hover:!text-orange-500"
              title="Reject with a bilingual apology email — only for applicants auto-approved by the earlier glitch"
            >
              <XCircle className="w-4 h-4" /> Reject + Apology
            </button>
          )}
          {!isReviewer && app.status !== "rejected" && (
            <button
              onClick={() => onUpdate(app.id, "rejected")}
              disabled={updating}
              data-testid="reject-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 hover:!border-red-500 hover:!text-red-500"
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
          )}
          {!isReviewer && app.status !== "rejected" && (
            <button
              onClick={() => {
                if (window.confirm(
                  `Reject ${app.full_name} SILENTLY (no email sent)?\n\n` +
                  `Use this when their email address is known-bounced or invalid — they would never receive the rejection anyway.\n\n` +
                  `Their status will flip to rejected and NO email will be attempted.`
                )) onUpdate(app.id, "rejected", "silent");
              }}
              disabled={updating}
              data-testid="reject-silent-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 hover:!border-white/60 hover:!text-white"
              title="Reject without sending any email — for bounced / invalid addresses"
            >
              <XCircle className="w-4 h-4" /> Reject (No Email)
            </button>
          )}

          {/* REVIEWER actions — buttons LOOK like real Approve/Reject, but under the hood
              they create a recommendation record (no status change, no email to applicant).
              The reviewer sees the apparent status flip via the `displayStatus` overlay in
              AppRow + the pendingRec-aware "Current Status" above. The main admin still sees
              the real status + the pending-rec banner. */}
          {isReviewerRole && !readOnlyArchive && (pendingRec?.decision !== "approved") && (
            <button
              onClick={() => {
                if (window.confirm(`Approve ${app.full_name}?`)) {
                  onRecommend(app.id, "approved", "");
                }
              }}
              disabled={updating}
              data-testid="approve-btn"
              className="btn-primary inline-flex items-center justify-center gap-2"
              style={{ background: "#22C55E", color: "#000" }}
              title="Approve this application"
            >
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
          )}
          {isReviewerRole && !readOnlyArchive && (pendingRec?.decision !== "rejected") && (
            <button
              onClick={() => {
                if (window.confirm(`Reject ${app.full_name}?`)) {
                  onRecommend(app.id, "rejected", "");
                }
              }}
              disabled={updating}
              data-testid="reject-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 hover:!border-red-500 hover:!text-red-500"
              title="Reject this application"
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
          )}
          {app.status === "approved" && (
            <a
              href={bust(`${API}/admin/applications/${app.id}/entry-pass.pdf`)}
              data-authed-download="1"
              data-download-filename={`entry-pass-${app.id}.pdf`}
              data-testid="download-pass-pdf-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 hover:!border-[#F5C518] hover:!text-[#F5C518]"
              title="Download this applicant's 🎟 Official Entry Pass as a PDF"
            >
              <Download className="w-4 h-4" /> Download Entry Pass PDF
            </a>
          )}
          {app.status === "approved" && !isReviewer && (
            <button
              onClick={resendEntryPass}
              disabled={resending || hasUnsavedEdits || !app.email}
              data-testid="send-updated-approval-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2 hover:!border-[#22C55E] hover:!text-[#22C55E] disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                hasUnsavedEdits
                  ? "Save or cancel your open edits before sending."
                  : !app.email
                  ? "Applicant has no email on file. Add an email, save, then send."
                  : "Send the updated bilingual approval email built from the LATEST saved record. Pass code stays the same. Save alone does not send."
              }
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" /> Send Updated Approval
                </>
              )}
            </button>
          )}
          {app.status === "approved" && hasUnsavedEdits && (
            <div
              data-testid="unsaved-edits-hint"
              className="text-[11px] tracking-wider uppercase text-[#F5C518]/80 font-heading"
            >
              Unsaved edit open · save or cancel before sending
            </div>
          )}
          {app.status === "rejected" && (
            <a
              href={composeRejection(app)}
              data-testid="email-btn"
              className="btn-ghost inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" /> Email Applicant
            </a>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-kicker mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">{children}</div>
    </div>
  );
}

function Field({ icon: Icon, label, value, full }) {
  return (
    <div className={`flex items-start gap-3 ${full ? "sm:col-span-2" : ""}`}>
      <Icon className="w-4 h-4 text-[#F5C518] mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{label}</div>
        <div className="font-body text-sm text-white break-words">{value}</div>
      </div>
    </div>
  );
}

function DetailEditField({ icon: Icon, label, value, onChange, type = "text", placeholder, multiline, full, testid }) {
  return (
    <div className={`flex items-start gap-3 ${full ? "sm:col-span-2" : ""}`}>
      <Icon className="w-4 h-4 text-[#F5C518] mt-2 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{label}</div>
        {multiline ? (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={3}
            data-testid={testid}
            className="w-full bg-black/40 border border-[#F5C518]/40 px-3 py-1.5 text-sm text-white focus:border-[#F5C518] focus:outline-none resize-y font-body"
          />
        ) : (
          <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testid}
            className="w-full bg-black/40 border border-[#F5C518]/40 px-3 py-1.5 text-sm text-white focus:border-[#F5C518] focus:outline-none font-body"
          />
        )}
      </div>
    </div>
  );
}

// Reusable inline-editable field. Default state: shows value + small EDIT pill button.
// When admin clicks Edit: turns into input + Save/Cancel. PATCHes only this field on save.
// Reviewer (`isReviewer`) sees the value but no Edit button — at all 11 fields.
export function EditableField({
  icon: Icon, label, value, displayValue, fieldKey, onSave, isReviewer,
  type = "text", options, placeholder, multiline, full, validate, parse, extra, testid,
  hidden = false, onEditingChange,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  // Bubble the open-editor state up so the modal can gate the
  // "Send Updated Approval" button on a clean saved state.
  useEffect(() => {
    if (onEditingChange) onEditingChange(fieldKey, editing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  if (hidden) return null;

  const startEdit = () => {
    setDraft(value ?? "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  const submit = async () => {
    const raw = (typeof draft === "string") ? draft.trim() : draft;
    if (validate) {
      const err = validate(raw);
      if (err) { toast.error(err); return; }
    }
    if ((raw || "") === (value || "")) {
      setEditing(false);
      return;
    }
    const newVal = parse ? parse(raw) : raw;
    setSaving(true);
    try {
      await onSave(fieldKey, newVal);
      toast.success(`${label} updated`);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to update ${label}`);
    } finally {
      setSaving(false);
    }
  };

  const displayed = displayValue !== undefined ? displayValue : (value ?? "—");

  return (
    <div className={`flex items-start gap-3 py-2 ${full ? "sm:col-span-2" : ""}`}>
      <Icon className="w-4 h-4 text-[#F5C518] mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-white/40 tracking-wider uppercase mb-1">{label}</div>
        {editing ? (
          <div className="flex flex-wrap items-start gap-2">
            {type === "select" ? (
              <select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid={`${testid}-select`}
                autoFocus
                className="flex-1 min-w-[220px] bg-black/40 border border-[#F5C518] px-3 py-1.5 text-sm text-white focus:outline-none"
              >
                <option value="">— Select —</option>
                {(options || []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : multiline ? (
              <textarea
                value={draft || ""}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={placeholder}
                rows={3}
                data-testid={`${testid}-input`}
                autoFocus
                className="flex-1 min-w-[220px] bg-black/40 border border-[#F5C518] px-3 py-1.5 text-sm text-white focus:outline-none resize-y font-body"
              />
            ) : (
              <input
                type={type}
                value={draft ?? ""}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={placeholder}
                data-testid={`${testid}-input`}
                autoFocus
                className="flex-1 min-w-[220px] bg-black/40 border border-[#F5C518] px-3 py-1.5 text-sm text-white focus:outline-none"
              />
            )}
            <button
              onClick={submit}
              disabled={saving}
              data-testid={`${testid}-save-btn`}
              className="px-3 py-1.5 bg-[#F5C518] text-black text-xs font-heading font-bold tracking-wider uppercase hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="px-3 py-1.5 border border-white/20 text-white/70 text-xs font-body tracking-wider uppercase hover:border-white/40 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-white break-words" data-testid={`${testid}-value`}>{displayed}</span>
            {!isReviewer && (
              <button
                onClick={startEdit}
                data-testid={`${testid}-edit-btn`}
                className="text-[10px] px-2 py-0.5 border border-[#F5C518]/40 text-[#F5C518] tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition"
              >
                Edit
              </button>
            )}
            {extra}
          </div>
        )}
      </div>
    </div>
  );
}


function DeliveryStatusCard({ status, loading, onRefresh, onComplete, onForceAll, running, progress, readOnly = false, archiveReadOnly = false, ready = true }) {
  const [expanded, setExpanded] = useState(false);
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  // Sep 20 recurrence forensic tools — deployed identity + synthetic
  // preview + scoped retrieve of an actual sent message. All three use
  // the ADMIN JWT already held by the signed-in dashboard session; the
  // token never leaves the browser process.
  const [deployedVersion, setDeployedVersion] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [previewOpening, setPreviewOpening] = useState(null);
  const [messageEvidence, setMessageEvidence] = useState(null);   // {id,subject,html_escaped,...}
  const [messageEvidenceLoading, setMessageEvidenceLoading] = useState(null); // message_id currently loading
  // May 2026 read-only archive: separate READ-EXPORT permission from
  // MUTATION permission. `readOnly` remains the reviewer flag (preserves
  // the existing click-block on CSV downloads for reviewers). Under the
  // May archive, an authorized admin retains CSV access; only the three
  // provider-triggering / mutating controls (Force-Send / Complete
  // Delivery / Re-verify) are additionally blocked.
  const mutationBlocked = readOnly || archiveReadOnly;

  const token = typeof window !== "undefined" ? localStorage.getItem("admin_token") : null;

  const runLookup = async (e) => {
    e?.preventDefault();
    if (readOnly) {
      logReviewerBlockedClick("Per-applicant Resend lookup", { email: lookupEmail.trim() });
      return;
    }
    const em = lookupEmail.trim();
    if (!em) return;
    setLookingUp(true);
    setLookupResult(null);
    try {
      const res = await axios.get(
        `${API}/admin/email-lookup?email=${encodeURIComponent(em)}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 2 * 60 * 1000 }
      );
      setLookupResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setLookingUp(false);
    }
  };

  // Sep 20 forensic — fetch deployed identity (source_sha256 / git_commit
  // / process_started_at). Read-only, admin-gated on backend.
  const fetchDeployedVersion = async () => {
    if (readOnly) return;
    setVersionLoading(true);
    try {
      const r = await axios.get(`${API}/_admin/version`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 30_000,
      });
      setDeployedVersion(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Version fetch failed");
    } finally {
      setVersionLoading(false);
    }
  };

  // Sep 21 forensic — open the deployed synthetic preview in a new tab.
  // Fetches the html via admin JWT (Authorization header), converts to a
  // Blob, and opens the blob URL — the JWT never appears in the URL bar
  // or in any URL fragment, so no credential is exposed.
  //
  // Popup-blocker safety: we must open the tab synchronously in the
  // same tick as the user's click (before any `await`), because most
  // browsers require an unbroken user-gesture chain for window.open.
  //
  // Isolation safety: we intentionally do NOT pass `noopener` to
  // `window.open` — with `noopener`, browsers return `null` even when
  // a tab actually opens, which caused the tab to remain stuck at
  // about:blank because we had no handle to navigate it. Instead we
  // retain the WindowProxy handle and immediately sever `w.opener`
  // (synchronously, before any await, before any navigation or content
  // rendering). If opener cannot be severed we close and abort.
  //
  // Referrer suppression: injected via <meta name="referrer"
  // content="no-referrer"> at the top of the served preview HTML, so
  // any nested subrequest inherits no-referrer regardless of scheme.
  const openSyntheticPreview = async (variant, resend) => {
    if (readOnly) return;
    const key = `${variant}-${resend}`;
    // 1) Open about:blank synchronously to preserve the user-gesture
    //    chain. Do NOT use `noopener` — it forces window.open to return
    //    null in Chrome/Firefox and the tab becomes unreachable.
    const w = window.open("about:blank", "_blank");
    if (!w) {
      toast.error("Popup blocked — allow pop-ups for this site to view the synthetic preview.");
      return;
    }
    // 2) IMMEDIATELY sever opener isolation — before any await, before
    //    any navigation, before any content is rendered. Verify.
    try { w.opener = null; } catch (_) { /* best effort */ }
    let isolated = false;
    try { isolated = (w.opener === null || w.opener === undefined); } catch (_) { isolated = false; }
    if (!isolated) {
      try { w.close(); } catch (_) {}
      toast.error("Preview aborted — opener isolation could not be established.");
      return;
    }
    setPreviewOpening(key);
    let blobUrl = null;
    try {
      const r = await axios.get(
        `${API}/_admin/preview/approval_email/${encodeURIComponent(variant)}?resend=${resend}&scope=synthetic`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "text", timeout: 30_000 }
      );
      // Inject a no-referrer meta at the top of the served HTML for
      // defense-in-depth (does not alter any visible approved copy or
      // design — the <meta> is invisible; email body is untouched).
      const referrerMeta = '<meta name="referrer" content="no-referrer">';
      const html = typeof r.data === "string"
        ? (r.data.match(/<head[^>]*>/i)
            ? r.data.replace(/<head[^>]*>/i, (m) => `${m}${referrerMeta}`)
            : `${referrerMeta}${r.data}`)
        : String(r.data);
      const blob = new Blob([html], { type: "text/html; charset=utf-8" });
      blobUrl = URL.createObjectURL(blob);
      // Re-check opener isolation after navigation is initiated. If the
      // child ever regained a non-null opener (should not happen), close.
      w.location.href = blobUrl;
      try {
        if (w.opener !== null && w.opener !== undefined) {
          try { w.opener = null; } catch (_) {}
        }
      } catch (_) {}
      // Blob URL revoked after the new tab has loaded so memory is freed.
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 60_000);
    } catch (err) {
      try { w.close(); } catch (_) {}
      if (blobUrl) { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewOpening(null);
    }
  };

  // Sep 20 forensic — retrieve one Winter approval-family message's
  // actual sent HTML from Resend (via backend proxy). Backend returns
  // html_escaped; we render inside a <pre> so no <img>/<a>/<script>
  // could trigger network I/O even if present.
  const fetchMessageEvidence = async (messageId, subject) => {
    if (readOnly) return;
    setMessageEvidenceLoading(messageId);
    try {
      const r = await axios.get(
        `${API}/admin/resend-message?message_id=${encodeURIComponent(messageId)}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30_000 }
      );
      setMessageEvidence({ ...r.data, _hint_subject: subject });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Message retrieve failed");
    } finally {
      setMessageEvidenceLoading(null);
    }
  };

  if (loading && !status) {
    return (
      <div
        className="mb-6 border border-white/10 bg-white/[0.02] p-5 flex items-center gap-3"
        data-testid="delivery-status-loading"
      >
        <Loader2 className="w-4 h-4 text-[#F5C518] animate-spin" />
        <span className="font-body text-sm text-white/60 tracking-wider uppercase">
          Verifying corrected-pass delivery with Resend…
        </span>
      </div>
    );
  }
  if (!status) {
    // Feb 2026 cross-edition audit — never silently hide essential
    // bulk controls. `status === null` means either (a) we haven't
    // fetched yet, (b) the last fetch was dropped as
    // superseded/mismatched by the scope guard, or (c) the response
    // came back from an old backend that didn't echo `scope_edition`.
    // Surface an actionable retry rather than removing the card
    // entirely.
    return (
      <div
        className="mb-6 border border-amber-500/40 bg-amber-500/[0.05] p-5 flex items-center justify-between gap-3 flex-wrap"
        data-testid="delivery-status-unavailable"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <div className="font-body text-sm text-amber-200/90 tracking-wider uppercase">
            Delivery counts unavailable for this edition
            <div className="normal-case tracking-normal text-white/60 text-xs mt-1">
              Bulk send actions are disabled until scope-matched counts load. Click Re-verify.
            </div>
          </div>
        </div>
        <button
          onClick={readOnly ? () => logReviewerBlockedClick("Re-verify") : onRefresh}
          data-testid="refresh-delivery-btn-unavailable"
          className="text-xs px-3 py-2 border border-amber-400/60 text-amber-300 font-heading tracking-wider uppercase hover:bg-amber-400 hover:text-black transition"
        >
          Re-verify
        </button>
      </div>
    );
  }

  const { total_approved, delivered_count, missing_count, failed_count, no_email_count, missing, failed, no_email, receipt_confirmed_count = 0, pixel_opened_count = 0, checked_in_count = 0, pending_verification_count = 0, pending_verification = [], provider_status = "fresh", provider_incomplete = false, provider_age_s = null, provider_negatives_trustworthy = true } = status;
  const outstanding = missing_count + failed_count;
  const allDone = outstanding === 0 && no_email_count === 0 && pending_verification_count === 0;
  const pct = total_approved ? Math.round((delivered_count / total_approved) * 100) : 0;
  const missingList = [...missing, ...failed];
  const provenCount = Math.max(receipt_confirmed_count, pixel_opened_count); // pixel is a superset — every confirm also loads pixel

  // Sep 12, 2026 owner-directed honesty: never let a `checking` /
  // `stale` / `unavailable` provider state look like a conclusive delivery
  // list. The badge below reflects the actual freshness of the Resend
  // scan behind these numbers.
  const providerBadge = (() => {
    if (provider_status === "fresh" && !provider_incomplete) return null;
    if (provider_status === "checking") {
      return { label: "Checking Resend delivery log…", tone: "amber", detail: "Delivery counts refresh in a few seconds." };
    }
    if (provider_status === "stale") {
      const ageM = provider_age_s ? Math.round(provider_age_s / 60) : null;
      return { label: `Resend log stale${ageM ? ` (${ageM} min old)` : ""}`, tone: "amber", detail: "Auto-refreshing. Current counts reflect the last successful scan." };
    }
    if (provider_status === "unavailable") {
      return { label: "Resend unavailable — using DB signals only", tone: "red", detail: "Only receipt-confirmed / pixel-opened / DB-stamped rows are counted as delivered." };
    }
    if (provider_incomplete) {
      return { label: "Partial Resend scan (bounded)", tone: "amber", detail: "Positive deliveries counted; rows without a positive signal are Pending Verification." };
    }
    return null;
  })();

  const statusColor = allDone ? "#22C55E" : outstanding > 0 ? "#F5C518" : "#EF4444";
  const Icon = allDone ? ShieldCheck : outstanding > 0 ? AlertTriangle : XCircle;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 border-2 p-5 sm:p-6"
      style={{ borderColor: statusColor, background: `${statusColor}0D` }}
      data-testid="delivery-status-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-[260px] flex-1">
          <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: statusColor }} />
          <div className="min-w-0">
            <div className="label-kicker mb-1" style={{ color: statusColor }}>
              Entry Pass Delivery · Verified via Resend + Receipt Tracking
            </div>
            <div className="font-heading font-extrabold text-white text-2xl sm:text-3xl leading-tight" data-testid="delivery-headline">
              {allDone
                ? `All ${total_approved} approved applicants received their corrected pass`
                : `${delivered_count} of ${total_approved} delivered`}
            </div>
            <div className="font-body text-xs sm:text-sm text-white/60 mt-1">
              {allDone
                ? `${pct}% verified against Resend's delivery log. Every pass shows the CREW row.`
                : `${pct}% delivered · ${provider_negatives_trustworthy ? `${missing_count} missing` : `${pending_verification_count} pending verification`}${failed_count ? ` · ${failed_count} failed` : ""}${no_email_count ? ` · ${no_email_count} no email on file` : ""}`}
            </div>
            {providerBadge && (
              <div
                className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1 border text-[11px] font-heading font-bold tracking-wider uppercase ${providerBadge.tone === "red" ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}
                data-testid="provider-freshness-badge"
                title={providerBadge.detail}
              >
                <AlertTriangle className="w-3 h-3" />
                {providerBadge.label}
                <span className="text-white/40 font-body normal-case tracking-normal">
                  · {providerBadge.detail}
                </span>
              </div>
            )}
            {provenCount > 0 && (
              <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-[11px] font-heading font-bold tracking-wider uppercase" data-testid="proven-received-badge">
                <ShieldCheck className="w-3 h-3" />
                {provenCount} Proven Received
                <span className="text-emerald-400/60 font-body normal-case tracking-normal">
                  · {receipt_confirmed_count} tapped Confirm · {pixel_opened_count} opened email
                </span>
              </div>
            )}
            {checked_in_count > 0 && (
              <div className="mt-2 ml-0 sm:ml-2 inline-flex items-center gap-2 px-2.5 py-1 border border-sky-500/40 bg-sky-500/10 text-sky-400 text-[11px] font-heading font-bold tracking-wider uppercase" data-testid="checked-in-badge">
                <QrCode className="w-3 h-3" />
                {checked_in_count} Checked In at Gate
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Feb 2026 cross-edition audit — exports scoped to the same
              edition the parent card counts are scoped to. `ed` is
              read from the payload's `scope_edition` echo (guaranteed
              present by the ready-gate on the parent). */}
          {(() => { /* eslint-disable-next-line no-unused-vars */ return null; })()}
          <a
            href={bust(`${API}/admin/checked-in.csv?edition=${encodeURIComponent(status.scope_edition)}`)}
              data-authed-download="1"
              data-download-filename={`checked-in-${status.scope_edition}.csv`}
            onClick={readOnly ? (e) => { e.preventDefault(); logReviewerBlockedClick("Checked-In CSV"); } : undefined}
            data-testid="download-checked-in-csv"
            className="inline-flex items-center gap-2 px-3 py-2 border border-sky-500/40 text-sky-400 text-xs font-heading font-bold tracking-wider uppercase hover:bg-sky-500 hover:text-black transition"
            title={`CSV: approved applicants (edition=${status.scope_edition}) who physically checked in at the gate via QR code`}
          >
            <QrCode className="w-3.5 h-3.5" /> Checked-In CSV ({checked_in_count})
          </a>
          <a
            href={bust(`${API}/admin/receipt-confirmed.csv?edition=${encodeURIComponent(status.scope_edition)}`)}
              data-authed-download="1"
              data-download-filename={`receipt-confirmed-${status.scope_edition}.csv`}
            onClick={readOnly ? (e) => { e.preventDefault(); logReviewerBlockedClick("Receipt Confirmed CSV"); } : undefined}
            data-testid="download-receipt-confirmed-csv"
            className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-500/40 text-emerald-400 text-xs font-heading font-bold tracking-wider uppercase hover:bg-emerald-500 hover:text-black transition"
            title={`CSV: approved applicants (edition=${status.scope_edition}) with HARD PROOF they received the email (tapped Confirm button or pixel loaded)`}
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Receipt Confirmed CSV ({provenCount})
          </a>
          <a
            href={bust(`${API}/admin/delivery-report.csv?edition=${encodeURIComponent(status.scope_edition)}&scope=delivered`)}
              data-authed-download="1"
              data-download-filename={`delivery-report-${status.scope_edition}-delivered.csv`}
            onClick={readOnly ? (e) => { e.preventDefault(); logReviewerBlockedClick("Delivered CSV"); } : undefined}
            data-testid="download-delivered-csv"
            className="inline-flex items-center gap-2 px-3 py-2 border border-green-500/40 text-green-400 text-xs font-heading font-bold tracking-wider uppercase hover:bg-green-500 hover:text-black transition"
            title={`CSV: everyone (edition=${status.scope_edition}) who HAS received their Entry Pass`}
          >
            <Download className="w-3.5 h-3.5" /> Delivered CSV ({delivered_count})
          </a>
          <a
            href={bust(`${API}/admin/delivery-report.csv?edition=${encodeURIComponent(status.scope_edition)}&scope=missing`)}
              data-authed-download="1"
              data-download-filename={`delivery-report-${status.scope_edition}-missing.csv`}
            onClick={readOnly ? (e) => { e.preventDefault(); logReviewerBlockedClick("Missing CSV"); } : undefined}
            data-testid="download-missing-csv"
            className="inline-flex items-center gap-2 px-3 py-2 border border-red-500/40 text-red-400 text-xs font-heading font-bold tracking-wider uppercase hover:bg-red-500 hover:text-black transition"
            title={`CSV: everyone (edition=${status.scope_edition}) who has NOT yet received their Entry Pass`}
          >
            <Download className="w-3.5 h-3.5" /> Missing CSV ({outstanding + no_email_count})
          </a>
          <a
            href={bust(`${API}/admin/never-delivered.csv?edition=${encodeURIComponent(status.scope_edition)}`)}
              data-authed-download="1"
              data-download-filename={`never-delivered-${status.scope_edition}.csv`}
            onClick={readOnly ? (e) => { e.preventDefault(); logReviewerBlockedClick("Never Delivered CSV"); } : undefined}
            data-testid="download-never-delivered-csv"
            className="inline-flex items-center gap-2 px-3 py-2 border border-orange-500/40 text-orange-400 text-xs font-heading font-bold tracking-wider uppercase hover:bg-orange-500 hover:text-black transition"
            title={`CSV (edition=${status.scope_edition}): approved applicants who DEFINITELY received zero emails from us (hard-bounced addresses + no email on file). Excludes spam-complaints since those were actually delivered.`}
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Never Delivered CSV
          </a>
          <button
            onClick={mutationBlocked ? () => logReviewerBlockedClick("Re-verify") : onRefresh}
            disabled={loading || running}
            data-testid="refresh-delivery-btn"
            className="text-xs text-white/60 hover:text-[#F5C518] transition disabled:opacity-40 tracking-wider uppercase px-3 py-2 border border-white/10"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Re-verify"}
          </button>
          <button
            onClick={mutationBlocked ? () => logReviewerBlockedClick("Force-Send to All") : onForceAll}
            disabled={running || !ready || !total_approved}
            data-testid="force-resend-all-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#F5C518] text-[#F5C518] text-xs font-heading font-bold tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition disabled:opacity-50"
            title={!ready
              ? "Waiting for scope-matched delivery counts before this action can be launched safely."
              : "Force-send a fresh unique-subject email to every approved applicant (guarantees an un-threaded inbox delivery)"}
          >
            {running ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Working…</>
            ) : !ready ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading scope…</>
            ) : (
              <>Force-Send to All ({total_approved})</>
            )}
          </button>
          {!allDone && (
            <button
              onClick={mutationBlocked ? () => logReviewerBlockedClick("Complete Delivery") : onComplete}
              disabled={running || !ready || outstanding === 0}
              data-testid="complete-delivery-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 font-heading font-bold tracking-wider uppercase text-sm transition disabled:opacity-50"
              style={{ background: "#F5C518", color: "#000" }}
            >
              {running ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Completing…</>
              ) : (
                <><Send className="w-4 h-4" /> Complete Delivery ({outstanding})</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="h-2 bg-black/40 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6 }}
            className="h-full"
            style={{ background: statusColor }}
          />
        </div>
      </div>

      {/* Active job progress */}
      {running && progress && (
        <div className="mt-4 p-3 border border-[#F5C518]/30 bg-black/30">
          <div className="flex items-center justify-between mb-2 text-xs text-white/70 font-body tracking-wider uppercase">
            <span>Sending in progress</span>
            <span className="text-[#F5C518] font-semibold">
              {progress.sent}/{progress.total} sent
              {progress.failed ? ` · ${progress.failed} failed` : ""}
            </span>
          </div>
          <div className="h-1.5 bg-black/60 overflow-hidden">
            <motion.div
              animate={{ width: `${progress.total ? Math.round((progress.processed / progress.total) * 100) : 0}%` }}
              transition={{ duration: 0.4 }}
              className="h-full bg-[#F5C518]"
            />
          </div>
        </div>
      )}

      {/* Per-email lookup — admin-only; reviewers shouldn't see emails (PII) */}
      {!readOnly && (
      <div className="mt-5 pt-4 border-t border-white/10">
        <div className="label-kicker mb-2" style={{ color: "#F5C518" }}>
          Check one specific applicant
        </div>
        <form onSubmit={runLookup} className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={lookupEmail}
            onChange={(e) => setLookupEmail(e.target.value)}
            placeholder="applicant@example.com"
            data-testid="lookup-email-input"
            className="flex-1 min-w-[240px] bg-black/40 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#F5C518] focus:outline-none"
          />
          <button
            type="submit"
            disabled={lookingUp || !lookupEmail.trim()}
            data-testid="lookup-email-btn"
            className="px-4 py-2 border border-[#F5C518] text-[#F5C518] text-sm font-body tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition disabled:opacity-50"
          >
            {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check Resend"}
          </button>
        </form>
        {lookupResult && (
          <div className="mt-3 p-3 border border-white/10 bg-black/30 text-xs font-body" data-testid="lookup-result">
            <div className="text-white/70 mb-2">
              <strong className="text-white">{lookupResult.email}</strong>
              {" · "}
              {lookupResult.applicant_found
                ? <span className="text-green-400">in applicant DB ({lookupResult.applicant?.status})</span>
                : <span className="text-red-400">not in applicant DB</span>}
            </div>
            {lookupResult.resend_message_count === 0 ? (
              <div className="text-red-400">
                No Resend messages found for this address. They have NEVER received any email from us.
              </div>
            ) : (
              <ul className="space-y-1">
                {lookupResult.resend_messages.slice(0, 8).map((m) => (
                  <li key={m.id} className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${
                      m.last_event === "delivered" || m.last_event === "opened" || m.last_event === "clicked"
                        ? "bg-green-500/20 text-green-400 border border-green-500/40"
                        : m.last_event === "bounced" || m.last_event === "complained"
                        ? "bg-red-500/20 text-red-400 border border-red-500/40"
                        : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/40"
                    }`}>
                      {m.last_event || "unknown"}
                    </span>
                    <span className="text-white/50">{new Date(m.created_at).toLocaleString()}</span>
                    <span className="text-white/40 truncate">{m.subject}</span>
                    <button
                      type="button"
                      onClick={() => fetchMessageEvidence(m.id, m.subject)}
                      disabled={messageEvidenceLoading === m.id}
                      data-testid={`view-sent-html-btn-${m.id}`}
                      className="ml-auto px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase border border-[#F5C518]/60 text-[#F5C518] hover:bg-[#F5C518] hover:text-black transition disabled:opacity-50"
                      title="Fetch this message's actual sent HTML from Resend (Winter approval-family only). Renders escaped so no network side effects."
                    >
                      {messageEvidenceLoading === m.id ? "…" : "View sent HTML"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {messageEvidence && (
              <div className="mt-3 border border-[#F5C518]/40 bg-black/50 p-3" data-testid="message-evidence-panel">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="text-[11px] text-[#F5C518] font-bold tracking-wider uppercase">
                    Sent HTML · {messageEvidence.id}
                  </div>
                  <button
                    type="button"
                    onClick={() => setMessageEvidence(null)}
                    data-testid="close-message-evidence-btn"
                    className="text-white/40 hover:text-white text-[10px] tracking-wider uppercase"
                  >Close</button>
                </div>
                <div className="text-[10px] text-white/60 mb-2 space-y-0.5">
                  <div>Subject · <span className="text-white/80">{messageEvidence.subject}</span></div>
                  <div>
                    Attributed to · <span className="text-[#F5C518]">#{messageEvidence.attributed_pass_code}</span>
                    <span className="text-white/30"> · </span>
                    <span className="text-white/70">{messageEvidence.attributed_participation_day}</span>
                  </div>
                  <div>
                    created_at · {messageEvidence.created_at}
                    <span className="text-white/30"> · </span>
                    last_event · <span className="text-white/80">{messageEvidence.last_event}</span>
                    <span className="text-white/30"> · </span>
                    html_len · {messageEvidence.html_len}
                  </div>
                </div>
                <pre
                  data-testid="message-evidence-pre"
                  className="text-[10px] leading-snug text-white/70 whitespace-pre-wrap break-all max-h-[420px] overflow-auto p-2 border border-white/10 bg-black/70"
                  // NOTE: dangerouslySetInnerHTML is DELIBERATELY NOT used.
                  // Server returns html.escape(..., quote=True) so this
                  // renders as text — no <img>/<a>/<script> can fire.
                >{messageEvidence.html_escaped}</pre>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Sep 20 · Deployed revision + synthetic previews (admin-only). */}
      {!readOnly && (
      <div className="mt-5 pt-4 border-t border-white/10" data-testid="deploy-forensic-panel">
        <div className="label-kicker mb-2" style={{ color: "#F5C518" }}>
          Deployed revision &amp; synthetic previews
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={fetchDeployedVersion}
            disabled={versionLoading}
            data-testid="fetch-deployed-version-btn"
            className="px-3 py-1.5 border border-[#F5C518] text-[#F5C518] text-xs tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition disabled:opacity-50"
          >
            {versionLoading ? "Checking…" : "Check deployed revision"}
          </button>
          {deployedVersion && (
            <div className="text-[10px] font-mono text-white/70 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="deployed-version-info">
              <span>source_sha256 · <span className="text-[#F5C518]">{deployedVersion.source_sha256}</span></span>
              <span>git · {deployedVersion.git_commit?.slice(0, 12)}</span>
              <span>started · {deployedVersion.process_started_at}</span>
            </div>
          )}
        </div>
        <div className="mt-3">
          <div className="text-[10px] text-white/60 tracking-wider uppercase mb-2">
            Synthetic preview (opens in new tab — fictional applicant, no PII):
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {["day1", "day2", "day3", "day1_day3", "outdoor", "indoor"].flatMap(v =>
              [0, 1].map(rs => {
                const key = `${v}-${rs}`;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => openSyntheticPreview(v, rs)}
                    disabled={previewOpening === key}
                    data-testid={`open-synthetic-preview-${v}-${rs === 1 ? "resend" : "initial"}-btn`}
                    className="px-2 py-1 text-[10px] tracking-wider uppercase border border-white/20 text-white/70 hover:border-[#F5C518] hover:text-[#F5C518] transition disabled:opacity-50"
                  >
                    {previewOpening === key ? "…" : `${v} · ${rs === 1 ? "resend" : "initial"}`}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      )}


      {/* Missing list — reviewer-blocked because it surfaces email addresses (PII) */}
      {missingList.length > 0 && !readOnly && (
        <div className="mt-4">
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid="toggle-missing-list"
            className="flex items-center gap-2 text-xs font-body tracking-wider uppercase text-white/70 hover:text-[#F5C518] transition"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Hide" : "Show"} missing recipients ({missingList.length})
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <ul className="mt-3 divide-y divide-white/5 border border-white/10 max-h-72 overflow-y-auto">
                  {missingList.map((r) => (
                    <li key={r.id} className="px-4 py-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-white truncate font-body">{r.full_name}</div>
                          <div className="text-white/40 text-xs truncate">
                            {r.email || <span className="text-red-400">no email on file</span>}
                            <span className="text-white/20 mx-2">·</span>
                            <span className="text-[#F5C518]/70">{r.group}</span>
                            {r.error && (
                              <>
                                <span className="text-white/20 mx-2">·</span>
                                <span className="text-red-400">{r.error}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {no_email.map((r) => (
                    <li key={r.id} className="px-4 py-2 text-sm bg-red-500/5">
                      <div className="text-white truncate font-body">{r.full_name}</div>
                      <div className="text-red-400 text-xs">No email on file — cannot send</div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}

const CREW_LISTS = [
  { key: "CrewZNatioN", label: "CrewZNatioN Guest List" },
  { key: "Blur Kit",    label: "Blur Kit Guest List" },
  { key: "A9X Club",    label: "A9X Club Guest List" },
  { key: "The Fahrers", label: "The Fahrers Guest List" },
  { key: "Super Fuel",  label: "Super Fuel Guest List" },
  { key: "Riyadh United Bikers", label: "Riyadh United Bikers Guest List" },
  { key: "RJC",         label: "RJC Guest List" },
];

function CrewGuestLists({ token, isReviewer = false }) {
  // Owner directive (Sept 12, 2026): admin JWT MUST NOT appear in the
  // URL / referrer / browser history / DOM `href`. The click interceptor
  // in AdminDashboard() sends the token via the Authorization header
  // instead. All hrefs below therefore omit `token=` and rely on the
  // `data-authed-download` attribute for header-auth fetching.
  const buildUrl = (crew, opts = {}) => {
    const qs = new URLSearchParams({ crew });
    if (opts.vehicles) qs.set("vehicles", opts.vehicles);
    return `${API}/admin/applications-guest-list.csv?${qs.toString()}`;
  };
  const buildCrossCrewUrl = (vehicles) => {
    const qs = new URLSearchParams({ vehicles });
    return `${API}/admin/applications-guest-list.csv?${qs.toString()}`;
  };

  return (
    <div className="mb-6 border border-white/10 bg-white/[0.02] p-4 sm:p-5">
      <div className="label-kicker mb-3" style={{ color: "#F5C518" }}>
        Per-Crew Guest Lists · Approved Only · Name · Mobile · Car · Group · Status · Primary Photo
      </div>
      <div className="flex flex-wrap gap-2">
        {CREW_LISTS.map((c) => {
          // CrewZNatioN list is refined: motorcycles AND SUVs/off-road are
          // excluded; each has its own dedicated list below. Other crew
          // buttons remain unfiltered so crew leaders see their full roster.
          const isCrewz = c.key.toLowerCase() === "crewznation";
          const href = isCrewz
            ? buildUrl(c.key, { vehicles: "non-suv-cars" })
            : buildUrl(c.key);
          return (
            <a
              key={c.key}
              href={href}
              data-authed-download="1"
              data-download-filename={`${c.key.replace(/\s+/g, "-").toLowerCase()}-guest-list.csv`}
              onClick={blockClickIfReviewer(isReviewer, `Crew Guest List · ${c.key}`)}
              data-testid={`crew-list-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#F5C518]/40 bg-[#F5C518]/5 text-[#F5C518] text-xs font-heading font-bold tracking-wider uppercase hover:bg-[#F5C518] hover:text-black transition"
              title={isCrewz ? "CrewZNatioN: sedans & sports cars only — motorcycles and SUVs/off-road have their own lists below" : `Download applicants in ${c.key}`}
            >
              <Download className="w-3.5 h-3.5" /> {c.label}
              {isCrewz && <span className="ml-1 opacity-60">(cars)</span>}
            </a>
          );
        })}

        {/* SUV / Off-Road Guest List — all crews, SUVs + pickups only */}
        <a
          href={buildCrossCrewUrl("suvs")}
          data-authed-download="1"
          data-download-filename="suv-guest-list.csv"
          onClick={blockClickIfReviewer(isReviewer, "SUV Guest List")}
          data-testid="crew-list-suvs"
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#A78BFA]/60 bg-[#A78BFA]/5 text-[#A78BFA] text-xs font-heading font-bold tracking-wider uppercase hover:bg-[#A78BFA] hover:text-black transition"
          title="All SUVs, pickups, and off-road vehicles across every crew"
        >
          <Download className="w-3.5 h-3.5" /> SUV Guest List
        </a>

        {/* Motorcycles Guest List — all crews, bikes only */}
        <a
          href={buildCrossCrewUrl("motorcycles")}
          data-authed-download="1"
          data-download-filename="motorcycles-guest-list.csv"
          onClick={blockClickIfReviewer(isReviewer, "Motorcycles Guest List")}
          data-testid="crew-list-motorcycles"
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#22D3EE]/60 bg-[#22D3EE]/5 text-[#22D3EE] text-xs font-heading font-bold tracking-wider uppercase hover:bg-[#22D3EE] hover:text-black transition"
          title="All motorcycle riders across every crew, deduplicated by detection logic"
        >
          <Download className="w-3.5 h-3.5" /> Motorcycles Guest List
        </a>
      </div>
    </div>
  );
}



function DuplicateCleanupPanel({ data, busy, token, onArchive, onRestore, onRefresh }) {
  const { total_approved, unique_people, duplicates_to_archive, archived_existing } = data;
  const hasDupes = duplicates_to_archive > 0;
  const hasArchived = archived_existing > 0;
  const accent = hasDupes ? "#F5C518" : "#22C55E";

  return (
    <div
      data-testid="duplicate-cleanup-panel"
      className="border-2 p-4 sm:p-5 mb-6"
      style={{ borderColor: accent + "55", background: accent + "0a" }}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="label-kicker" style={{ color: accent }}>
            Duplicate Cleanup {hasDupes && "· Action available"}
          </div>
          <div className="font-heading text-white text-sm sm:text-base mt-1">
            {total_approved} approved record{total_approved === 1 ? "" : "s"}
            {" · "}
            {unique_people} unique {unique_people === 1 ? "person" : "people"}
            {hasDupes && (
              <>
                {" · "}
                <span style={{ color: accent }}>
                  {duplicates_to_archive} duplicate{duplicates_to_archive === 1 ? "" : "s"} ready to archive
                </span>
              </>
            )}
            {hasArchived && (
              <>
                {" · "}
                <span className="text-white/60">
                  {archived_existing} already archived
                </span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onRefresh}
          data-testid="refresh-duplicates-btn"
          className="text-xs px-3 py-1.5 border border-white/20 text-white/70 font-heading tracking-wider uppercase hover:bg-white hover:text-black transition"
        >
          Refresh
        </button>
      </div>

      {!hasDupes && !hasArchived && (
        <div className="text-sm text-white/60 mt-2">
          No duplicate approved records detected. The list is clean.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        <a
          href={bust(`${API}/admin/duplicates/preview.csv`)}
              data-authed-download="1"
              data-download-filename="preview.csv"
          data-testid="preview-duplicates-btn"
          className="btn-ghost inline-flex items-center gap-2"
          title="Dry-run review CSV. Lists every approved record with KEEP / REMOVE flag and the matching reason. NO database changes."
        >
          <Download className="w-4 h-4" /> Preview Duplicates (CSV)
        </a>
        {hasDupes && (
          <button
            onClick={onArchive}
            disabled={busy}
            data-testid="archive-duplicates-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5C518] text-black font-heading font-bold tracking-wider uppercase hover:bg-[#FFD93D] disabled:opacity-50 transition"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            Archive {duplicates_to_archive} Duplicate{duplicates_to_archive === 1 ? "" : "s"}
          </button>
        )}
        {hasArchived && (
          <button
            onClick={onRestore}
            disabled={busy}
            data-testid="restore-duplicates-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/40 text-red-400 font-heading tracking-wider uppercase hover:bg-red-500 hover:text-white disabled:opacity-50 transition"
            title="Restore every archived duplicate back to Approved"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Restore {archived_existing} Archived
          </button>
        )}
      </div>

      {hasDupes && (
        <div className="mt-4 p-3 border border-white/10 bg-black/30 text-xs text-white/70 leading-relaxed">
          <strong className="text-white">Recommended workflow:</strong> 1) Click <em>Preview Duplicates (CSV)</em> and review the rows marked REMOVE. 2) If everything looks correct, click <em>Archive {duplicates_to_archive} Duplicate{duplicates_to_archive === 1 ? "" : "s"}</em>. The action is reversible — you can click Restore at any time.
        </div>
      )}
    </div>
  );
}



function SmsBlastPanel({ data, audit, token, busy, onTest, onBlast, onRetryFailed, onRefresh, ready = true }) {
  const { configured, from_number, total_approved, sms_ok, sms_failed, never_attempted } = data;
  const accent = configured ? "#22D3EE" : "#EF4444"; // cyan when ready, red when not configured
  const hasFailed = sms_failed > 0;
  const hasIssues = audit && (audit.invalid > 0 || audit.missing > 0);

  return (
    <div
      data-testid="sms-blast-panel"
      className="border-2 p-4 sm:p-5 mb-6"
      style={{ borderColor: accent + "55", background: accent + "0a" }}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="label-kicker" style={{ color: accent }}>
            SMS Blast (Twilio) {!configured && "· NOT CONFIGURED"}
          </div>
          <div className="font-heading text-white text-sm sm:text-base mt-1">
            {configured ? (
              <>
                From <span className="text-white/70">{from_number}</span>
                {" · "}
                {total_approved} approved
                {" · "}
                <span style={{ color: "#22C55E" }}>{sms_ok} delivered</span>
                {" · "}
                <span style={{ color: hasFailed ? "#EF4444" : "#888" }}>{sms_failed} failed</span>
                {" · "}
                <span className="text-white/60">{never_attempted} not yet sent</span>
              </>
            ) : (
              <span className="text-red-400">
                Twilio credentials missing in backend .env. Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER and restart.
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onRefresh}
          data-testid="refresh-sms-status-btn"
          className="text-xs px-3 py-1.5 border border-white/20 text-white/70 font-heading tracking-wider uppercase hover:bg-white hover:text-black transition"
        >
          Refresh
        </button>
      </div>

      {configured && (
        <>
          {/* Phone audit summary — surfaces problems BEFORE the blast */}
          {audit && (
            <div
              className="mt-3 p-3 border bg-black/30 text-xs leading-relaxed"
              style={{
                borderColor: hasIssues ? "#EF4444" : "#22C55E",
                color: hasIssues ? "#FCA5A5" : "#86EFAC",
              }}
              data-testid="sms-phone-audit-summary"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <strong className="text-white">Phone Number Audit:</strong>{" "}
                  <span style={{ color: "#22C55E" }}>{audit.sendable_total} sendable</span>
                  {" · "}
                  <span className="text-white/70">
                    KSA ok: {audit.ksa_ok}
                    {" · "}auto-fixed to +966: {audit.ksa_fixable}
                    {" · "}non-KSA: {audit.non_ksa_ok}
                  </span>
                  {hasIssues && (
                    <>
                      {" · "}
                      <span style={{ color: "#EF4444", fontWeight: 700 }}>
                        ⚠ {audit.invalid + audit.missing} need{audit.invalid + audit.missing === 1 ? "s" : ""} manual fix
                        {" "}(invalid: {audit.invalid}, missing: {audit.missing})
                      </span>
                    </>
                  )}
                </div>
                <a
                  href={bust(`${API}/admin/sms/audit.csv`)}
              data-authed-download="1"
              data-download-filename="audit.csv"
                  data-testid="sms-audit-csv-btn"
                  className="text-xs px-3 py-1.5 border border-[#22D3EE]/60 text-[#22D3EE] font-heading tracking-wider uppercase hover:bg-[#22D3EE] hover:text-black transition"
                  title="Download a per-applicant phone number audit CSV"
                >
                  <Download className="w-3.5 h-3.5 inline mr-1" /> Audit CSV
                </a>
              </div>
              {hasIssues && audit.problem_samples && audit.problem_samples.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-white/80 mb-2">First {Math.min(audit.problem_samples.length, 5)} problem applicant{audit.problem_samples.length === 1 ? "" : "s"}:</div>
                  <ul className="space-y-1">
                    {audit.problem_samples.slice(0, 5).map((p) => (
                      <li key={p.id} className="text-white/70">
                        <span className="text-red-400 font-bold">{p.status.toUpperCase()}</span>
                        {" · "}
                        <span className="text-white">{p.name}</span>
                        {" · "}
                        <span className="text-white/50">{p.crew}</span>
                        {p.raw_phone && <span className="text-white/50"> · raw: <code>{p.raw_phone}</code></span>}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 text-white/60">
                    Download the full <em>Audit CSV</em> to see all of them, then use the inline edit feature in the application list to fix each phone number before blasting.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={onTest}
              disabled={busy}
              data-testid="sms-test-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#22D3EE] text-[#22D3EE] font-heading tracking-wider uppercase hover:bg-[#22D3EE] hover:text-black disabled:opacity-50 transition"
              title="Send ONE test SMS to verify Twilio is working"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
              Send Test SMS
            </button>
            <button
              onClick={onBlast}
              disabled={busy || !ready || total_approved === 0}
              data-testid="sms-blast-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5C518] text-black font-heading font-bold tracking-wider uppercase hover:bg-[#FFD93D] disabled:opacity-50 transition"
              title={!ready
                ? "Waiting for scope-matched SMS counts before this action can be launched safely."
                : "Blast the bilingual Entry Pass SMS to every approved applicant in the current edition"}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {!ready ? "Loading scope…" : `SMS Blast to All Approved (${total_approved})`}
            </button>
            {hasFailed && (
              <button
                onClick={onRetryFailed}
                disabled={busy || !ready}
                data-testid="sms-retry-failed-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/50 text-red-400 font-heading tracking-wider uppercase hover:bg-red-500 hover:text-white disabled:opacity-50 transition"
                title={!ready
                  ? "Waiting for scope-matched SMS counts before this action can be launched safely."
                  : "Re-send only to applicants whose last SMS attempt failed"}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                {!ready ? "Loading scope…" : `Retry ${sms_failed} Failed`}
              </button>
            )}
          </div>

          <div className="mt-4 p-3 border border-white/10 bg-black/30 text-xs text-white/70 leading-relaxed">
            <strong className="text-white">Recommended workflow:</strong> 1) Click <em>Send Test SMS</em> and put your own phone number — verify the message arrives and the link opens correctly. 2) Once confirmed, click <em>SMS Blast to All Approved</em> and type <code className="text-[#F5C518]">SEND SMS BLAST</code> when prompted. Successful sends in the last 6 hours are auto-skipped (idempotent — safe against double-clicks).
          </div>
        </>
      )}
    </div>
  );
}
